import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Textarea,
  Text,
  HStack,
  Button,
  ButtonGroup,
  Select,
  SimpleGrid,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import { Bar, Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { WebAPIContext } from "../../contexts/WebAPIContext";
import GoogleAPI from "../../utils/GoogleAPI";
import { CurrencyNameJPY, WorkflowId } from "../../contexts/constants";
import { ConfirmorInfo, Currency, PagePermissions, PartNo } from "../../contexts/types";
import { getLocalCurrencyId, formatYearMonthByLocale } from "../../utils/utils";
import { isSameFiscalYear } from "../../utils/calculation";
import { useAppSelector } from "../../store/hooks";
import { getConfirmor, updateConfirm } from "../../utils/workflow";
import * as XLSX from 'xlsx';
import ConfirmorList from "./ConfirmorList";
import HelpIcon from "./HelpIcon";
import { ProjectContext, getPermissions } from "./InvestmentScreeningDetail";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Filler, Tooltip, Legend, annotationPlugin);

interface ReviewBusinessProps {
  projectId: string;
  reviewDate?: string | null;
  projectEndDate?: string | null;
}

// （calcTotalSummary, calcAvgSummary は3カード形式に移行したため削除済み）

// 比較モード型
type CompareMode = "previous" | "originalPlan";

// 比較モード別の色設定
const compareColors = {
  previous: { bg: "rgba(66, 153, 225, 0.7)", border: "rgba(66, 153, 225, 1)" },       // 青: 前回値
  originalPlan: { bg: "rgba(72, 187, 120, 0.7)", border: "rgba(72, 187, 120, 1)" },    // 緑: 申請時計画
};

// グループ化棒グラフのデータ生成（期間指定対応）
const createChartData = (
  compareWith: number[],
  current: number[],
  compareLabel: string,
  mode: CompareMode,
  currentValueLabel: string,
  startIdx: number = 0,
  endIdx: number = 0,
  labels: string[] = [],
  boundaryIdx: number = 0
) => {
  const slicedCurrent = current.slice(startIdx, endIdx);
  const localBoundary = boundaryIdx - startIdx;

  // 実績（境界前）と見通し（境界後）で背景色を変える
  const bgColors = slicedCurrent.map((_, i) =>
    i < localBoundary ? "rgba(237, 137, 54, 0.7)" : "rgba(237, 137, 54, 0.35)"
  );
  const borderColors = slicedCurrent.map((_, i) =>
    i < localBoundary ? "rgba(237, 137, 54, 1)" : "rgba(237, 137, 54, 0.6)"
  );

  return {
    labels: labels.slice(startIdx, endIdx).map(formatYearMonthByLocale),
    datasets: [
      {
        label: compareLabel,
        data: compareWith.slice(startIdx, endIdx),
        backgroundColor: compareColors[mode].bg,
        borderColor: compareColors[mode].border,
        borderWidth: 1,
      },
      {
        label: currentValueLabel,
        data: slicedCurrent,
        backgroundColor: bgColors,
        borderColor: borderColors,
        borderWidth: 1,
      },
    ],
  };
};

// 振り返り境界線のアノテーション（コンテンツは描画時に t() で解決）
const buildReviewBoundaryAnnotation = (label: string) => ({
  type: "line" as const,
  xMin: 0,
  xMax: 0,
  borderColor: "rgba(229, 62, 62, 0.8)",
  borderWidth: 2,
  borderDash: [6, 4],
  label: {
    display: true,
    content: label,
    position: "start" as const,
    backgroundColor: "rgba(229, 62, 62, 0.8)",
    color: "white",
    font: { size: 10 },
  },
});

// Y軸の最大値を両モードで統一するための計算
const calcUnifiedMax = (data: { originalPlan: number[]; previous: number[]; current: number[] }, startIdx: number, endIdx: number): number => {
  const sliced = [
    ...data.originalPlan.slice(startIdx, endIdx),
    ...data.previous.slice(startIdx, endIdx),
    ...data.current.slice(startIdx, endIdx),
  ];
  const max = Math.max(...sliced);
  return Math.ceil(max * 1.1); // 10%の余白
};

// チャートオプション生成（期間指定対応）
// 棒グラフ用: ボーダーラインは振り返り月とその前月の間（adjustedBoundary - 0.5）に配置
const createChartOptions = (_allowNegative: boolean = false, startIdx: number = 0, endIdx: number = 0, unifiedMax?: number, boundaryIdx: number = 0, boundaryLabel: string = "", currentCurrency: string = "") => {
  const adjustedBoundary = boundaryIdx - startIdx;
  const showBoundary = adjustedBoundary >= 0 && adjustedBoundary < (endIdx - startIdx);

  return ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" as const },
      datalabels: false as any,
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const val = ctx.parsed.y;
            const isJPY = currentCurrency === CurrencyNameJPY;
            // 日本円（JPY）のときは小数を四捨五入して整数にし、それ以外はそのまま表示
            const formattedVal = val != null
              ? (isJPY ? Math.round(val).toLocaleString() : val.toLocaleString())
              : "-";
            return `${ctx.dataset.label}: ${formattedVal}`;
          }
        }
      },
      annotation: {
        annotations: showBoundary ? {
          reviewBoundary: {
            ...buildReviewBoundaryAnnotation(boundaryLabel),
            xMin: adjustedBoundary - 0.5,
            xMax: adjustedBoundary - 0.5,
          },
        } : {},
      },
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
          autoSkip: true,
          maxTicksLimit: Math.min(12, endIdx - startIdx),
          font: { size: 10 },
        },
      },
      y: {
        beginAtZero: true,
        ...(unifiedMax !== undefined ? { suggestedMax: unifiedMax } : {}),
        ticks: {
          maxTicksLimit: 8,
        },
      },
    },
  });
};

// ① 実績のみ: 振り返り日までの実績と同期間の計画を比較
const calcActualVsPlan = (original: number[] | undefined, curr: number[] | undefined, boundaryIdx: number = 0) => {
  original = original ?? [];
  curr = curr ?? [];
  const planValue = original.slice(0, boundaryIdx).reduce((a, b) => a + b, 0);
  const actualValue = curr.slice(0, boundaryIdx).reduce((a, b) => a + b, 0);
  const rate = planValue !== 0 ? ((actualValue - planValue) / Math.abs(planValue)) * 100 : 0;
  return { planValue, actualValue, rate };
};

// ②-1 前回予測の精度: 前回振り返り〜今回振り返りの期間で、前回想定と今回実績を比較

// 累積計算
const calcCumulative = (data: number[]) =>
  data.reduce<number[]>((acc, v) => { acc.push((acc.length > 0 ? acc[acc.length - 1] : 0) + v); return acc; }, []);

const calcForecastAccuracy = (prev: number[] | undefined, curr: number[] | undefined, prevIdx: number = 0, boundaryIdx: number = 0) => {
  prev = prev ?? [];
  curr = curr ?? [];
  const prevEstimate = prev.slice(prevIdx, boundaryIdx).reduce((a, b) => a + b, 0);
  const currActual = curr.slice(prevIdx, boundaryIdx).reduce((a, b) => a + b, 0);
  const rate = prevEstimate !== 0 ? ((currActual - prevEstimate) / Math.abs(prevEstimate)) * 100 : 0;
  return { prevEstimate, currActual, rate };
};

// ③ 最終見通し: 全期間（実績＋修正後の想定値）と全期間の計画値を比較
const calcFinalOutlook = (original: number[] | undefined, curr: number[] | undefined) => {
  original = original ?? [];
  curr = curr ?? [];
  const planTotal = original.reduce((a, b) => a + b, 0);
  const outlookTotal = curr.reduce((a, b) => a + b, 0);
  const rate = planTotal !== 0 ? ((outlookTotal - planTotal) / Math.abs(planTotal)) * 100 : 0;
  return { planTotal, outlookTotal, rate };
};

// 平均項目用: ① 実績ベースの計画比（平均）
const calcActualVsPlanAvg = (original: number[] | undefined, curr: number[] | undefined, boundaryIdx: number = 0) => {
  original = original ?? [];
  curr = curr ?? [];
  const planSlice = original.slice(0, boundaryIdx);
  const actualSlice = curr.slice(0, boundaryIdx);
  const planAvg = planSlice.length > 0 ? planSlice.reduce((a, b) => a + b, 0) / planSlice.length : 0;
  const actualAvg = actualSlice.length > 0 ? actualSlice.reduce((a, b) => a + b, 0) / actualSlice.length : 0;
  const rate = planAvg !== 0 ? ((actualAvg - planAvg) / Math.abs(planAvg)) * 100 : 0;
  return { planValue: planAvg, actualValue: actualAvg, rate };
};

// 平均項目用: ②-1 前回予測の精度（平均）
const calcForecastAccuracyAvg = (prev: number[] | undefined, curr: number[] | undefined, prevIdx: number = 0, boundaryIdx: number = 0) => {
  prev = prev ?? [];
  curr = curr ?? [];
  const prevSlice = prev.slice(prevIdx, boundaryIdx);
  const currSlice = curr.slice(prevIdx, boundaryIdx);
  const prevAvg = prevSlice.length > 0 ? prevSlice.reduce((a, b) => a + b, 0) / prevSlice.length : 0;
  const currAvg = currSlice.length > 0 ? currSlice.reduce((a, b) => a + b, 0) / currSlice.length : 0;
  const rate = prevAvg !== 0 ? ((currAvg - prevAvg) / Math.abs(prevAvg)) * 100 : 0;
  return { prevEstimate: prevAvg, currActual: currAvg, rate };
};

// 平均項目用: ③ 軌道修正後の計画比（平均）
const calcFinalOutlookAvg = (original: number[] | undefined, curr: number[] | undefined) => {
  original = original ?? [];
  curr = curr ?? [];
  const planAvg = original.length > 0 ? original.reduce((a, b) => a + b, 0) / original.length : 0;
  const outlookAvg = curr.length > 0 ? curr.reduce((a, b) => a + b, 0) / curr.length : 0;
  const rate = planAvg !== 0 ? ((outlookAvg - planAvg) / Math.abs(planAvg)) * 100 : 0;
  return { planTotal: planAvg, outlookTotal: outlookAvg, rate };
};

// 申請時計画のサマリ計算
// （calcOriginalPlanSummary は3カード形式に移行したため削除済み）

// 増減率の色: lowerIsBetter=true のとき正値が赤（コスト系）、false のとき負値が赤（利益系）
const rateColor = (rate: number, lowerIsBetter = false): string =>
  lowerIsBetter
    ? (rate > 10 ? "red.600" : rate > 0 ? "orange.500" : "green.600")
    : (rate < -10 ? "red.600" : rate < 0 ? "orange.500" : "green.600");

// チャート付きセクション（棒グラフ） - id のみ保持、title/unit は描画時に t() で解決
const CHART_SECTION_IDS = ["manufacturingCost"] as const;

// ⑤ 設備稼働率（④ 生産数量の後に描画するため分離）の id
const OPERATING_RATE_SECTION_ID = "operatingRate";

// テキスト入力項目 - id のみ保持、label/placeholder は描画時に t() で解決
const TEXT_ITEM_IDS = ["maintenance_cost", "skill_transfer", "energy_efficiency"] as const;

// スプレッドシート定数
const SHEET_NAME_PRODUCTION_ACTUALS = "ProductionActuals";

/**
 * P&Lデータのラベル配列を、現在のEOL予定日（projectEndDate）に合わせて延長または短縮する。
 * BasicInfoでEOL予定日が変更された場合に、振り返り画面のチャート期間を正しく反映するために使用。
 */
const adjustLabelsToEOL = (rawLabels: string[], projectEndDate: string | null | undefined): string[] => {
  if (!projectEndDate || rawLabels.length === 0) return rawLabels;
  const parts = projectEndDate.split('-');
  const eolYear = parseInt(parts[0]);
  const eolMonth = parseInt(parts[1]);

  // EOL予定日の属する年度末（4月始まり → 翌3月）を計算
  // 例: 2030/5 → FY2030 → 年度末 2031/3
  //     2029/8 → FY2029 → 年度末 2030/3
  const fiscalYear = eolMonth >= 4 ? eolYear : eolYear - 1;
  const targetYear = fiscalYear + 1;
  const targetMonth = 3;
  const targetYM = targetYear * 100 + targetMonth;

  const toYM = (label: string): number => {
    const lp = label.split('/');
    return parseInt(lp[0]) * 100 + parseInt(lp[1]);
  };

  const lastYM = toYM(rawLabels[rawLabels.length - 1]);

  if (targetYM < lastYM) {
    return rawLabels.filter(l => toYM(l) <= targetYM);
  }
  if (targetYM === lastYM) return rawLabels;

  const extended = [...rawLabels];
  let curYear = parseInt(rawLabels[rawLabels.length - 1].split('/')[0]);
  let curMonth = parseInt(rawLabels[rawLabels.length - 1].split('/')[1]);
  while (extended.length <= rawLabels.length + 120) {
    curMonth++;
    if (curMonth > 12) { curMonth = 1; curYear++; }
    extended.push(`${curYear}/${curMonth}`);
    if (curYear === targetYear && curMonth === targetMonth) break;
  }
  return extended;
};

/**
 * 数値配列をターゲット長に合わせて延長（末尾を0埋め）または短縮（スライス）する。
 */
const extendOrTruncate = (arr: number[], targetLength: number): number[] => {
  if (arr.length === targetLength) return arr;
  if (arr.length > targetLength) return arr.slice(0, targetLength);
  return [...arr, ...new Array(targetLength - arr.length).fill(0)];
};

// ========== 稼働率の月別配列抽出ヘルパー ==========
// nonWorkingHours JSON から月ラベル対応の稼働率配列（0〜100）を抽出
// nonWorkingHoursByMonth 配列はカレンダー月順（1月=index 0, 12月=index 11）で保存されている
const extractAvailabilityRates = (nonWorkingHoursJson: string | null | undefined, labels: string[]): number[] => {
  if (!nonWorkingHoursJson) return new Array(labels.length).fill(0);
  try {
    const parsed: { fiscalYear: string; nonWorkingHoursByMonth: { availabilityRate?: number; workTime?: string }[] }[] = JSON.parse(nonWorkingHoursJson);
    return labels.map(label => {
      const parts = label.split('/');
      const calYear = parseInt(parts[0]);
      const calMonth = parseInt(parts[1]);
      // 会計年度: 4月以降は翌年3月期、1〜3月は当年3月期
      const fyYear = calMonth >= 4 ? calYear + 1 : calYear;
      const fyStr = `${fyYear}年3月期`;
      // ※会計年度文字列は保存時の表示言語でフォーマットが異なるため、完全一致ではなく年数値で判定する
      const yearData = parsed.find((d: any) => isSameFiscalYear(d.fiscalYear, fyStr));
      if (!yearData) return 0;
      // カレンダー月順（1月=0, 2月=1, ..., 12月=11）
      const monthIdx = calMonth - 1;
      const monthData = yearData.nonWorkingHoursByMonth[monthIdx];
      // 標準労働時間が0（未入力 or 00:00）の月は稼働なしとみなし0%
      if (!monthData?.workTime || monthData.workTime === "00:00") return 0;
      return (monthData.availabilityRate ?? 0) * 100;
    });
  } catch {
    return new Array(labels.length).fill(0);
  }
};

// ========== Decimal文字列を数値に変換するユーティリティ ==========
const toNum = (v: any): number => {
  if (typeof v === 'number') return v;
  try { return parseFloat(String(v)) || 0; } catch { return 0; }
};

// "X年Yヶ月" 形式の回収期間文字列を月数（整数）または "回収不能" に変換
const parsePaybackStr = (s: string): number | "回収不能" | null => {
  if (!s) return null;
  const m = s.match(/(\d+)年(\d+)ヶ月/);
  if (m) return parseInt(m[1]) * 12 + parseInt(m[2]);
  const ym = s.match(/(\d+)年/);
  if (ym) return parseInt(ym[1]) * 12;
  const mo = s.match(/(\d+)ヶ月/);
  if (mo) return parseInt(mo[1]);
  if (s === "回収不能") return "回収不能";
  return null;
};

// project オブジェクトの originalProfitAndLoss から回収期間（月数または "回収不能"）を取得
const getPaybackMonths = (project: any): number | "回収不能" | null => {
  if (!project?.originalProfitAndLoss) return null;
  try {
    const d = JSON.parse(project.originalProfitAndLoss);
    const s = d?.paybackPeriodByYear?.paybackPeriod;
    return s ? parsePaybackStr(s) : null;
  } catch { return null; }
};

// ========== 生産ライン稼働日数のラベル対応配列抽出ヘルパー ==========
// fiscalYear "YYYY年3月期" 形式の JSON から月ラベル ("YYYY/M") に対応する値を抽出
const extractWorkingDaysByLabel = (
  workingDaysJson: string | null | undefined,
  labels: string[],
  field: 'workingDays' | 'workOnWeekendAndHolidays' | 'lineCount'
): number[] => {
  if (!workingDaysJson) return new Array(labels.length).fill(0);
  try {
    const parsed: { fiscalYear: string; workingDaysData: any }[] = JSON.parse(workingDaysJson);
    return labels.map(label => {
      const parts = label.split('/');
      const calYear = parseInt(parts[0]);
      const calMonth = parseInt(parts[1]);
      // 日本会計年度: 4月始まり → 4月は当年度末翌年3月期
      const fyYear = calMonth >= 4 ? calYear + 1 : calYear;
      const fyStr = `${fyYear}年3月期`;
      // ※会計年度文字列は保存時の表示言語でフォーマットが異なるため、完全一致ではなく年数値で判定する
      const yearData = parsed.find((d: any) => isSameFiscalYear(d.fiscalYear, fyStr));
      if (!yearData) return 0;
      // カレンダー月順（1月=0, 2月=1, ..., 12月=11）
      const monthIdx = calMonth - 1;
      // lineCount 未保存（ライン数機能追加前のデータ）は入力画面の初期値に合わせて1ライン扱い
      return yearData.workingDaysData[field]?.[monthIdx] ?? (field === 'lineCount' ? 1 : 0);
    });
  } catch {
    return new Array(labels.length).fill(0);
  }
};

// BOMデータから指定月のサイクルタイム（秒）を取得（複数部品は最大値）
const getCycleTimeFromBomData = (bomItems: any[], yearMonth: string): number => {
  const parts = yearMonth.split('/');
  const key = `${parts[0]}-${parts[1].padStart(2, '0')}`; // "YYYY/M" → "YYYY-MM"
  let maxCT = 0;
  for (const bom of bomItems) {
    if (!bom.cycleTimeMonthly) continue;
    try {
      const monthly = JSON.parse(bom.cycleTimeMonthly);
      const val = parseFloat(monthly[key] ?? '0');
      if (!isNaN(val) && val > maxCT) maxCT = val;
    } catch { }
  }
  return maxCT;
};

// BOMデータから指定月の歩留まり（0〜1）を取得（複数工程は乗算）
const getYieldRateFromBomData = (bomItems: any[], yearMonth: string): number => {
  const parts = yearMonth.split('/');
  const key = `${parts[0]}-${parts[1].padStart(2, '0')}`;
  let yieldProduct = 1.0;
  for (const bom of bomItems) {
    if (!bom.yieldMonthly) continue;
    try {
      const monthly = JSON.parse(bom.yieldMonthly);
      const val = parseFloat(monthly[key] ?? '100');
      if (!isNaN(val) && val > 0) yieldProduct *= val / 100;
    } catch { }
  }
  return yieldProduct;
};

// BOM + 製造ラインから月別生産キャパ（個）を算出
// 計算式: (稼働日数 + 休日出勤日数) × 86400秒 ÷ CT(秒) × 稼働率 × 歩留まり × ライン数
const calcProductionCapacityFromBom = (
  bomItems: any[],
  workingDaysJson: string | null | undefined,
  nonWorkingHoursJson: string | null | undefined,
  labels: string[]
): number[] => {
  if (bomItems.length === 0 || labels.length === 0) return new Array(labels.length).fill(0);
  const wd = extractWorkingDaysByLabel(workingDaysJson, labels, 'workingDays');
  const hd = extractWorkingDaysByLabel(workingDaysJson, labels, 'workOnWeekendAndHolidays');
  const lc = extractWorkingDaysByLabel(workingDaysJson, labels, 'lineCount');
  // extractAvailabilityRates は 0〜100 を返すので 100 で割って 0〜1 へ
  const ar = extractAvailabilityRates(nonWorkingHoursJson, labels).map(r => r / 100);
  return labels.map((label, i) => {
    const ct = getCycleTimeFromBomData(bomItems, label);
    const yr = getYieldRateFromBomData(bomItems, label);
    const totalDays = wd[i] + hd[i];
    // 0ライン入力時は0として扱う（未保存時の1ライン補完は extractWorkingDaysByLabel 側で実施）
    const lineCount = lc[i];
    if (ct <= 0 || totalDays <= 0) return 0;
    const rawQty = Math.round(totalDays * 86400 / ct);
    return Math.round(rawQty * ar[i] * yr * lineCount);
  });
};

// PartNo別に生産キャパを算出する（BOMをPartNoでフィルタして個別に計算）
const calcProductionCapacityByPartNo = (
  bomItems: any[],
  workingDaysJson: string | null | undefined,
  nonWorkingHoursJson: string | null | undefined,
  labels: string[]
): Record<string, number[]> => {
  const partNos = [...new Set(bomItems.map((b: any) => b.partNo).filter(Boolean))];
  const result: Record<string, number[]> = {};
  for (const pn of partNos) {
    const filtered = bomItems.filter((b: any) => b.partNo === pn);
    result[pn] = calcProductionCapacityFromBom(filtered, workingDaysJson, nonWorkingHoursJson, labels);
  }
  return result;
};

// PartNo別キャパを全体集計に合わせて比例配分する
// 【背景】各PartNoを独立計算すると各PartNoが「ラインを独占した場合」の値になるため
//         合計が全体集計を超えてしまう。全体集計を上限として比例配分することで
//         BoiseBuffalo + BoiseBuffalo2 + ... = 全体集計 を保証する
const scaleCapacityByPartNoToTotal = (
  rawByPN: Record<string, number[]>,
  totalCap: number[],
  len: number
): Record<string, number[]> => {
  const partNos = Object.keys(rawByPN);
  if (partNos.length <= 1) return rawByPN;
  const scaled: Record<string, number[]> = {};
  for (const pn of partNos) {
    scaled[pn] = Array.from({ length: len }, (_, i) => {
      const rawSum = partNos.reduce((s, p) => s + (rawByPN[p]?.[i] ?? 0), 0);
      if (rawSum === 0) return 0;
      return Math.round((rawByPN[pn]?.[i] ?? 0) / rawSum * (totalCap[i] ?? 0));
    });
  }
  return scaled;
};

// PartNo別にCT・歩留まりを集計してmax/min/avgを返す（多数モデル対応）
// yearMonth: "YYYY/M" 形式（reviewBoundaryIdxの月ラベル）
type BomStats = { max: number; min: number; avg: number };
const calcBomPartNoStats = (
  bomItems: any[],
  yearMonth: string
): { ct: BomStats; yield: BomStats } | null => {
  if (bomItems.length === 0) return null;
  const [y, m] = yearMonth.split('/');
  const boundaryKey = `${y}-${m.padStart(2, '0')}`;
  const byPartNo = new Map<string, { maxCt: number; yieldVals: number[] }>();
  for (const bom of bomItems) {
    const pn: string = bom.partNo;
    if (!byPartNo.has(pn)) byPartNo.set(pn, { maxCt: 0, yieldVals: [] });
    const entry = byPartNo.get(pn)!;
    if (bom.cycleTimeMonthly) {
      try {
        const monthly = JSON.parse(bom.cycleTimeMonthly);
        for (const [k, v] of Object.entries(monthly as Record<string, string>)) {
          if (k <= boundaryKey) {
            const val = parseFloat(v ?? '0');
            if (!isNaN(val) && val > entry.maxCt) entry.maxCt = val;
          }
        }
      } catch { }
    }
    if (bom.yieldMonthly) {
      try {
        const monthly = JSON.parse(bom.yieldMonthly);
        for (const [k, v] of Object.entries(monthly as Record<string, string>)) {
          if (k <= boundaryKey) {
            const val = parseFloat(v ?? '0');
            if (!isNaN(val) && val > 0) entry.yieldVals.push(val);
          }
        }
      } catch { }
    }
  }
  const avg = (arr: number[]) => arr.reduce((a, b) => a + b, 0) / arr.length;
  const entries = [...byPartNo.values()];
  const cts = entries.map(e => e.maxCt).filter(v => v > 0);
  const yields = entries.map(e => e.yieldVals.length > 0 ? avg(e.yieldVals) : 0).filter(v => v > 0);
  if (cts.length < 2 || yields.length < 2) return null; // 複数PartNoがない場合はnullを返して単一値表示に fallback
  return {
    ct: { max: Math.max(...cts), min: Math.min(...cts), avg: avg(cts) },
    yield: { max: Math.max(...yields), min: Math.min(...yields), avg: avg(yields) },
  };
};

// ============================================================
// CT・歩留まり 期間平均ヘルパー関数
// ============================================================

// startLabel〜endLabel に含まれる月ラベルを allLabels から抽出
const getMonthLabelsInRange = (
  startLabel: string,
  endLabel: string,
  allLabels: string[]
): string[] => {
  const startIdx = allLabels.indexOf(startLabel);
  const endIdx = allLabels.indexOf(endLabel);
  if (startIdx < 0 || endIdx < 0 || startIdx > endIdx) return [];
  return allLabels.slice(startIdx, endIdx + 1);
};

// 期間平均CT（秒）: 各月のmax CT（複数ItemNo）を取得 → 期間平均
const calcPeriodAvgCT = (
  bomItems: any[],
  startLabel: string,
  endLabel: string,
  allLabels: string[]
): number => {
  const months = getMonthLabelsInRange(startLabel, endLabel, allLabels);
  if (months.length === 0) return 0;
  const sum = months.reduce((s, m) => s + getCycleTimeFromBomData(bomItems, m), 0);
  return sum / months.length;
};

// 期間平均歩留まり（%）: 各月の工程乗算歩留まりを取得 → 期間平均
const calcPeriodAvgYield = (
  bomItems: any[],
  startLabel: string,
  endLabel: string,
  allLabels: string[]
): number => {
  const months = getMonthLabelsInRange(startLabel, endLabel, allLabels);
  if (months.length === 0) return 0;
  const sum = months.reduce((s, m) => s + getYieldRateFromBomData(bomItems, m) * 100, 0);
  return sum / months.length;
};

// PartNo別期間平均を算出してPartNo間統計（max/min/avg）を返す
// PartNo数 < 2 の場合は null を返して単一値表示にフォールバック
const calcPeriodAvgBomStats = (
  bomItems: any[],
  startLabel: string,
  endLabel: string,
  allLabels: string[]
): { ct: BomStats; yield: BomStats } | null => {
  if (bomItems.length === 0) return null;
  const partNos = [...new Set(bomItems.map((b: any) => b.partNo).filter(Boolean))] as string[];
  if (partNos.length < 2) return null;
  const ctVals: number[] = [];
  const yldVals: number[] = [];
  for (const pn of partNos) {
    const items = bomItems.filter((b: any) => b.partNo === pn);
    const ct = calcPeriodAvgCT(items, startLabel, endLabel, allLabels);
    const yld = calcPeriodAvgYield(items, startLabel, endLabel, allLabels);
    if (ct > 0) ctVals.push(ct);
    if (yld > 0) yldVals.push(yld);
  }
  if (ctVals.length < 2 || yldVals.length < 2) return null;
  const avg = (arr: number[]) => arr.reduce((a, b) => a + b, 0) / arr.length;
  return {
    ct: { max: Math.max(...ctVals), min: Math.min(...ctVals), avg: avg(ctVals) },
    yield: { max: Math.max(...yldVals), min: Math.min(...yldVals), avg: avg(yldVals) },
  };
};

interface Confirmor {
  reviewBusiness?: {
    companyDivision?: ConfirmorInfo[];
    companyHeadDivision?: ConfirmorInfo[];
    procurementDivision?: ConfirmorInfo[];
  };
}

const getAutoColWidths = (data: (string | number)[][]): XLSX.ColInfo[] => {
  const colWidths: number[] = [];
  for (const row of data) {
    row.forEach((cell, i) => {
      const cellValue = String(cell ?? '');
      const cellLength = [...cellValue].reduce(
        (sum, ch) => sum + (ch.charCodeAt(0) > 255 ? 2 : 1),
        0
      );
      colWidths[i] = Math.max(colWidths[i] || 10, cellLength);
    });
  }
  return colWidths.map(w => ({ wch: w + 2 }));
};

const ReviewBusiness: React.FC<ReviewBusinessProps> = ({ projectId, reviewDate, projectEndDate: projectEndDateProp }) => {
  const { t } = useTranslation();
  const WebAPI = useContext(WebAPIContext);
  const toast = useToast();
  const googleAPI = useMemo(() => new GoogleAPI(), []);
  const TITLE = t("features.InvestmentScreening.Review.ReviewBusiness.title");
  const reviewBoundaryLabel = t("common.label.review");
  const currentValueLabel = t("common.label.currentValue");
  const currentOutlookLabel = t("common.label.currentOutlook");
  const actualsCumulativeLabel = t("common.label.actualsCumulative");
  const outlookCumulativeLabel = t("common.label.outlookCumulative");

  /** チャート付きセクション（棒グラフ） — t() 解決のため画面内で構築 */
  const chartSections = useMemo(() => CHART_SECTION_IDS.map((id) => {
    if (id === "manufacturingCost") {
      return {
        id,
        title: t("features.InvestmentScreening.Review.ReviewBusiness.section.manufacturingCost"),
        unit: t("features.InvestmentScreening.Review.ReviewBusiness.unit.thousandYen"),
        summaryType: "total" as const,
        allowNegative: false,
        lowerIsBetter: true,
        data: { originalPlan: [] as number[], previous: [] as number[], current: [] as number[] },
      };
    }
    return null;
  }).filter((s): s is NonNullable<typeof s> => s !== null), [t]);

  /** ⑤ 設備稼働率セクション */
  const operatingRateSection = useMemo(() => ({
    id: OPERATING_RATE_SECTION_ID,
    title: t("features.InvestmentScreening.Review.ReviewBusiness.section.operatingRate"),
    unit: t("features.InvestmentScreening.Review.ReviewBusiness.unit.percent"),
    summaryType: "average" as const,
    allowNegative: false,
    data: { originalPlan: [] as number[], previous: [] as number[], current: [] as number[] },
  }), [t]);

  /** ログインユーザー：従業員ID */
  const employeeId = useAppSelector((state) => state.employee.employeeId);
  /** ログインユーザー：部署ID */
  const divisionId = useAppSelector((state) => state.employee.divisionId);
  /** ユーザー権限 */
  const projectContext = useContext(ProjectContext);
  const userPermissions = projectContext?.userPermissions ?? [];

  /** 権限 */
  const [pagePermissions, setPagePermissions] = useState<PagePermissions>();
  /** 起案者部署ID（確認ボタン活性判定用） */
  const [registrantDivisionId, setRegistrantDivisionId] = useState<string | null>(null);
  /** 確認担当者 */
  const [confirmor, setConfirmor] = useState<Confirmor | null>(null);
  /** 確認ボタン横幅 */
  const [buttonWidths, setButtonWidths] = useState<Record<string, number>>({});
  /** 確認ボタン下担当者名 */
  const [names, setNames] = useState<Record<string, ConfirmorInfo[]>>({
    companyDivision: [],
    companyHeadDivision: [],
    procurementDivision: [],
  });
  /** 確認元部署 */
  const [confirmSource, setConfirmSource] = useState<string>("");
  /** 保存中フラグ */
  const [isSaving, setIsSaving] = useState(false);
  /** データ取得中フラグ */
  const [isLoading, setIsLoading] = useState(true);
  /** 確認ボタン ref */
  const refs: Record<string, React.RefObject<HTMLButtonElement>> = {
    companyDivision: useRef<HTMLButtonElement>(null),
    companyHeadDivision: useRef<HTMLButtonElement>(null),
    procurementDivision: useRef<HTMLButtonElement>(null),
  };
  const { isOpen: isOpenConfirmModal, onOpen: onOpenConfirmModal, onClose: onCloseConfirmModal } = useDisclosure();

  // GoogleAPI 初期化
  useEffect(() => {
    const initGapi = async () => {
      try {
        if (!await googleAPI.initGapi()) {
          throw new Error("initGapi error");
        }
      } catch (e) {
        console.warn("GoogleAPI初期化エラー（スプレッドシート機能が使用不可）", e);
      }
    };
    initGapi();
  }, [googleAPI]);

  // ========== 実データ state ==========
  /** 月ラベル（実APIから取得） */
  const [monthLabels, setMonthLabels] = useState<string[]>([]);
  /** 振り返り境界インデックス（今回振り返り日に対応する月） */
  const [reviewBoundaryIdx, setReviewBoundaryIdx] = useState<number>(0);
  /** 前回振り返り境界インデックス */
  const [prevReviewIdx, setPrevReviewIdx] = useState<number>(0);
  /** 期間セレクタのマイルストーン */
  const [milestones, setMilestones] = useState<{ label: string; idx: number; isCurrent?: boolean }[]>([]);
  /** 表示通貨（プルダウンで切り替え） */
  const [currencyName, setCurrencyName] = useState<string>(CurrencyNameJPY);
  /** プロジェクトのローカル通貨名 */
  const [localCurrencyName, setLocalCurrencyName] = useState<string>(CurrencyNameJPY);
  /** ① 営業利益（月別）- ローカル通貨版 */
  const [operatingProfitData, setOperatingProfitData] = useState<{ originalPlan: number[]; previous: number[]; current: number[] } | null>(null);
  /** ① 営業利益（月別）- JPY版 */
  const [operatingProfitDataJPY, setOperatingProfitDataJPY] = useState<{ originalPlan: number[]; previous: number[]; current: number[] } | null>(null);
  /** ② 製造原価（月別）- ローカル通貨版 */
  const [manufacturingCostData, setManufacturingCostData] = useState<{ originalPlan: number[]; previous: number[]; current: number[] } | null>(null);
  /** ② 製造原価（月別）- JPY版 */
  const [manufacturingCostDataJPY, setManufacturingCostDataJPY] = useState<{ originalPlan: number[]; previous: number[]; current: number[] } | null>(null);
  /** ⑤ 設備稼働率（月別、単位: %） */
  const [operatingRateRealData, setOperatingRateRealData] = useState<{ originalPlan: number[]; previous: number[]; current: number[] } | null>(null);
  /** ③ 回収期間（月数 or "回収不能"）: 計画値・振り返り値 */
  const [paybackPlan, setPaybackPlan] = useState<number | "回収不能" | null>(null);
  const [paybackReview, setPaybackReview] = useState<number | "回収不能" | null>(null);
  /** ⑥ 歩留まり（%）: 計画値・振り返り値 */
  const [yieldPlan, setYieldPlan] = useState<number | null>(null);
  const [yieldReview, setYieldReview] = useState<number | null>(null);
  /** ⑥ サイクルタイム（秒）: 計画値・振り返り値 */
  const [cycleTimePlan, setCycleTimePlan] = useState<number | null>(null);
  const [cycleTimeReview, setCycleTimeReview] = useState<number | null>(null);
  /** ⑦ 人員数（人）: 計画値・振り返り値 */
  const [headcountPlan, setHeadcountPlan] = useState<number | null>(null);
  const [headcountReview, setHeadcountReview] = useState<number | null>(null);
  /** 振り返りデータが存在するか */
  const [hasReviewData, setHasReviewData] = useState<boolean>(false);
  /** ④ 生産キャパ（月別・個）: BOM × 製造ライン から計算（全体集計） */
  const [productionCapacityData, setProductionCapacityData] = useState<{ originalPlan: number[]; previous: number[]; current: number[] } | null>(null);
  /** ④ 生産キャパ（PartNo別）: PartNoごとのBOMで個別算出 */
  const [planCapacityByPartNo, setPlanCapacityByPartNo] = useState<Record<string, number[]>>({});
  const [prevCapacityByPartNo, setPrevCapacityByPartNo] = useState<Record<string, number[]>>({});
  const [currentCapacityByPartNo, setCurrentCapacityByPartNo] = useState<Record<string, number[]>>({});
  /** 実際の PartNo 一覧（API取得後に設定） */
  const [realPartNos, setRealPartNos] = useState<string[]>([]);
  /** PartNoごとのdisplayPartNo解決マップ（No.435対応、画面表示・Excel出力・PdfReviewContent受け渡し用） */
  const [displayPartNoMap, setDisplayPartNoMap] = useState<Record<string, string | null>>({});

  // ========== スプレッドシート state ==========
  /** 入力モード: "manual"=テーブル直接入力 / "google"=GoogleSpreadSheet連携 */
  const [productionInputMode, setProductionInputMode] = useState<"manual" | "google">("manual");
  /** スプレッドシート操作中のローディングフラグ */
  const [isSpreadSheetLoading, setIsSpreadSheetLoading] = useState<boolean>(false);
  /** 開いているスプレッドシートのID */
  const [spreadsheetId, setSpreadsheetId] = useState<string | null>(null);
  /** 開いているスプレッドシートのウィンドウ参照（閉じたとき手動モードに戻すために保持） */
  const [spreadSheetWindow, setSpreadSheetWindow] = useState<Window | null>(null);

  // ========== UI state ==========
  const [textValues, setTextValues] = useState<Record<string, string>>({});
  const [comments, setComments] = useState<Record<string, string>>({});
  const [compareMode, setCompareMode] = useState<CompareMode>("previous");
  const [periodStart, setPeriodStart] = useState<number>(0);
  const [periodEnd, setPeriodEnd] = useState<number>(0);
  const [selectedPartNo, setSelectedPartNo] = useState<string>("all");
  // 前回振り返り日（nullなら初回振り返り → 比較ラインラベルを「申請時計画値」に切り替え）
  const [prevReviewDateState, setPrevReviewDateState] = useState<string | null>(null);
  // ⑥ 多数モデル対応: BOM由来のmax/min/avg統計（PartNo数 >= 2 の場合のみ非null）
  const [yieldStatsPlan, setYieldStatsPlan] = useState<BomStats | null>(null);
  const [yieldStatsReview, setYieldStatsReview] = useState<BomStats | null>(null);
  const [ctStatsPlan, setCtStatsPlan] = useState<BomStats | null>(null);
  const [ctStatsReview, setCtStatsReview] = useState<BomStats | null>(null);
  // ⑥ 前回振り返り値: BOM由来のmax/min/avg統計（PartNo数 >= 2 の場合のみ非null）
  const [yieldStatsPrev, setYieldStatsPrev] = useState<BomStats | null>(null);
  const [ctStatsPrev, setCtStatsPrev] = useState<BomStats | null>(null);
  // ⑥ PartNo別の歩留まり・CT境界値（ドロップダウン切り替え用）
  const [yieldAtBoundaryByPartNo, setYieldAtBoundaryByPartNo] = useState<Record<string, { plan: number; prev: number; current: number }>>({});
  const [ctAtBoundaryByPartNo, setCtAtBoundaryByPartNo] = useState<Record<string, { plan: number; prev: number; current: number }>>({});
  const [selectedPartNoYieldCt, setSelectedPartNoYieldCt] = useState<string>("all");
  // ⑥ 期間平均の期間ラベル（セル内表示用）
  const [productionStartLabelState, setProductionStartLabelState] = useState<string>("");
  const [todayEndLabelState, setTodayEndLabelState] = useState<string>("");
  const [prevEndLabelState, setPrevEndLabelState] = useState<string | null>(null);
  // 前回振り返り: 単一値比較項目（compareMode="previous" 時に使用）
  const [yieldPrev, setYieldPrev] = useState<number | null>(null);
  const [cycleTimePrev, setCycleTimePrev] = useState<number | null>(null);
  const [headcountPrev, setHeadcountPrev] = useState<number | null>(null);
  const [paybackPrev, setPaybackPrev] = useState<number | "回収不能" | null>(null);

  // PartNo別の生産実績入力値（キー: "partNo-monthIndex"）
  const [productionActuals, setProductionActuals] = useState<Record<string, number>>({});
  // フォーカス中の入力セルキー（カンマ表示 ↔ 数値入力の切り替え）
  const [focusedInputKey, setFocusedInputKey] = useState<string | null>(null);
  // 保存済みの生産実績データ（PartNo別。保存ボタン押下で確定、グラフの今回値に使用）
  // キー: PartNo名、値: 月別データ配列。空オブジェクト=未保存
  const [savedProductionByPartNo, setSavedProductionByPartNo] = useState<Record<string, number[]>>({});
  // 前回振り返りの生産実績データ（PartNo別）
  const [prevSavedProductionByPartNo, setPrevSavedProductionByPartNo] = useState<Record<string, number[]>>({});
  // 前回振り返りの quantityByPartNo（実績未登録時のフォールバック）
  const [prevQuantityByPartNo, setPrevQuantityByPartNo] = useState<Record<string, number[]>>({});
  // 申請時計画の quantityByPartNo（PartNo別グラフの申請時計画ラインに使用）
  const [planQuantityByPartNo, setPlanQuantityByPartNo] = useState<Record<string, number[]>>({});

  useEffect(() => {
    const perms = getPermissions(userPermissions, WorkflowId.ReviewBusiness, undefined, employeeId, divisionId, registrantDivisionId ?? undefined);
    setPagePermissions(perms);
  }, [userPermissions, employeeId, divisionId, registrantDivisionId]);

  useEffect(() => {
    const widths: Record<string, number> = Object.keys(refs).reduce((acc, key) => {
      acc[key] = refs[key]?.current?.offsetWidth || 0;
      return acc;
    }, {} as Record<string, number>);
    setButtonWidths(widths);
  }, []);

  useEffect(() => {
    if (!projectId) return;
    const fetchConfirmorData = async () => {
      try {
        const data = await getConfirmor(WebAPI, projectId, reviewDate);
        setConfirmor(data);
      } catch { /* スルー */ }
    };
    fetchConfirmorData();
  }, [projectId, reviewDate]);

  useEffect(() => {
    if (confirmor?.reviewBusiness) {
      setNames({
        companyDivision: confirmor.reviewBusiness.companyDivision ?? [],
        companyHeadDivision: confirmor.reviewBusiness.companyHeadDivision ?? [],
        procurementDivision: confirmor.reviewBusiness.procurementDivision ?? [],
      });
    }
  }, [confirmor]);

  // ========== データ取得 ==========
  useEffect(() => {
    if (!projectId) return;
    fetchData();
  }, [projectId, reviewDate, projectEndDateProp]);

  const fetchData = async () => {
    setIsLoading(true);
    // 前回振り返り値stateをリセット（ナビゲーション時のstale値防止）
    setYieldPrev(null);
    setCycleTimePrev(null);
    setHeadcountPrev(null);
    setPaybackPrev(null);
    setYieldStatsPrev(null);
    setCtStatsPrev(null);
    setPrevReviewDateState(null);
    try {
      // 1. 振り返り一覧取得（前回の振り返り日を特定するため）
      let prevReviewDate: string | null = null;
      try {
        const listResp = await (WebAPI as any).request({
          query: `
            query getReviewListForBusiness($projectId: String!) {
              getReviewListByProjectId(projectId: $projectId) {
                reviewDate
                businessReview
              }
            }
          `,
          variables: { projectId }
        });
        const reviewList: { reviewDate: string; businessReview?: string }[] = listResp?.data?.getReviewListByProjectId || [];
        if (reviewList.length > 0 && reviewDate) {
          const sortedList = [...reviewList].sort((a, b) => a.reviewDate.localeCompare(b.reviewDate));
          // 現在のreviewDateより前の最新レビュー日を取得（現在日がリストに存在しない場合も考慮）
          const prevDates = sortedList.filter(r => r.reviewDate < reviewDate);
          if (prevDates.length > 0) {
            prevReviewDate = prevDates[prevDates.length - 1].reviewDate;
          }
          setPrevReviewDateState(prevReviewDate);
          // 保存済みの business_review を復元
          const currentReview = reviewList.find(r => r.reviewDate === reviewDate);
          if (currentReview?.businessReview) {
            try {
              const saved = JSON.parse(currentReview.businessReview);
              if (saved.textValues) setTextValues(saved.textValues);
              if (saved.comments) setComments(saved.comments);
            } catch { /* パース失敗は無視 */ }
          }
        }
      } catch { /* 失敗しても続行 */ }

      // 2. displayPartNo解決マップ取得（No.435対応、失敗しても表示は実キーにフォールバックするため続行）
      try {
        const partNoResp = await (WebAPI as any).request({
          query: `
            query getPartNoForReviewBusiness($projectId: String, $reviewDate: String) {
              getPartNo(projectId: $projectId, reviewDate: $reviewDate) {
                partNo
                displayPartNo
              }
            }
          `,
          variables: { projectId, ...(reviewDate && { reviewDate }) }
        });
        const partNoListForDisplay: Pick<PartNo, "partNo" | "displayPartNo">[] = partNoResp?.data?.getPartNo || [];
        setDisplayPartNoMap(
          Object.fromEntries(partNoListForDisplay.map(p => [p.partNo, p.displayPartNo || null]))
        );
      } catch { /* 失敗しても続行（表示は実キーにフォールバック） */ }

      // 3. 計画値取得（申請時 — reviewDate なし）
      const planResp = await (WebAPI as any).request({
        query: `
          query getProjectForBusinessReview($projectId: String, $useRDSData: Boolean) {
            getProjectById(projectId: $projectId, useRDSData: $useRDSData) {
              projectId
              originalProfitAndLoss
              displayOriginalProfitAndLoss
              originalProfitAndLossByMonth
              displayOriginalProfitAndLossByMonth
              companyPlantSnapshot
              productionLineId
              monthOfRecoveryOriginal
              reviewSchedule
              registrantDivisionId
              projectStartDate
              projectEndDate
            }
          }
        `,
        variables: { projectId, useRDSData: true }
      });
      const planProject = planResp?.data?.getProjectById;
      if (!planProject) {
        toast({ title: TITLE, description: t("common.message.projectNotFound"), status: 'warning', duration: 5000, isClosable: true });
        return;
      }
      if (planProject.registrantDivisionId) setRegistrantDivisionId(planProject.registrantDivisionId);
      if (!planProject.originalProfitAndLossByMonth) {
        toast({ title: TITLE, description: t("common.message.profitLossDataNotCreated"), status: 'warning', duration: 5000, isClosable: true });
        return;
      }

      const plData = JSON.parse(planProject.originalProfitAndLossByMonth);
      if (!plData.labels || plData.labels.length === 0) {
        toast({ title: TITLE, description: t("common.message.monthlyDataEmpty"), status: 'warning', duration: 5000, isClosable: true });
        return;
      }

      let labels: string[] = plData.labels.map((l: string) => {
        const parts = l.split(/[-\/]/);
        if (parts.length >= 2) return `${parts[0]}/${parseInt(parts[1])}`;
        return l;
      });
      // BasicInfoのEOL予定日（現在値）に合わせてラベルを延長または短縮
      // propから渡された値（常に最新）を優先し、フォールバックとしてAPI取得値を使用
      labels = adjustLabelsToEOL(labels, projectEndDateProp ?? planProject.projectEndDate);
      setMonthLabels(labels);
      const totalMonths = labels.length;

      // 量産開始月ラベル（CT・歩留まり期間平均の開始点）
      // BasicInfo の projectStartDate から特定。未設定なら計画開始月（labels[0]）で代替
      let productionStartLabel = labels[0];
      if (planProject.projectStartDate) {
        const psParts = (planProject.projectStartDate as string).split('-');
        const psLabel = `${psParts[0]}/${parseInt(psParts[1])}`;
        const foundIdx = labels.findIndex(l => l === psLabel);
        if (foundIdx >= 0) productionStartLabel = psLabel;
      }

      // 実際の PartNo 一覧を取得（quantityByPartNo のキー）＋申請時計画の数量を保存
      let planQtyLocal: Record<string, number[]> = {};
      // BOM から算出した申請時生産キャパ（テーブルデフォルト・チャートと同一ソース）
      let planCapByPNLocal: Record<string, number[]> = {};
      if (plData.quantityByPartNo && typeof plData.quantityByPartNo === 'object') {
        const partNos = Object.keys(plData.quantityByPartNo);
        if (partNos.length > 0) setRealPartNos(partNos);
        // 申請時計画の PartNo 別数量（グラフの「申請時計画」ラインに使用）
        const planQty: Record<string, number[]> = {};
        for (const [pn, arr] of Object.entries(plData.quantityByPartNo)) {
          planQty[pn] = extendOrTruncate((arr as string[]).map(toNum), totalMonths);
        }
        setPlanQuantityByPartNo(planQty);
        planQtyLocal = planQty;
      }

      // 振り返り境界インデックス（今回振り返り日 → ラベルから特定）
      let boundary = Math.min(18, totalMonths);
      if (reviewDate && labels.length > 0) {
        const rParts = reviewDate.split('-');
        const rLabel = `${rParts[0]}/${parseInt(rParts[1])}`;
        const foundIdx = labels.findIndex(l => l === rLabel);
        if (foundIdx >= 0) boundary = foundIdx;
      }
      setReviewBoundaryIdx(boundary);

      // 前回振り返り境界（前回reviewDateから特定。なければ申請時=0をフォールバック）
      let prevBoundary = 0;
      if (prevReviewDate && labels.length > 0) {
        const prParts = prevReviewDate.split('-');
        const prLabel = `${prParts[0]}/${parseInt(prParts[1])}`;
        const foundIdx = labels.findIndex(l => l === prLabel);
        if (foundIdx >= 0) prevBoundary = foundIdx;
      }
      setPrevReviewIdx(prevBoundary);

      // reviewScheduleからフェーズ名を取得するヘルパー
      let scheduleItems: { date: string; phase?: string }[] = [];
      if (planProject.reviewSchedule) {
        try { scheduleItems = JSON.parse(planProject.reviewSchedule); } catch { /* skip */ }
      }
      const getPhaseForDate = (date: string | null): string | undefined => {
        if (!date) return undefined;
        const ym = date.substring(0, 7); // "YYYY-MM"
        return scheduleItems.find(s => s.date && s.date.startsWith(ym))?.phase;
      };

      // 期間セレクタ用マイルストーン
      const milestoneList: { label: string; idx: number; isCurrent?: boolean }[] = [
        { label: t("features.InvestmentScreening.Review.common.milestoneApplication", { label: formatYearMonthByLocale(labels[0]) }), idx: 0 },
      ];
      // 過去の振り返り全件（今回より前、申請時=idx0 を除く）を時系列順で追加
      const pastScheduleItems = scheduleItems
        .filter(s => s.date && reviewDate && s.date < reviewDate)
        .sort((a, b) => a.date.localeCompare(b.date));
      for (const s of pastScheduleItems) {
        const parts = s.date.split('-');
        const lbl = `${parts[0]}/${parseInt(parts[1])}`;
        const foundIdx = labels.findIndex(l => l === lbl);
        if (foundIdx > 0 && foundIdx < totalMonths) {
          milestoneList.push({
            label: s.phase
              ? t("features.InvestmentScreening.Review.common.milestonePhase", { phase: s.phase, label: formatYearMonthByLocale(labels[foundIdx]) })
              : t("features.InvestmentScreening.Review.common.milestoneReview", { label: formatYearMonthByLocale(labels[foundIdx]) }),
            idx: foundIdx,
          });
        }
      }
      if (boundary > 0 && boundary < totalMonths) {
        const currentPhase = getPhaseForDate(reviewDate ?? null);
        const currentMilestoneLabel = currentPhase
          ? t("features.InvestmentScreening.Review.ReviewSales.milestone.currentReviewWithPhase", { phase: currentPhase, label: labels[boundary] ? formatYearMonthByLocale(labels[boundary]) : '' })
          : t("features.InvestmentScreening.Review.common.milestoneCurrentReview", { label: labels[boundary] ? formatYearMonthByLocale(labels[boundary]) : '' });
        milestoneList.push({ label: currentMilestoneLabel, idx: boundary, isCurrent: true });
      }
      // 将来の振り返りスケジュール（今回より後）を全件追加し、最初の次回日をデフォルト終了に使う
      const futureScheduleItems = scheduleItems
        .filter(s => s.date && reviewDate && s.date > reviewDate)
        .sort((a, b) => a.date.localeCompare(b.date));
      let nextBoundaryForDefault = totalMonths; // デフォルト終了: 次回振り返り日（なければ全期間末）
      for (const s of futureScheduleItems) {
        const parts = s.date.split('-');
        const lbl = `${parts[0]}/${parseInt(parts[1])}`;
        const foundIdx = labels.findIndex(l => l === lbl);
        if (foundIdx >= 0 && foundIdx < totalMonths) {
          if (nextBoundaryForDefault === totalMonths) nextBoundaryForDefault = foundIdx; // 最初の次回
          milestoneList.push({
            label: s.phase
              ? t("features.InvestmentScreening.Review.common.milestonePhase", { phase: s.phase, label: formatYearMonthByLocale(labels[foundIdx]) })
              : t("features.InvestmentScreening.Review.common.milestoneReview", { label: formatYearMonthByLocale(labels[foundIdx]) }),
            idx: foundIdx,
          });
        }
      }
      milestoneList.push({ label: t("features.InvestmentScreening.Review.ReviewBusiness.endLabel", { label: labels[totalMonths - 1] ? formatYearMonthByLocale(labels[totalMonths - 1]) : '' }), idx: totalMonths });
      setMilestones(milestoneList);
      // デフォルト期間（仕様: 開始=前回振り返り日・初回は申請時、終了=次回振り返り日）
      setPeriodStart(prevBoundary > 0 ? prevBoundary : 0);
      setPeriodEnd(nextBoundaryForDefault);

      // 通貨マスタ取得（ローカル通貨検出）
      // ProjectContext（親画面）で取得済みならそれを利用し、未取得（context未提供 or 空）の場合のみ自前で取得する
      const localCurrId = getLocalCurrencyId(planProject.companyPlantSnapshot);
      let detectedLocalCurrency = CurrencyNameJPY;
      try {
        let currencyData: Currency[] = projectContext?.currencyData ?? [];
        if (localCurrId) {
          const localCurr = currencyData.find((c: any) => c.currencyId === localCurrId);
          if (localCurr?.currencyName) detectedLocalCurrency = localCurr.currencyName;
        }
      } catch { /* fallback to JPY */ }
      setLocalCurrencyName(detectedLocalCurrency);
      setCurrencyName(detectedLocalCurrency);

      // P&L計画値の抽出（ローカル通貨版）
      // EOL変更によるラベル調整に合わせて配列長を揃える
      const profitPlan = extendOrTruncate((plData.profit || new Array(totalMonths).fill("0")).map(toNum), totalMonths);
      let salesCostPlan = extendOrTruncate((plData.salesCost || new Array(totalMonths).fill("0")).map(toNum), totalMonths);

      // salesCostPlanが全ゼロの場合（申請時に月次salesCostが未保存の旧データ）、
      // 年度別salesCostとtotalSalesの比率から月次を近似する
      const salesCostPlanAllZero = salesCostPlan.every((v: number) => v === 0);
      if (salesCostPlanAllZero && planProject.originalProfitAndLoss) {
        try {
          const annualData = JSON.parse(planProject.originalProfitAndLoss);
          const annualSalesCost: string[] = annualData?.profitAndLossByYear?.salesCost || [];
          const annualTotalSales: string[] = annualData?.profitAndLossByYear?.totalSales || [];
          const monthlyTotalSales = (plData.totalSales || new Array(totalMonths).fill("0")).map(toNum);
          if (annualSalesCost.length > 0 && annualTotalSales.length > 0) {
            const MONTHS_PER_YEAR = 12;
            salesCostPlan = monthlyTotalSales.map((mSales: number, i: number) => {
              const yearIdx = Math.min(Math.floor(i / MONTHS_PER_YEAR), annualSalesCost.length - 1);
              const aSales = Number(annualTotalSales[yearIdx] || 0);
              const aCost = Number(annualSalesCost[yearIdx] || 0);
              if (aSales === 0) return 0;
              return mSales * (aCost / aSales);
            });
          }
        } catch { /* 近似できない場合はゼロのまま */ }
      }

      // JPY版計画値
      const plDataJPY = planProject.displayOriginalProfitAndLossByMonth
        ? JSON.parse(planProject.displayOriginalProfitAndLossByMonth)
        : null;
      const profitPlanJPY = plDataJPY
        ? extendOrTruncate((plDataJPY.profit || new Array(totalMonths).fill("0")).map(toNum), totalMonths)
        : profitPlan;
      let salesCostPlanJPY = plDataJPY
        ? extendOrTruncate((plDataJPY.salesCost || new Array(totalMonths).fill("0")).map(toNum), totalMonths)
        : salesCostPlan;
      if (salesCostPlanAllZero && salesCostPlanJPY.every((v: number) => v === 0)) {
        const jpyAnnualRaw = planProject.displayOriginalProfitAndLoss
          ? (() => { try { return JSON.parse(planProject.displayOriginalProfitAndLoss); } catch { return null; } })()
          : null;
        const jpyAnnualSalesCost: string[] = jpyAnnualRaw?.profitAndLossByYear?.salesCost || [];
        const jpyAnnualTotalSales: string[] = jpyAnnualRaw?.profitAndLossByYear?.totalSales || [];
        const monthlyTotalSalesForJPY = plDataJPY
          ? (plDataJPY.totalSales || new Array(totalMonths).fill("0")).map(toNum)
          : (plData.totalSales || new Array(totalMonths).fill("0")).map(toNum);
        if (jpyAnnualSalesCost.length > 0 && jpyAnnualTotalSales.length > 0) {
          const MONTHS_PER_YEAR = 12;
          salesCostPlanJPY = monthlyTotalSalesForJPY.map((mSales: number, i: number) => {
            const yearIdx = Math.min(Math.floor(i / MONTHS_PER_YEAR), jpyAnnualSalesCost.length - 1);
            const aSales = Number(jpyAnnualTotalSales[yearIdx] || 0);
            const aCost = Number(jpyAnnualSalesCost[yearIdx] || 0);
            if (aSales === 0) return 0;
            return mSales * (aCost / aSales);
          });
        } else {
          salesCostPlanJPY = salesCostPlan;
        }
      }

      const paybackPlanVal: number | "回収不能" | null = getPaybackMonths(planProject);
      setPaybackPlan(paybackPlanVal);

      // 4. 振り返り値取得（reviewDate がある場合）
      let profitCurrent = new Array(totalMonths).fill(0);
      let salesCostCurrent = new Array(totalMonths).fill(0);
      let profitCurrentJPY = new Array(totalMonths).fill(0);
      let salesCostCurrentJPY = new Array(totalMonths).fill(0);
      let paybackRevVal: number | "回収不能" | null = null;
      let hasReview = false;

      if (reviewDate) {
        try {
          const revResp = await (WebAPI as any).request({
            query: `
              query getProjectForBusinessReviewDate($projectId: String, $useRDSData: Boolean, $reviewDate: String) {
                getProjectById(projectId: $projectId, useRDSData: $useRDSData, reviewDate: $reviewDate) {
                  projectId
                  originalProfitAndLoss
                  originalProfitAndLossByMonth
                  displayOriginalProfitAndLossByMonth
                  monthOfRecoveryOriginal
                }
              }
            `,
            variables: { projectId, useRDSData: true, reviewDate }
          });
          const revProject = revResp?.data?.getProjectById;
          if (revProject?.originalProfitAndLossByMonth) {
            const revPlData = JSON.parse(revProject.originalProfitAndLossByMonth);
            const rawProfit = (revPlData.profit || new Array(totalMonths).fill("0")).map(toNum);
            const rawSalesCost = (revPlData.salesCost || new Array(totalMonths).fill("0")).map(toNum);
            // 境界以降（将来月）は申請時計画値を使用し、実績未入力月で差異を生じさせない
            profitCurrent = rawProfit.map((v: number, i: number) => i < boundary ? v : (profitPlan[i] ?? 0));
            salesCostCurrent = rawSalesCost.map((v: number, i: number) => i < boundary ? v : (salesCostPlan[i] ?? 0));
            hasReview = rawProfit.some((v: number) => v !== 0);
          }
          paybackRevVal = getPaybackMonths(revProject);
          if (revProject?.displayOriginalProfitAndLossByMonth) {
            const revPlDataJPY = JSON.parse(revProject.displayOriginalProfitAndLossByMonth);
            const rawProfitJPY = (revPlDataJPY.profit || new Array(totalMonths).fill("0")).map(toNum);
            const rawSalesCostJPY = (revPlDataJPY.salesCost || new Array(totalMonths).fill("0")).map(toNum);
            profitCurrentJPY = rawProfitJPY.map((v: number, i: number) => i < boundary ? v : (profitPlanJPY[i] ?? 0));
            salesCostCurrentJPY = rawSalesCostJPY.map((v: number, i: number) => i < boundary ? v : (salesCostPlanJPY[i] ?? 0));
          }
        } catch (e) {
          console.warn("振り返りP&Lデータ取得エラー:", e);
        }
      }
      setPaybackReview(paybackRevVal);
      setHasReviewData(hasReview);

      // 5. 前回振り返り値取得（prevReviewDate がある場合）
      let profitPrevious = profitPlan;  // fallback: 申請時計画値
      let salesCostPrevious = salesCostPlan;
      let profitPreviousJPY = profitPlanJPY;
      let salesCostPreviousJPY = salesCostPlanJPY;
      if (prevReviewDate) {
        try {
          const prevResp = await (WebAPI as any).request({
            query: `
              query getProjectForPrevBusinessReview($projectId: String, $useRDSData: Boolean, $reviewDate: String) {
                getProjectById(projectId: $projectId, useRDSData: $useRDSData, reviewDate: $reviewDate) {
                  projectId
                  originalProfitAndLoss
                  displayOriginalProfitAndLoss
                  originalProfitAndLossByMonth
                  displayOriginalProfitAndLossByMonth
                  monthOfRecoveryOriginal
                }
              }
            `,
            variables: { projectId, useRDSData: true, reviewDate: prevReviewDate }
          });
          const prevProject = prevResp?.data?.getProjectById;
          if (prevProject?.originalProfitAndLossByMonth) {
            const prevPlData = JSON.parse(prevProject.originalProfitAndLossByMonth);
            profitPrevious = (prevPlData.profit || new Array(totalMonths).fill("0")).map(toNum);
            salesCostPrevious = (prevPlData.salesCost || new Array(totalMonths).fill("0")).map(toNum);
            const salesCostPrevAllZero = salesCostPrevious.every((v: number) => v === 0);
            if (salesCostPrevAllZero && prevProject.originalProfitAndLoss) {
              try {
                const prevAnnualData = JSON.parse(prevProject.originalProfitAndLoss);
                const prevAnnualSalesCost: string[] = prevAnnualData?.profitAndLossByYear?.salesCost || [];
                const prevAnnualTotalSales: string[] = prevAnnualData?.profitAndLossByYear?.totalSales || [];
                const prevMonthlyTotalSales = (prevPlData.totalSales || new Array(totalMonths).fill("0")).map(toNum);
                if (prevAnnualSalesCost.length > 0 && prevAnnualTotalSales.length > 0) {
                  const MONTHS_PER_YEAR = 12;
                  salesCostPrevious = prevMonthlyTotalSales.map((mSales: number, i: number) => {
                    const yearIdx = Math.min(Math.floor(i / MONTHS_PER_YEAR), prevAnnualSalesCost.length - 1);
                    const aSales = Number(prevAnnualTotalSales[yearIdx] || 0);
                    const aCost = Number(prevAnnualSalesCost[yearIdx] || 0);
                    if (aSales === 0) return 0;
                    return mSales * (aCost / aSales);
                  });
                }
              } catch { /* 近似できない場合はゼロのまま */ }
            }
            // 前回境界以降（将来月）は申請時計画値でマスク（今回値と同様の処理）
            profitPrevious = profitPrevious.map((v: number, i: number) => i < prevBoundary ? v : (profitPlan[i] ?? 0));
            salesCostPrevious = salesCostPrevious.map((v: number, i: number) => i < prevBoundary ? v : (salesCostPlan[i] ?? 0));
            // 前回振り返りのPartNo別数量（実績未登録時のフォールバック）
            if (prevPlData.quantityByPartNo && typeof prevPlData.quantityByPartNo === 'object') {
              const prevQty: Record<string, number[]> = {};
              for (const [pn, arr] of Object.entries(prevPlData.quantityByPartNo)) {
                prevQty[pn] = (arr as string[]).map(toNum);
              }
              setPrevQuantityByPartNo(prevQty);
            }
            // 前回振り返りの回収期間
            setPaybackPrev(getPaybackMonths(prevProject));
          }
          if (prevProject?.displayOriginalProfitAndLossByMonth) {
            const prevPlDataJPY = JSON.parse(prevProject.displayOriginalProfitAndLossByMonth);
            profitPreviousJPY = (prevPlDataJPY.profit || new Array(totalMonths).fill("0")).map(toNum);
            salesCostPreviousJPY = (prevPlDataJPY.salesCost || new Array(totalMonths).fill("0")).map(toNum);
            const salesCostPrevJPYAllZero = salesCostPreviousJPY.every((v: number) => v === 0);
            if (salesCostPrevJPYAllZero && prevProject.displayOriginalProfitAndLoss) {
              try {
                const prevAnnualDataJPY = JSON.parse(prevProject.displayOriginalProfitAndLoss);
                const prevAnnualSalesCostJPY: string[] = prevAnnualDataJPY?.profitAndLossByYear?.salesCost || [];
                const prevAnnualTotalSalesJPY: string[] = prevAnnualDataJPY?.profitAndLossByYear?.totalSales || [];
                const prevMonthlyTotalSalesJPY = (prevPlDataJPY.totalSales || new Array(totalMonths).fill("0")).map(toNum);
                if (prevAnnualSalesCostJPY.length > 0 && prevAnnualTotalSalesJPY.length > 0) {
                  const MONTHS_PER_YEAR = 12;
                  salesCostPreviousJPY = prevMonthlyTotalSalesJPY.map((mSales: number, i: number) => {
                    const yearIdx = Math.min(Math.floor(i / MONTHS_PER_YEAR), prevAnnualSalesCostJPY.length - 1);
                    const aSales = Number(prevAnnualTotalSalesJPY[yearIdx] || 0);
                    const aCost = Number(prevAnnualSalesCostJPY[yearIdx] || 0);
                    if (aSales === 0) return 0;
                    return mSales * (aCost / aSales);
                  });
                }
              } catch { /* 近似できない場合はゼロのまま */ }
            }
            // 前回境界以降は申請時計画値でマスク（JPY版）
            profitPreviousJPY = profitPreviousJPY.map((v: number, i: number) => i < prevBoundary ? v : (profitPlanJPY[i] ?? 0));
            salesCostPreviousJPY = salesCostPreviousJPY.map((v: number, i: number) => i < prevBoundary ? v : (salesCostPlanJPY[i] ?? 0));
          }
        } catch { /* fallback to plan */ }
      }
      setOperatingProfitData({ originalPlan: profitPlan, previous: profitPrevious, current: profitCurrent });
      setManufacturingCostData({ originalPlan: salesCostPlan, previous: salesCostPrevious, current: salesCostCurrent });
      setOperatingProfitDataJPY({ originalPlan: profitPlanJPY, previous: profitPreviousJPY, current: profitCurrentJPY });
      setManufacturingCostDataJPY({ originalPlan: salesCostPlanJPY, previous: salesCostPreviousJPY, current: salesCostCurrentJPY });

      // 6. 製造ライン取得（⑤⑥: 稼働率・歩留まり・サイクルタイム）
      const productionLineId = planProject.productionLineId;
      // step7（BOM × ライン生産キャパ算出）に引き継ぐ生データ
      let planLineWorkingDays: string | null = null;
      let planLineNonWorkingHours: string | null = null;
      let revLineWorkingDays: string | null = null;
      let revLineNonWorkingHours: string | null = null;
      let prevLineWorkingDays: string | null = null;
      let prevLineNonWorkingHours: string | null = null;

      if (productionLineId) {
        const plnFields = `productionLineId yieldRate cycleTime nonWorkingHours workingDays`;
        try {
          // 計画値
          const plnPlanResp = await (WebAPI as any).request({
            query: `
              query getProductionLineForBusinessReview($projectId: String, $productionLineId: String) {
                getProductionLineById(projectId: $projectId, productionLineId: $productionLineId) { ${plnFields} }
              }
            `,
            variables: { projectId, productionLineId }
          });
          const planLine = plnPlanResp?.data?.getProductionLineById;
          if (planLine) {
            setYieldPlan(planLine.yieldRate != null ? Number(planLine.yieldRate) : null);
            setCycleTimePlan(planLine.cycleTime != null ? Number(planLine.cycleTime) : null);
            const availPlan = extractAvailabilityRates(planLine.nonWorkingHours, labels);
            planLineWorkingDays = planLine.workingDays ?? null;
            planLineNonWorkingHours = planLine.nonWorkingHours ?? null;

            if (reviewDate) {
              try {
                // 振り返り値
                const plnRevResp = await (WebAPI as any).request({
                  query: `
                    query getProductionLineForBusinessReviewDate($projectId: String, $productionLineId: String, $reviewDate: String) {
                      getProductionLineById(projectId: $projectId, productionLineId: $productionLineId, reviewDate: $reviewDate) { ${plnFields} }
                    }
                  `,
                  variables: { projectId, productionLineId, reviewDate }
                });
                const revLine = plnRevResp?.data?.getProductionLineById;
                if (revLine) {
                  setYieldReview(revLine.yieldRate != null ? Number(revLine.yieldRate) : null);
                  setCycleTimeReview(revLine.cycleTime != null ? Number(revLine.cycleTime) : null);
                  const availReview = extractAvailabilityRates(revLine.nonWorkingHours, labels);
                  revLineWorkingDays = revLine.workingDays ?? null;
                  revLineNonWorkingHours = revLine.nonWorkingHours ?? null;

                  // 前回振り返りの稼働率
                  let availPrev = availPlan;
                  if (prevReviewDate) {
                    try {
                      const plnPrevResp = await (WebAPI as any).request({
                        query: `
                          query getProductionLineForPrevBusinessReview($projectId: String, $productionLineId: String, $reviewDate: String) {
                            getProductionLineById(projectId: $projectId, productionLineId: $productionLineId, reviewDate: $reviewDate) { yieldRate cycleTime nonWorkingHours workingDays }
                          }
                        `,
                        variables: { projectId, productionLineId, reviewDate: prevReviewDate }
                      });
                      const prevLine = plnPrevResp?.data?.getProductionLineById;
                      if (prevLine?.nonWorkingHours) {
                        availPrev = extractAvailabilityRates(prevLine.nonWorkingHours, labels);
                        prevLineWorkingDays = prevLine.workingDays ?? null;
                        prevLineNonWorkingHours = prevLine.nonWorkingHours ?? null;
                      }
                      if (prevLine?.yieldRate != null) setYieldPrev(Number(prevLine.yieldRate));
                      if (prevLine?.cycleTime != null) setCycleTimePrev(Number(prevLine.cycleTime));
                    } catch { /* fallback to plan */ }
                  }
                  setOperatingRateRealData({ originalPlan: availPlan, previous: availPrev, current: availReview });
                }
              } catch (e) {
                console.warn("振り返り製造ライン取得エラー:", e);
                setOperatingRateRealData({ originalPlan: availPlan, previous: availPlan, current: new Array(totalMonths).fill(0) });
              }
            } else {
              setOperatingRateRealData({ originalPlan: availPlan, previous: availPlan, current: new Array(totalMonths).fill(0) });
            }
          }
        } catch (e) {
          console.warn("製造ライン取得エラー:", e);
        }
      }

      // 7. 設備取得（⑦: 人員数）
      try {
        // 計画値
        const eqPlanResp = await (WebAPI as any).request({
          query: `
            query getEquipmentForBusinessReview($projectId: String) {
              getEquipmentForEdit(projectId: $projectId) {
                unskilledWorker
                multiSkilledWorker
                technician
              }
            }
          `,
          variables: { projectId }
        });
        const planEqs = eqPlanResp?.data?.getEquipmentForEdit || [];
        const planHeadcount = planEqs.reduce(
          (sum: number, eq: any) => sum + (eq.unskilledWorker || 0) + (eq.multiSkilledWorker || 0) + (eq.technician || 0), 0
        );
        setHeadcountPlan(planHeadcount > 0 ? planHeadcount : null);

        if (reviewDate) {
          const eqRevResp = await (WebAPI as any).request({
            query: `
              query getEquipmentForBusinessReviewDate($projectId: String, $reviewDate: String) {
                getEquipmentForEdit(projectId: $projectId, reviewDate: $reviewDate) {
                  unskilledWorker
                  multiSkilledWorker
                  technician
                }
              }
            `,
            variables: { projectId, reviewDate }
          });
          const revEqs = eqRevResp?.data?.getEquipmentForEdit || [];
          const revHeadcount = revEqs.reduce(
            (sum: number, eq: any) => sum + (eq.unskilledWorker || 0) + (eq.multiSkilledWorker || 0) + (eq.technician || 0), 0
          );
          setHeadcountReview(revHeadcount > 0 ? revHeadcount : null);
        }
        if (prevReviewDate) {
          try {
            const eqPrevResp = await (WebAPI as any).request({
              query: `
                query getEquipmentForPrevBusinessReview($projectId: String, $reviewDate: String) {
                  getEquipmentForEdit(projectId: $projectId, reviewDate: $reviewDate) {
                    unskilledWorker
                    multiSkilledWorker
                    technician
                  }
                }
              `,
              variables: { projectId, reviewDate: prevReviewDate }
            });
            const prevEqs = eqPrevResp?.data?.getEquipmentForEdit || [];
            const prevHeadcount = prevEqs.reduce(
              (sum: number, eq: any) => sum + (eq.unskilledWorker || 0) + (eq.multiSkilledWorker || 0) + (eq.technician || 0), 0
            );
            setHeadcountPrev(prevHeadcount > 0 ? prevHeadcount : null);
          } catch (e) {
            console.warn("前回設備取得エラー:", e);
          }
        }
      } catch (e) {
        console.warn("設備取得エラー:", e);
      }

      // 8. BOM取得 + 生産キャパ算出（④ 生産数量グラフの計画値）
      // 計算式: (稼働日数 + 休日出勤日数) × 86400秒 ÷ CT(秒) × 稼働率 × 歩留まり × ライン数
      try {
        const bomFields = `partNo cycleTimeMonthly yieldMonthly`;
        // 計画値BOM
        const bomPlanResp = await (WebAPI as any).request({
          query: `
            query getBomForBusinessReview($projectId: String) {
              getBomByProjectId(projectId: $projectId) { ${bomFields} }
            }
          `,
          variables: { projectId }
        });
        const planBom: any[] = bomPlanResp?.data?.getBomByProjectId || [];

        const capOriginalPlan = calcProductionCapacityFromBom(
          planBom, planLineWorkingDays, planLineNonWorkingHours, labels
        );
        let capPrevious = capOriginalPlan;
        let capCurrent = capOriginalPlan;

        // 前回振り返りBOM（1回だけ取得して変数に保持）
        let prevBomFetched: any[] = [];
        if (prevReviewDate) {
          try {
            const bomPrevResp = await (WebAPI as any).request({
              query: `
                query getBomForPrevBusinessReview($projectId: String, $reviewDate: String) {
                  getBomByProjectId(projectId: $projectId, reviewDate: $reviewDate) { ${bomFields} }
                }
              `,
              variables: { projectId, reviewDate: prevReviewDate }
            });
            prevBomFetched = bomPrevResp?.data?.getBomByProjectId || [];
            if (prevBomFetched.length > 0) {
              capPrevious = calcProductionCapacityFromBom(
                prevBomFetched,
                prevLineWorkingDays ?? planLineWorkingDays,
                prevLineNonWorkingHours ?? planLineNonWorkingHours,
                labels
              );
            }
          } catch { /* fallback to plan */ }
        }

        // 今回振り返りBOM
        let revBomForStats: any[] = [];
        if (reviewDate) {
          try {
            const bomRevResp = await (WebAPI as any).request({
              query: `
                query getBomForBusinessReviewDate($projectId: String, $reviewDate: String) {
                  getBomByProjectId(projectId: $projectId, reviewDate: $reviewDate) { ${bomFields} }
                }
              `,
              variables: { projectId, reviewDate }
            });
            const revBom: any[] = bomRevResp?.data?.getBomByProjectId || [];
            revBomForStats = revBom;
            if (revBom.length > 0) {
              capCurrent = calcProductionCapacityFromBom(
                revBom,
                revLineWorkingDays ?? planLineWorkingDays,
                revLineNonWorkingHours ?? planLineNonWorkingHours,
                labels
              );
            }
          } catch { /* fallback to plan */ }
        }

        if (planBom.length > 0) {
          setProductionCapacityData({ originalPlan: capOriginalPlan, previous: capPrevious, current: capCurrent });

          // PartNo別キャパを算出（チャートのPartNo個別表示に使用）
          // 独立計算後、全体集計に合わせて比例配分する（sum of PartNos = 全体集計）
          const planRawByPN = calcProductionCapacityByPartNo(planBom, planLineWorkingDays, planLineNonWorkingHours, labels);
          const planCapByPN = scaleCapacityByPartNoToTotal(planRawByPN, capOriginalPlan, labels.length);
          setPlanCapacityByPartNo(planCapByPN);
          planCapByPNLocal = planCapByPN;

          // 前回BOMは上で取得済みのものを再利用（二重フェッチを避ける）
          const prevBomForPNCalc = prevReviewDate
            ? (prevBomFetched.length > 0 ? prevBomFetched : planBom)
            : planBom;
          const prevRawByPN = calcProductionCapacityByPartNo(prevBomForPNCalc, prevLineWorkingDays ?? planLineWorkingDays, prevLineNonWorkingHours ?? planLineNonWorkingHours, labels);
          setPrevCapacityByPartNo(scaleCapacityByPartNoToTotal(prevRawByPN, capPrevious, labels.length));

          if (reviewDate && revBomForStats.length > 0) {
            const currentRawByPN = calcProductionCapacityByPartNo(revBomForStats, revLineWorkingDays ?? planLineWorkingDays, revLineNonWorkingHours ?? planLineNonWorkingHours, labels);
            setCurrentCapacityByPartNo(scaleCapacityByPartNoToTotal(currentRawByPN, capCurrent, labels.length));
          } else {
            setCurrentCapacityByPartNo(planCapByPN);
          }
        }

        // ⑥ CT・歩留まり 期間平均（量産開始月〜境界前月の平均値で比較）
        // 前回値は prevReviewDate BOM を別途取得せず、今回BOM（revBomForStats）で期間を変えて算出
        if (planBom.length > 0) {
          const todayEndLabel = boundary > 0 ? labels[boundary - 1] : labels[0];
          const prevEndLabel = prevBoundary > 0 ? labels[prevBoundary - 1] : null;
          setProductionStartLabelState(productionStartLabel);
          setTodayEndLabelState(todayEndLabel);
          setPrevEndLabelState(prevEndLabel);
          const allPartNosInBom = [...new Set(planBom.map((b: any) => b.partNo).filter(Boolean))] as string[];

          // PartNo間統計（複数PartNo時: max/min/avg）
          const planPeriodStats = calcPeriodAvgBomStats(planBom, productionStartLabel, todayEndLabel, labels);
          if (planPeriodStats) {
            setCtStatsPlan(planPeriodStats.ct);
            setYieldStatsPlan(planPeriodStats.yield);
          }

          if (revBomForStats.length > 0) {
            const revPeriodStats = calcPeriodAvgBomStats(revBomForStats, productionStartLabel, todayEndLabel, labels);
            if (revPeriodStats) {
              setCtStatsReview(revPeriodStats.ct);
              setYieldStatsReview(revPeriodStats.yield);
            }
            if (prevEndLabel) {
              const prevPeriodStats = calcPeriodAvgBomStats(revBomForStats, productionStartLabel, prevEndLabel, labels);
              if (prevPeriodStats) {
                setCtStatsPrev(prevPeriodStats.ct);
                setYieldStatsPrev(prevPeriodStats.yield);
              }
            }
          }

          // 単一値（yieldPlan/cycleTimePlan等）をBOM期間平均で上書き（製造ライン単一値より優先）
          if (allPartNosInBom.length >= 1) {
            const firstPn = allPartNosInBom[0];
            const planFirst = planBom.filter((b: any) => b.partNo === firstPn);
            setYieldPlan(Math.round(calcPeriodAvgYield(planFirst, productionStartLabel, todayEndLabel, labels) * 10) / 10);
            setCycleTimePlan(Math.round(calcPeriodAvgCT(planFirst, productionStartLabel, todayEndLabel, labels) * 10) / 10);

            if (revBomForStats.length > 0) {
              const revFirst = revBomForStats.filter((b: any) => b.partNo === firstPn);
              setYieldReview(Math.round(calcPeriodAvgYield(revFirst, productionStartLabel, todayEndLabel, labels) * 10) / 10);
              setCycleTimeReview(Math.round(calcPeriodAvgCT(revFirst, productionStartLabel, todayEndLabel, labels) * 10) / 10);

              if (prevEndLabel) {
                setYieldPrev(Math.round(calcPeriodAvgYield(revFirst, productionStartLabel, prevEndLabel, labels) * 10) / 10);
                setCycleTimePrev(Math.round(calcPeriodAvgCT(revFirst, productionStartLabel, prevEndLabel, labels) * 10) / 10);
              }
            }
          }

          // PartNo別個別値（ドロップダウン切り替え用）
          // PartNo数にかかわらず常に全PartNoのデータを構築する（1件でも申請書側で参照するため）
          {
            const yieldByPN: Record<string, { plan: number; prev: number; current: number }> = {};
            const ctByPN: Record<string, { plan: number; prev: number; current: number }> = {};
            const revBomForPN = revBomForStats.length > 0 ? revBomForStats : planBom;
            for (const pn of allPartNosInBom) {
              const planItems = planBom.filter((b: any) => b.partNo === pn);
              const revItems = revBomForPN.filter((b: any) => b.partNo === pn);
              yieldByPN[pn] = {
                plan: Math.round(calcPeriodAvgYield(planItems, productionStartLabel, todayEndLabel, labels) * 10) / 10,
                current: Math.round(calcPeriodAvgYield(revItems, productionStartLabel, todayEndLabel, labels) * 10) / 10,
                prev: prevEndLabel
                  ? Math.round(calcPeriodAvgYield(revItems, productionStartLabel, prevEndLabel, labels) * 10) / 10
                  : Math.round(calcPeriodAvgYield(planItems, productionStartLabel, todayEndLabel, labels) * 10) / 10,
              };
              ctByPN[pn] = {
                plan: Math.round(calcPeriodAvgCT(planItems, productionStartLabel, todayEndLabel, labels) * 10) / 10,
                current: Math.round(calcPeriodAvgCT(revItems, productionStartLabel, todayEndLabel, labels) * 10) / 10,
                prev: prevEndLabel
                  ? Math.round(calcPeriodAvgCT(revItems, productionStartLabel, prevEndLabel, labels) * 10) / 10
                  : Math.round(calcPeriodAvgCT(planItems, productionStartLabel, todayEndLabel, labels) * 10) / 10,
              };
            }
            setYieldAtBoundaryByPartNo(yieldByPN);
            setCtAtBoundaryByPartNo(ctByPN);
          }
        }
      } catch (e) {
        console.warn("BOM・生産キャパ算出エラー:", e);
      }

      // 9. 生産実績データ読み込み＋デフォルト値計算
      if (reviewDate) {
        try {
          // 8a. 今回振り返りの保存済み実績
          let newActuals: Record<string, number> = {};
          let newSaved: Record<string, number[]> = {};
          const paResp = await (WebAPI as any).request({
            query: `
              query getProductionActualsForBusiness($projectId: String!, $reviewDate: String!) {
                getProductionActuals(projectId: $projectId, reviewDate: $reviewDate) {
                  partNo
                  productionActuals
                }
              }
            `,
            variables: { projectId, reviewDate }
          });
          const paList: { partNo: string; productionActuals: string }[] = paResp?.data?.getProductionActuals || [];
          for (const item of paList) {
            try {
              const arr: number[] = JSON.parse(item.productionActuals);
              newSaved[item.partNo] = arr;
              arr.forEach((v, i) => { newActuals[`${item.partNo}-${i}`] = v; });
            } catch { /* パース失敗は無視 */ }
          }

          // 8b. 前回振り返りの保存済み実績（デフォルト値の計算元 + グラフの前回値）
          let prevSaved: Record<string, number[]> = {};
          if (prevReviewDate) {
            try {
              const prevPaResp = await (WebAPI as any).request({
                query: `
                  query getPrevProductionActualsForBusiness($projectId: String!, $reviewDate: String!) {
                    getProductionActuals(projectId: $projectId, reviewDate: $reviewDate) {
                      partNo
                      productionActuals
                    }
                  }
                `,
                variables: { projectId, reviewDate: prevReviewDate }
              });
              const prevPaList: { partNo: string; productionActuals: string }[] = prevPaResp?.data?.getProductionActuals || [];
              for (const item of prevPaList) {
                try {
                  prevSaved[item.partNo] = JSON.parse(item.productionActuals);
                } catch { /* パース失敗は無視 */ }
              }
            } catch (e) {
              console.warn("前回振り返り生産実績取得エラー:", e);
            }
          }

          // 8c. デフォルト値を計算してマージ
          // 優先順位: 今回保存済み値 > 前回値（前回境界前） > 計画値（将来月） > 空欄（今回実績期間）
          // 将来月のデフォルト: BOM 生産キャパ（チャートの「申請時計画値」と同一ソース）
          const defaultActuals: Record<string, number> = {};
          for (const pn of Object.keys(planQtyLocal)) {
            const planArr = planCapByPNLocal[pn] ?? planQtyLocal[pn] ?? [];
            const prevArr = prevSaved[pn];
            for (let i = 0; i < totalMonths; i++) {
              const key = `${pn}-${i}`;
              if (newActuals[key] !== undefined) continue; // 今回保存済みが最優先
              if (prevArr && i < prevBoundary) {
                // 前回振り返り済み期間: 前回保存値をデフォルトに
                defaultActuals[key] = prevArr[i] ?? 0;
              } else if (i >= boundary) {
                // 将来月: 計画値をデフォルトに
                defaultActuals[key] = planArr[i] ?? 0;
              }
              // prevBoundary <= i < boundary: 今回の新規実績期間 → 空欄のまま
            }
          }

          setProductionActuals({ ...defaultActuals, ...newActuals });
          setSavedProductionByPartNo(newSaved);
          setPrevSavedProductionByPartNo(prevSaved);
        } catch (e) {
          console.warn("生産実績取得エラー:", e);
        }
      }

      // データ不足 toast（ReviewSales.tsx の forEach パターンに準拠）
      const missingDataSources: string[] = [];
      if (!hasReview && reviewDate) {
        missingDataSources.push(t("features.InvestmentScreening.Review.ReviewBusiness.screenName.profitLoss"));
      }
      missingDataSources.forEach((displayName) => {
        toast({
          title: TITLE,
          description: t("common.message.pleaseRegisterFrom", { name: displayName }),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      });

    } catch (error) {
      console.error("ReviewBusiness fetchData error:", error);
      toast({ title: TITLE, description: t("common.message.fetchFailed"), status: 'error', duration: 5000, isClosable: true });
    } finally {
      setIsLoading(false);
    }
  };

  // ========== スプレッドシート連携 ==========

  /** 表示中の PartNo 一覧 */
  const activePartNos = realPartNos;

  /** 現在の月ラベル配列 */
  const baseLabelsForSpreadSheet = monthLabels;

  /** スプレッドシートウィンドウ監視用インターバルの参照 */
  const windowCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /** 「画面で入力」ボタン押下: スプレッドシートタブを閉じて手動モードに戻す */
  const handleManualClick = () => {
    spreadSheetWindow?.close();
  };

  /**
   * 「GoogleSpread」ボタン押下
   * スプレッドシートを作成してデータを書き込み、新規タブで開く
   */
  const handleGoogleSpreadClick = async () => {
    if (productionInputMode === "google") {
      return;
    }

    try {
      setIsSpreadSheetLoading(true);
      setProductionInputMode("google");

      // ポップアップブロック回避：非同期処理より前にユーザー操作起因でウィンドウを確保
      const newWindow = window.open("", "_blank");

      if (!await googleAPI.signIn()) {
        throw new Error("signIn error");
      }

      const folderPath = `SpreadSheet/Review/${projectId}`;
      let folderId = await googleAPI.createFolderPath(folderPath, null);
      if (!folderId) folderId = "";

      const fileName = "ReviewProductionActuals";
      const ssId = await googleAPI.createSpreadSheet(fileName, folderId, null);
      setSpreadsheetId(ssId);

      if (ssId) {
        // シート追加・整列
        await googleAPI.addSheets(ssId, [SHEET_NAME_PRODUCTION_ACTUALS]);
        await googleAPI.reorderSheets(ssId, [SHEET_NAME_PRODUCTION_ACTUALS]);

        // 書き込みデータ作成: ヘッダー行 + PartNo行
        const currentPartNos = activePartNos;
        const currentLabels = baseLabelsForSpreadSheet;
        const headerRow = [t("features.InvestmentScreening.Review.ReviewBusiness.spreadsheetCategoryHeader"), ...currentLabels];
        const dataRows = currentPartNos.map(pn => [
          pn,
          ...currentLabels.map((_, i) => productionActuals[`${pn}-${i}`] || 0),
        ]);
        const writeData = [headerRow, ...dataRows];

        // シートをクリアして書き込み
        const clearRows = Math.max(writeData.length + 5, 20);
        const clearCols = Math.max(currentLabels.length + 2, 10);
        const emptyValues = Array.from({ length: clearRows }, () => Array(clearCols).fill(""));
        await googleAPI.spreadSheetSetValue(ssId, SHEET_NAME_PRODUCTION_ACTUALS, 1, 1, emptyValues);
        await googleAPI.spreadSheetSetValue(ssId, SHEET_NAME_PRODUCTION_ACTUALS, 1, 1, writeData);

        // 事前確保したウィンドウをスプレッドシートに遷移
        if (newWindow) {
          newWindow.location.href = googleAPI.getOpenSpradSheetUrl(ssId);
          setSpreadSheetWindow(newWindow);

          // GoogleSpreadシートウィンドウを手動で閉じられた場合
          if (windowCheckIntervalRef.current) clearInterval(windowCheckIntervalRef.current);
          const interval = setInterval(() => {
            if (newWindow.closed) {
              clearInterval(interval); // 監視を停止
              windowCheckIntervalRef.current = null;
              setProductionInputMode("manual");
            }
          }, 500); // 500ms ごとに監視
          windowCheckIntervalRef.current = interval;
        }

        setProductionInputMode("google");
      }
    } catch (error) {
      console.error("GoogleSpread Error:", error);
      spreadSheetWindow?.close();
      setProductionInputMode("manual");
      toast({
        title: TITLE,
        description: t("common.message.spreadsheetUnavailable"),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSpreadSheetLoading(false);
    }
  };

  // SpreadSheetウィンドウの状態監視（ウィンドウが存在すればgoogleモード、なければmanualモード）
  useEffect(() => {
    if (spreadSheetWindow) {
      setProductionInputMode("google");
    } else {
      setProductionInputMode("manual");
    }
  }, [spreadSheetWindow]);

  // コンポーネントのアンマウント時（画面遷移時）にGoogleSpreadタブを閉じ、監視を停止する
  useEffect(() => {
    return () => {
      if (spreadSheetWindow && !spreadSheetWindow.closed) {
        spreadSheetWindow.close();
      }
    };
  }, [spreadSheetWindow]);

  useEffect(() => {
    return () => {
      if (windowCheckIntervalRef.current) {
        clearInterval(windowCheckIntervalRef.current);
        windowCheckIntervalRef.current = null;
      }
    };
  }, []);

  /**
   * 「データ連携」ボタン押下
   * スプレッドシートから生産実績を読み込んで productionActuals に反映する
   */
  const handleSyncData = async () => {
    if (!spreadsheetId || productionInputMode !== "google") return;

    try {
      setIsSpreadSheetLoading(true);

      // トークン有効性チェック
      if (!await googleAPI.ensureValidToken()) {
        googleAPI.signIn();
      }

      const range = `${SHEET_NAME_PRODUCTION_ACTUALS}!A1:ZZ`;
      const result = await googleAPI.spreadSheetGetValues(spreadsheetId, range);

      if (!result.values || result.values.length < 2) {
        toast({
          title: TITLE,
          description: t("common.message.spreadsheetDataEmpty"),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const header: string[] = result.values[0].map((v: any) => String(v));
      const currentLabels = baseLabelsForSpreadSheet;

      // ヘッダー列から月ラベル → monthIndex のマッピングを作成
      const colToMonthIdx: Record<number, number> = {};
      const invalidHeaders: string[] = [];
      for (let col = 1; col < header.length; col++) {
        const label = header[col];
        const idx = currentLabels.indexOf(label);
        if (idx >= 0) {
          colToMonthIdx[col] = idx;
        } else if (label) {
          invalidHeaders.push(label);
        }
      }

      // 各PartNo行を読み込んで productionActuals を更新
      const newActuals: Record<string, number> = { ...productionActuals };
      const conversionErrors: string[] = [];

      for (let row = 1; row < result.values.length; row++) {
        const rowData: any[] = result.values[row];
        const pn = String(rowData[0] ?? "").trim();
        if (!pn) continue;

        for (const [colStr, idx] of Object.entries(colToMonthIdx)) {
          const col = parseInt(colStr);
          const raw = String(rowData[col] ?? "0").replace(/,/g, "");
          const val = Number(raw);
          if (isNaN(val)) {
            conversionErrors.push(t("features.InvestmentScreening.Review.common.invalidValue", { partNo: pn, label: currentLabels[idx], raw }));
            newActuals[`${pn}-${idx}`] = 0;
          } else {
            newActuals[`${pn}-${idx}`] = val;
          }
        }
      }

      setProductionActuals(newActuals);

      const hasError = conversionErrors.length > 0 || invalidHeaders.length > 0;
      toast({
        title: TITLE,
        description: hasError
          ? t("features.InvestmentScreening.Review.common.dataSyncCompletedWithErrors", { count: conversionErrors.length + invalidHeaders.length })
          : t("features.InvestmentScreening.Review.common.dataSyncCompleted"),
        status: hasError ? 'warning' : 'success',
        duration: hasError ? 5000 : 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("SyncData Error:", error);
      toast({
        title: TITLE,
        description: t("common.message.syncFailed"),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSpreadSheetLoading(false);
    }
  };

  // ========== Excel出力 ==========
  const handleExcelExport = () => {
    if (!hasReviewData) {
      toast({ title: TITLE, description: t('common.message.excelExportNoData'), status: 'warning', duration: 5000, isClosable: true });
      return;
    }

    const workbook = XLSX.utils.book_new();

    // 出力日時シート
    const now = new Date(Date.now() + 9 * 60 * 60 * 1000);
    const pad = (n: number) => String(n).padStart(2, '0');
    const dateStr = `${now.getUTCFullYear()}-${pad(now.getUTCMonth() + 1)}-${pad(now.getUTCDate())} ` +
      `${pad(now.getUTCHours())}:${pad(now.getUTCMinutes())}:${pad(now.getUTCSeconds())}`;
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([[dateStr]]), t('features.InvestmentScreening.Review.ReviewBusiness.section.exportDateTime'));

    const unit = currencyName;

    // ① 営業利益シート
    const profitData = currencyName === CurrencyNameJPY
      ? (operatingProfitDataJPY ?? operatingProfitData)
      : operatingProfitData;
    if (profitData) {
      const data: (string | number)[][] = [
        ['', ...monthLabels],
        [`${t('common.label.originalPlan')} (${unit})`, ...profitData.originalPlan],
        [`${t('common.label.previousValue')} (${unit})`, ...profitData.previous],
        [`${t('common.label.currentValue')} (${unit})`, ...profitData.current],
      ];
      const ws = XLSX.utils.aoa_to_sheet(data);
      ws['!cols'] = getAutoColWidths(data);
      XLSX.utils.book_append_sheet(workbook, ws, t('features.InvestmentScreening.Review.ReviewBusiness.section.operatingProfit'));
    }

    // ② 製造原価シート
    const costData = currencyName === CurrencyNameJPY
      ? (manufacturingCostDataJPY ?? manufacturingCostData)
      : manufacturingCostData;
    if (costData) {
      const data: (string | number)[][] = [
        ['', ...monthLabels],
        [`${t('common.label.originalPlan')} (${unit})`, ...costData.originalPlan],
        [`${t('common.label.previousValue')} (${unit})`, ...costData.previous],
        [`${t('common.label.currentValue')} (${unit})`, ...costData.current],
      ];
      const ws = XLSX.utils.aoa_to_sheet(data);
      ws['!cols'] = getAutoColWidths(data);
      XLSX.utils.book_append_sheet(workbook, ws, t('features.InvestmentScreening.Review.ReviewBusiness.section.manufacturingCost'));
    }

    // ④ 生産キャパシート（全体合算 + PartNo別縦積み）
    if (productionCapacityData) {
      const totalLen = monthLabels.length;
      const hasPNCapacity = Object.keys(planCapacityByPartNo).length > 0;

      // 全体合算の今回値: 保存済み実績があればPartNo別に合算、なければBOMキャパ
      const overallCurrent: number[] = hasPNCapacity
        ? (() => {
          const result = new Array(totalLen).fill(0);
          for (const pn of activePartNos) {
            const pnData = savedProductionByPartNo[pn] ?? currentCapacityByPartNo[pn] ?? planCapacityByPartNo[pn];
            if (pnData) for (let i = 0; i < totalLen; i++) result[i] += pnData[i] ?? 0;
          }
          return result;
        })()
        : productionCapacityData.current;

      // 全体合算の前回値: 前回保存済み実績があればPartNo別に合算、なければBOMキャパ
      const hasPrevSaved = activePartNos.some(pn => prevSavedProductionByPartNo[pn]);
      const overallPrevious: number[] = (hasPNCapacity && hasPrevSaved)
        ? (() => {
          const result = new Array(totalLen).fill(0);
          for (const pn of activePartNos) {
            const pnData = prevSavedProductionByPartNo[pn] ?? prevCapacityByPartNo[pn];
            if (pnData) for (let i = 0; i < totalLen; i++) result[i] += pnData[i] ?? 0;
          }
          return result;
        })()
        : productionCapacityData.previous;

      const data: (string | number)[][] = [];
      if (activePartNos.length >= 2) data.push([t('features.InvestmentScreening.Review.ReviewBusiness.option.allPartNos')]);
      data.push(['', ...monthLabels]);
      data.push([`${t('common.label.originalPlan')} (pcs)`, ...productionCapacityData.originalPlan]);
      data.push([`${t('common.label.previousValue')} (pcs)`, ...overallPrevious]);
      data.push([`${t('common.label.currentValue')} (pcs)`, ...overallCurrent]);
      if (activePartNos.length >= 2) {
        for (const pn of activePartNos) {
          const currentPnData = savedProductionByPartNo[pn] ?? currentCapacityByPartNo[pn] ?? planCapacityByPartNo[pn];
          const planPnData = planCapacityByPartNo[pn];
          const prevPnData = prevCapacityByPartNo[pn];
          if (!currentPnData && !planPnData && !prevPnData) continue;
          data.push([]);
          data.push([`${t('common.label.partNo')} ${displayPartNoMap[pn] || pn}`]);
          data.push(['', ...monthLabels]);
          data.push([`${t('common.label.originalPlan')} (pcs)`, ...(planPnData ?? [])]);
          data.push([`${t('common.label.previousValue')} (pcs)`, ...(prevPnData ?? [])]);
          data.push([`${t('common.label.currentValue')} (pcs)`, ...(currentPnData ?? [])]);
        }
      }
      const ws = XLSX.utils.aoa_to_sheet(data);
      ws['!cols'] = getAutoColWidths(data);
      XLSX.utils.book_append_sheet(workbook, ws, t('features.InvestmentScreening.Review.ReviewBusiness.section.productionVolume'));

      // 累積生産数量シート（月別データを累積に変換）
      const cumData: (string | number)[][] = [];
      if (activePartNos.length >= 2) cumData.push([t('features.InvestmentScreening.Review.ReviewBusiness.option.allPartNos')]);
      cumData.push(['', ...monthLabels]);
      cumData.push([`${t('common.label.originalPlan')} (${t('features.InvestmentScreening.Review.ReviewBusiness.unit.pieces')})`, ...calcCumulative(productionCapacityData.originalPlan)]);
      cumData.push([`${t('common.label.previousValue')} (${t('features.InvestmentScreening.Review.ReviewBusiness.unit.pieces')})`, ...calcCumulative(overallPrevious)]);
      cumData.push([`${t('common.label.currentValue')} (${t('features.InvestmentScreening.Review.ReviewBusiness.unit.pieces')})`, ...calcCumulative(overallCurrent)]);
      if (activePartNos.length >= 2) {
        for (const pn of activePartNos) {
          const currentPnData = savedProductionByPartNo[pn] ?? currentCapacityByPartNo[pn] ?? planCapacityByPartNo[pn];
          const planPnData = planCapacityByPartNo[pn];
          const prevPnData = prevSavedProductionByPartNo[pn] ?? prevCapacityByPartNo[pn];
          if (!currentPnData && !planPnData && !prevPnData) continue;
          cumData.push([]);
          cumData.push([`${t('common.label.partNo')} ${displayPartNoMap[pn] || pn}`]);
          cumData.push(['', ...monthLabels]);
          cumData.push([`${t('common.label.originalPlan')} (${t('features.InvestmentScreening.Review.ReviewBusiness.unit.pieces')})`, ...calcCumulative(planPnData ?? [])]);
          cumData.push([`${t('common.label.previousValue')} (${t('features.InvestmentScreening.Review.ReviewBusiness.unit.pieces')})`, ...calcCumulative(prevPnData ?? [])]);
          cumData.push([`${t('common.label.currentValue')} (${t('features.InvestmentScreening.Review.ReviewBusiness.unit.pieces')})`, ...calcCumulative(currentPnData ?? [])]);
        }
      }
      const cumWs = XLSX.utils.aoa_to_sheet(cumData);
      cumWs['!cols'] = getAutoColWidths(cumData);
      XLSX.utils.book_append_sheet(workbook, cumWs, t('features.InvestmentScreening.Review.ReviewBusiness.section.cumulativeProduction'));
    }

    // ⑤ 設備稼働率シート
    if (operatingRateRealData) {
      const data: (string | number)[][] = [
        ['', ...monthLabels],
        [`${t('common.label.originalPlan')} (${t('features.InvestmentScreening.Review.ReviewBusiness.unit.percent')})`, ...operatingRateRealData.originalPlan],
        [`${t('common.label.previousValue')} (${t('features.InvestmentScreening.Review.ReviewBusiness.unit.percent')})`, ...operatingRateRealData.previous],
        [`${t('common.label.currentValue')} (${t('features.InvestmentScreening.Review.ReviewBusiness.unit.percent')})`, ...operatingRateRealData.current],
      ];
      const ws = XLSX.utils.aoa_to_sheet(data);
      ws['!cols'] = getAutoColWidths(data);
      XLSX.utils.book_append_sheet(workbook, ws, t('features.InvestmentScreening.Review.ReviewBusiness.section.operatingRate'));
    }

    const timestamp = dateStr.replace(/[-: ]/g, '').slice(0, 14);
    XLSX.writeFile(workbook, `${t('features.InvestmentScreening.Review.ReviewBusiness.excel.fileNamePrefix')}_${projectId}_${timestamp}.xlsx`);
  };

  // ========== 確認ボタン ==========
  const handleModalOpen: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    event.currentTarget.blur();
    if (!reviewDate) {
      toast({ title: TITLE, description: t("common.message.reviewDateNotSet"), status: 'warning', duration: 5000, isClosable: true });
      return;
    }
    if (!hasReviewData) {
      toast({ title: TITLE, description: t("features.InvestmentScreening.Review.common.graphNotDisplayedBusiness"), status: 'warning', duration: 5000, isClosable: true });
      return;
    }
    if (Object.keys(planCapacityByPartNo).length > 0 && Object.keys(savedProductionByPartNo).length === 0) {
      toast({ title: TITLE, description: t("features.InvestmentScreening.Review.common.productionActualsNotSaved"), status: 'warning', duration: 5000, isClosable: true });
      return;
    }
    const { name } = event.target as HTMLButtonElement;
    setConfirmSource(name);
    onOpenConfirmModal();
  };

  // ========== businessChartData 構築 ==========
  // 申請書画面（ReportAndDraft）の振り返りモードで再描画するためのデータを Chart.js 形式に変換して保存する。
  // - 通貨依存グラフ（営業利益・製造原価）: ローカル通貨1本で保存。申請書側で convertChartDatasets により表示通貨へ動的変換（Simulationと同パターン）
  // - 通貨非依存グラフ（生産数量・設備稼働率）: previousMode / originalPlanMode の2パターン
  // - テーブル系（回収期間・人員数・歩留まり・CT・生産実績）: 生データ3値（plan/review/prev）で保存
  // PartNo をキーとする Record を、画面の PartNo SELECT（activePartNos）順に並べ直す共通ヘルパー。
  // - SELECT に存在する PartNo を SELECT 順で先頭に並べ、その後に SELECT に無い PartNo を元の順序で続ける。
  // - 保存先（申請書画面の歩留・CT用 PartNo SELECT 等）が Object.keys 列挙順を使うため、
  //   このヘルパーで「挿入順=SELECT順」のオブジェクトを生成して保存する。
  /**
   * PartNo をキーとする Record を PartNo SELECT の表示順に並べ直す
   * @param record PartNo をキーとする任意の Record
   * @param orderPartNos 画面の PartNo SELECT に表示されている順序の配列
   * @returns SELECT 順に並べ直した新しい Record
   */
  const reorderRecordByPartNoSelect = <V,>(record: Record<string, V>, orderPartNos: string[]): Record<string, V> => {
    const keys = Object.keys(record);
    const orderedFromSelect = orderPartNos.filter((pn) => keys.includes(pn));
    const orderedRest = keys.filter((pn) => !orderPartNos.includes(pn));
    const result: Record<string, V> = {};
    for (const pn of [...orderedFromSelect, ...orderedRest]) {
      result[pn] = record[pn];
    }
    return result;
  };

  /**
   * 申請書画面（ReportAndDraft）の振り返りセクションで使用するグラフ・テーブルデータを構築して返す。
   * 実画面の表示範囲（periodStart〜periodEnd）でスライスし、申請書側が同じ境界で描画できるよう
   * monthLabels / boundary / prevBoundary をローカル化した値で返す。
   * @returns 申請書側に保存する事業視点グラフデータオブジェクト
   */
  const buildBusinessChartData = () => {
    const labels = monthLabels.length > 0 ? monthLabels : [];
    const fullLen = labels.length;
    // 実画面の表示範囲（periodStart〜periodEnd）に合わせて保存することで、
    // 申請書画面のチャートの見え方が事業視点画面と一致するようにする。
    // periodStart/periodEnd は実画面初期化時に「前回振り返り境界〜次回振り返り境界」へ設定されており、
    // ユーザーが操作した場合はその値が使われる（C-2-5'-5-j 修正方針 B-1）。
    const sliceStart = Math.max(0, Math.min(periodStart, fullLen));
    const sliceEnd = Math.max(sliceStart, Math.min(periodEnd, fullLen));
    const slicedLabels = labels.slice(sliceStart, sliceEnd);
    // ローカル化した境界（datasets の i < localBoundary / i >= localBoundary - 1 判定に使う）。
    // 申請書側に渡す `boundary` プロパティもこの値（実画面表示範囲基準）にすることで、
    // 申請書画面の renderChart / buildBusinessChartOptions / fillPlugin が同じ境界で描画できる。
    const localBoundary = activeBoundary - sliceStart;
    const localPrevBoundary = activePrevBoundary - sliceStart;

    // 通貨依存セクション（営業利益・製造原価）の Chart.js データ生成
    type CurrencySectionData = { originalPlan: number[]; previous: number[]; current: number[] } | null;

    // 営業利益用 Line グラフ（実画面 ReviewBusiness 行2441-2463 と同じ 3 datasets 構成）
    //   datasets[0]: 比較値（前回値 or 申請時計画）— 単色実線
    //   datasets[1]: 実績（境界前のみ・他は null）— 濃いオレンジ
    //   datasets[2]: 今回見通し（境界後のみ・他は null）— 薄いオレンジ破線
    // boundaryIdx をルートに保持することで申請書側で「振り返り」縦線 annotation を再構築可能。
    /**
     * 営業利益の Line グラフデータを構築する（3 datasets 構成）
     * @param data 計画値・前回値・今回値を含む通貨依存データ
     * @param mode 比較モード（"previous": 前回値との比較 / "originalPlan": 申請時計画との比較）
     * @returns Chart.js 用の labels + datasets オブジェクト
     */
    const buildOperatingProfitChartData = (data: NonNullable<CurrencySectionData>, mode: CompareMode) => {
      const compareWith = (mode === "previous" ? data.previous : data.originalPlan).slice(sliceStart, sliceEnd);
      const compareLabel = mode === "previous"
        ? (prevReviewDateState ? t("common.label.previousValue") : t("common.label.originalPlanValue"))
        : t("common.label.originalPlanValue");
      const actuals = data.current.slice(sliceStart, sliceEnd);
      const boundary = localBoundary;
      return {
        labels: slicedLabels,
        datasets: [
          {
            label: compareLabel,
            data: compareWith,
            borderColor: mode === "previous" ? "rgba(66, 153, 225, 1)" : "rgba(72, 187, 120, 1)",
            borderWidth: 2.5, pointRadius: 0, pointHoverRadius: 4, tension: 0.4, fill: false, order: 1,
          },
          {
            label: t("common.label.actuals"),
            data: actuals.map((v, i) => (i < boundary ? v : null)),
            borderColor: "rgba(237, 137, 54, 1)", borderWidth: 3, pointRadius: 1.5, pointHoverRadius: 5,
            tension: 0.4, fill: false, spanGaps: false, order: 0,
          },
          {
            label: currentOutlookLabel,
            data: actuals.map((v, i) => (i >= boundary - 1 ? v : null)),
            borderColor: "rgba(237, 137, 54, 0.7)", borderWidth: 2.5, borderDash: [8, 4],
            pointRadius: 0, pointHoverRadius: 4, tension: 0.4, fill: false, spanGaps: true, order: 0,
          },
        ],
      };
    };

    // 生産数量（lifecycle / 累積）用 Line グラフ
    //   実画面 ReviewBusiness 行2880-2916 と同じ 3 datasets 構造
    //   compareValues / currentValues は呼び出し元で算出済み（lifecycle と累積で使う元値が異なる）
    //   ※ compareValues/currentValues は **全期間長** の配列で受け取り、ここで slice する
    /**
     * 生産数量（ライフサイクル / 累積）の Line グラフデータを構築する（3 datasets 構成）
     * @param compareValues 全期間長の比較値配列（計画値 or 前回値）
     * @param currentValues 全期間長の今回値配列
     * @param mode 比較モード（"previous": 前回値との比較 / "originalPlan": 申請時計画との比較）
     * @returns Chart.js 用の labels + datasets オブジェクト（sliceStart〜sliceEnd でトリム済み）
     */
    const buildProductionLineChartData = (
      compareValues: number[],
      currentValues: number[],
      mode: CompareMode,
    ) => {
      const compareLabel = mode === "previous"
        ? (prevReviewDateState ? t("common.label.previousValue") : t("common.label.originalPlanValue"))
        : t("common.label.originalPlanValue");
      const boundary = localBoundary;
      const slicedCompare = compareValues.slice(sliceStart, sliceEnd);
      const slicedCurrent = currentValues.slice(sliceStart, sliceEnd);
      return {
        labels: slicedLabels,
        datasets: [
          {
            label: compareLabel,
            data: slicedCompare,
            borderColor: mode === "previous" ? "rgba(66, 153, 225, 1)" : "rgba(72, 187, 120, 1)",
            borderWidth: 2.5, pointRadius: 0, tension: 0.3, fill: false, order: 1,
          },
          {
            label: t("common.label.actuals"),
            data: slicedCurrent.map((v, i) => (i < boundary ? v : null)),
            borderColor: "rgba(237, 137, 54, 1)", borderWidth: 3, pointRadius: 1,
            tension: 0.3, fill: false, spanGaps: false, order: 0,
          },
          {
            label: currentOutlookLabel,
            data: slicedCurrent.map((v, i) => (i >= boundary - 1 ? v : null)),
            borderColor: "rgba(237, 137, 54, 0.7)", borderWidth: 2.5, borderDash: [8, 4],
            pointRadius: 0, tension: 0.3, fill: false, spanGaps: true, order: 0,
          },
        ],
      };
    };

    const buildCurrencySection = (
      id: string,
      title: string,
      unit: string,
      summaryType: "total" | "average",
      data: CurrencySectionData,
      dataJPY: CurrencySectionData, // summaryValuesでのみ参照
      mode: CompareMode,
    ) => {
      if (!data) return null;
      const compareWith = mode === "previous" ? data.previous : data.originalPlan;
      const compareLabel = mode === "previous" ? t("common.label.previousValue") : t("common.label.originalPlan");
      // 営業利益は実画面と同じ 3 datasets の Line 構造で保存（申請書側はそのまま渡すだけで描画可能）
      // 製造原価などの棒グラフは createChartData 側で slice(startIdx, endIdx) と
      // ローカル境界化（boundaryIdx - startIdx）を行うので、ここでは全期間データを渡す。
      const chartData = id === "operatingProfit"
        ? buildOperatingProfitChartData(data, mode)
        : createChartData(
          compareWith,
          data.current,
          compareLabel,
          mode,
          currentValueLabel,
          sliceStart,
          sliceEnd,
          labels,
          activeBoundary,
        );
      const isAvg = summaryType === "average";
      const summaryValues = {
        [localCurrencyName]: {
          actualVsPlan: isAvg
            ? calcActualVsPlanAvg(data.originalPlan, data.current, activeBoundary)
            : calcActualVsPlan(data.originalPlan, data.current, activeBoundary),
          forecastAccuracy: isAvg
            ? calcForecastAccuracyAvg(data.previous, data.current, activePrevBoundary, activeBoundary)
            : calcForecastAccuracy(data.previous, data.current, activePrevBoundary, activeBoundary),
          finalOutlook: isAvg
            ? calcFinalOutlookAvg(data.originalPlan, data.current)
            : calcFinalOutlook(data.originalPlan, data.current),
        },
        [CurrencyNameJPY]: {
          actualVsPlan: isAvg
            ? calcActualVsPlanAvg(dataJPY?.originalPlan, dataJPY?.current, activeBoundary)
            : calcActualVsPlan(dataJPY?.originalPlan, dataJPY?.current, activeBoundary),
          forecastAccuracy: isAvg
            ? calcForecastAccuracyAvg(dataJPY?.previous, dataJPY?.current, activePrevBoundary, activeBoundary)
            : calcForecastAccuracy(dataJPY?.previous, dataJPY?.current, activePrevBoundary, activeBoundary),
          finalOutlook: isAvg
            ? calcFinalOutlookAvg(dataJPY?.originalPlan, dataJPY?.current)
            : calcFinalOutlook(dataJPY?.originalPlan, dataJPY?.current),
        }
      };
      return { id, title, unit, summaryType, chartData, summaryValues };
    };

    const buildSectionsForMode = (mode: CompareMode) => {
      const unit = localCurrencyName || CurrencyNameJPY;
      return [
        buildCurrencySection("operatingProfit", t("features.InvestmentScreening.Review.ReviewBusiness.operatingProfit"), unit, "total", operatingProfitData, operatingProfitDataJPY, mode),
        buildCurrencySection("manufacturingCost", t("features.InvestmentScreening.Review.ReviewBusiness.section.manufacturingCost"), unit, "total", manufacturingCostData, manufacturingCostDataJPY, mode),
      ].filter((s): s is NonNullable<typeof s> => s !== null);
    };

    // 通貨非依存グラフ（生産数量・設備稼働率）
    const buildNonCurrencyChartsForMode = (mode: CompareMode) => {
      const result: Record<string, any> = {};

      // 生産数量（lifecycle + 累積）— 実画面と同じ 3 datasets Line で PartNo 別に保存
      // PartNo 別データ（planCapacityByPartNo / prevCapacityByPartNo / currentCapacityByPartNo）から
      // PartNo ごとに lifecycle + 累積のグラフデータと 3 カードのサマリー値を生成。
      // 保存順は画面の PartNo SELECT（activePartNos）と同じ順序にそろえる。
      // activePartNos に存在しない PartNo は後ろに付け足す（保存データの欠落を防ぐ）。
      const planCapacityKeys = Object.keys(planCapacityByPartNo);
      const orderedFromSelect = activePartNos.filter((pn) => planCapacityKeys.includes(pn));
      const orderedRest = planCapacityKeys.filter((pn) => !activePartNos.includes(pn));
      const partNosForCapacity = [...orderedFromSelect, ...orderedRest];
      if (partNosForCapacity.length > 0) {
        result.productionCapacityByPartNo = partNosForCapacity.map((partNo) => {
          const planArr = planCapacityByPartNo[partNo] ?? [];
          const prevArr = prevCapacityByPartNo[partNo] ?? planArr;
          // current 配列は実画面の currentForChart（行2868-2900）と同じロジックで再構築する。
          //   productionActuals[`${partNo}-${i}`] 優先 → currentCapacityByPartNo → planCapacityByPartNo のフォールバック。
          // これをやらないと、量産開始前の月に 0 が並んだり、量産終了後の見通しが 0 に落ちる差異が生じる。
          const bomArr = currentCapacityByPartNo[partNo] ?? planArr;
          const currentArr = Array.from({ length: fullLen }, (_, i) => {
            const actVal = productionActuals[`${partNo}-${i}`];
            return actVal !== undefined ? actVal : (bomArr[i] ?? 0);
          });
          const data: { originalPlan: number[]; previous: number[]; current: number[] } = {
            originalPlan: planArr,
            previous: prevArr,
            current: currentArr,
          };
          const compareWith = mode === "previous" ? data.previous : data.originalPlan;
          // lifecycle 用は buildProductionLineChartData が内部で slice(sliceStart, sliceEnd) する
          const chartData = buildProductionLineChartData(compareWith, data.current, mode);
          // 累積は実画面（行2917-2918）と同じく「スライス後に累積化」する
          const cumulativeCompareWith = calcCumulative(compareWith.slice(sliceStart, sliceEnd));
          const cumulativeCurrent = calcCumulative(data.current.slice(sliceStart, sliceEnd));
          // 累積は既にスライス済みなので、buildProductionLineChartData が再度スライスしないように
          // 全期間長と同じ位置に置く必要はない（labels/boundary が slicedLabels/localBoundary なので、
          // ここでは sliceStart=0, sliceEnd=length と同じ動作になるよう、ヘルパーへ渡す値を整形する）。
          // → 簡単化のため累積はインラインで datasets を組み立てる
          const cumulativeChartData = (() => {
            const compareLabel = mode === "previous"
              ? (prevReviewDateState ? t("common.label.previousValue") : t("common.label.originalPlanValue"))
              : t("common.label.originalPlanValue");
            const boundary = localBoundary;
            return {
              labels: slicedLabels,
              datasets: [
                {
                  label: compareLabel,
                  data: cumulativeCompareWith,
                  borderColor: mode === "previous" ? "rgba(66, 153, 225, 1)" : "rgba(72, 187, 120, 1)",
                  borderWidth: 2.5, pointRadius: 0, tension: 0.3, fill: false, order: 1,
                },
                {
                  label: t("common.label.actuals"),
                  data: cumulativeCurrent.map((v, i) => (i < boundary ? v : null)),
                  borderColor: "rgba(237, 137, 54, 1)", borderWidth: 3, pointRadius: 1,
                  tension: 0.3, fill: false, spanGaps: false, order: 0,
                },
                {
                  label: currentOutlookLabel,
                  data: cumulativeCurrent.map((v, i) => (i >= boundary - 1 ? v : null)),
                  borderColor: "rgba(237, 137, 54, 0.7)", borderWidth: 2.5, borderDash: [8, 4],
                  pointRadius: 0, tension: 0.3, fill: false, spanGaps: true, order: 0,
                },
              ],
            };
          })();
          // summaryValues は全期間配列＋全期間 boundary で計算（実画面の summary も全期間ベース）
          const summaryValues = {
            actualVsPlan: calcActualVsPlan(data.originalPlan, data.current, activeBoundary),
            forecastAccuracy: calcForecastAccuracy(data.previous, data.current, activePrevBoundary, activeBoundary),
            finalOutlook: calcFinalOutlook(data.originalPlan, data.current),
          };
          return {
            partNo,
            displayPartNo: displayPartNoMap[partNo] ?? null,
            unit: t("features.InvestmentScreening.Review.ReviewBusiness.unit.perMonth"),
            chartData,
            cumulativeChartData,
            summaryValues,
          };
        });
      }

      // 設備稼働率
      if (operatingRateRealData) {
        const compareWith = mode === "previous" ? operatingRateRealData.previous : operatingRateRealData.originalPlan;
        const compareLabel = mode === "previous" ? t("common.label.previousValue") : t("common.label.originalPlan");
        const chartData = createChartData(
          compareWith,
          operatingRateRealData.current,
          compareLabel,
          mode,
          currentValueLabel,
          sliceStart,
          sliceEnd,
          labels,
          activeBoundary,
        );
        result.operatingRate = {
          title: t("features.InvestmentScreening.Review.ReviewBusiness.section.operatingRate"),
          unit: t("features.InvestmentScreening.Review.ReviewBusiness.unit.percent"),
          chartData,
        };
      }

      return result;
    };

    return {
      // 申請書画面側の renderChart は monthLabels/boundary をそのまま使うので、
      // 実画面の表示範囲（sliceStart〜sliceEnd）にローカル化した値を保存する。
      monthLabels: slicedLabels,
      boundary: localBoundary,
      prevBoundary: localPrevBoundary,
      localCurrencyName,
      previousMode: { sections: buildSectionsForMode("previous") },
      originalPlanMode: { sections: buildSectionsForMode("originalPlan") },
      nonCurrencyCharts: {
        previousMode: buildNonCurrencyChartsForMode("previous"),
        originalPlanMode: buildNonCurrencyChartsForMode("originalPlan"),
      },
      tables: {
        payback: { plan: paybackPlan, review: paybackReview, prev: paybackPrev },
        headcount: { plan: headcountPlan, review: headcountReview, prev: headcountPrev },
        yield: { plan: yieldPlan, review: yieldReview, prev: yieldPrev },
        cycleTime: { plan: cycleTimePlan, review: cycleTimeReview, prev: cycleTimePrev },
        // PartNo 別オブジェクトは画面の PartNo SELECT（activePartNos）順に並べ直して保存。
        // activePartNos に無い PartNo は後ろに付け足す（保存データの欠落を防ぐ）。
        yieldByPartNo: reorderRecordByPartNoSelect(yieldAtBoundaryByPartNo, activePartNos),
        yieldStats: { plan: yieldStatsPlan, review: yieldStatsReview, prev: yieldStatsPrev },
        cycleTimeByPartNo: reorderRecordByPartNoSelect(ctAtBoundaryByPartNo, activePartNos),
        cycleTimeStats: { plan: ctStatsPlan, review: ctStatsReview, prev: ctStatsPrev },
        // PartNoごとのdisplayPartNo解決マップ（No.435対応、PdfReviewContent.tsxでの表示変換用）
        displayPartNoMap: displayPartNoMap,
        // 生産実績テーブルも実画面と同じ表示範囲（sliceStart〜sliceEnd）で保存する。
        // monthLabels もスライス済みなので、申請書側でラベルと整合する範囲だけ表示される。
        productionActuals: Object.fromEntries(
          Object.entries(savedProductionByPartNo).map(([pn, arr]) => [pn, arr.slice(sliceStart, sliceEnd)])
        ),
        prevProductionActuals: Object.fromEntries(
          Object.entries(prevSavedProductionByPartNo).map(([pn, arr]) => [pn, arr.slice(sliceStart, sliceEnd)])
        ),
      },
      textValues,
      comments,
    };
  };

  const handleConfirm = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.blur();
    setIsSaving(true);
    try {
      const businessReview = JSON.stringify({ textValues, comments });
      const businessChartData = JSON.stringify(buildBusinessChartData());
      const saveResp = await (WebAPI as any).request({
        query: `
          mutation putReview($input: PutReviewInput!) {
            putReview(input: $input) { status message }
          }
        `,
        variables: { input: { projectId, reviewDate, businessReview, businessChartData } }
      });
      const saveResult = saveResp?.data?.putReview;
      if (saveResult?.status !== 'success') {
        toast({ title: TITLE, description: saveResult?.message ? t(saveResult.message) : t("common.message.saveFailed"), status: 'error', duration: 5000, isClosable: true });
        return;
      }
      await updateConfirm(WebAPI, projectId, WorkflowId.ReviewBusiness, employeeId, confirmSource, "true", reviewDate);
      const data = await getConfirmor(WebAPI, projectId, reviewDate);
      setConfirmor(data);
      toast({ title: TITLE, description: t("common.message.inputSaved"), status: 'success', duration: 3000, isClosable: true });
      onCloseConfirmModal();
    } catch { /* スルー */ } finally {
      setIsSaving(false);
    }
  };

  const handleSaveProductionActuals = async () => {
    if (!projectId || !reviewDate) return;
    try {
      const totalLen = baseLabelsForSpreadSheet.length;
      const newSaved: Record<string, number[]> = {};
      for (const pn of activePartNos) {
        const arr = new Array(totalLen).fill(0);
        for (let i = 0; i < totalLen; i++) {
          arr[i] = productionActuals[`${pn}-${i}`] ?? 0;
        }
        newSaved[pn] = arr;
        await (WebAPI as any).request({
          query: `
            mutation putProductionActualsForBusiness($input: PutProductionActualsInput!) {
              putProductionActuals(input: $input) { status message }
            }
          `,
          variables: {
            input: {
              projectId,
              reviewDate,
              partNo: pn,
              productionActuals: JSON.stringify(arr),
            }
          }
        });
      }
      setSavedProductionByPartNo(newSaved);
      toast({ title: TITLE, description: t("features.InvestmentScreening.Review.common.productionActualsSaved"), status: 'success', duration: 3000, isClosable: true });
    } catch (error) {
      console.error("handleSaveProductionActuals error:", error);
      toast({ title: TITLE, description: t("features.InvestmentScreening.Review.common.productionActualsSaveFailed"), status: 'error', duration: 5000, isClosable: true });
    }
  };

  // 実データ用のラベル（取得済みなら実データ、まだなら空配列）
  const activeLabels = monthLabels.length > 0 ? monthLabels : [];
  // 期間セレクタ用マイルストーン（実データのみ使用。取得前は空配列）
  const activeMilestones = milestones;
  // ①②⑤の境界インデックス
  const activeBoundary = reviewBoundaryIdx;
  const activePrevBoundary = prevReviewIdx;

  // ⑥ 歩留・CT セルに表示する期間ラベル（内部保持は"YYYY/M"だが表示はロケールに合わせて整形する）
  const yieldCtCurrPeriodText = productionStartLabelState && todayEndLabelState
    ? t("features.InvestmentScreening.Review.ReviewBusiness.averageWithRange", { from: formatYearMonthByLocale(productionStartLabelState), to: formatYearMonthByLocale(todayEndLabelState) })
    : "";
  const yieldCtPrevPeriodText = productionStartLabelState
    ? (compareMode === "previous" && prevEndLabelState
      ? t("features.InvestmentScreening.Review.ReviewBusiness.averageWithRange", { from: formatYearMonthByLocale(productionStartLabelState), to: formatYearMonthByLocale(prevEndLabelState) })
      : t("features.InvestmentScreening.Review.ReviewBusiness.averageWithRange", { from: formatYearMonthByLocale(productionStartLabelState), to: formatYearMonthByLocale(todayEndLabelState) }))
    : "";

  return (
    <Box
      position="fixed"
      top="10vh"
      ml="2vw"
      mr="2vw"
      left="250px"
      width="calc(96vw - 250px)"
      height="85vh"
      p={6}
      overflowY="auto"
      bg="white"
      textAlign="left"
    >
      {/* 操作パネル（sticky固定） */}
      <Box
        position="sticky"
        top={-6}
        zIndex={10}
        bg="white"
        pt={6}
        pb={3}
        mb={4}
        borderBottom="1px solid"
        borderColor="gray.200"
      >
        <HStack spacing={6} align="center" wrap="wrap">
          <ButtonGroup size="sm" isAttached>
            <Button
              colorScheme={compareMode === "previous" ? "blue" : "gray"}
              variant={compareMode === "previous" ? "solid" : "outline"}
              onClick={() => setCompareMode("previous")}
            >
              {t("common.button.comparePreviousValue")}
            </Button>
            <Button
              colorScheme={compareMode === "originalPlan" ? "green" : "gray"}
              variant={compareMode === "originalPlan" ? "solid" : "outline"}
              onClick={() => setCompareMode("originalPlan")}
            >
              {t("common.button.compareOriginalPlan")}
            </Button>
          </ButtonGroup>
          <HStack spacing={2} align="center">
            <Text fontSize="xs" color="gray.500" flexShrink={0}>{t("common.label.displayPeriod")}</Text>
            <Select
              size="sm"
              w="260px"
              value={periodStart}
              onChange={(e) => {
                const newStart = Number(e.target.value);
                setPeriodStart(newStart);
                if (newStart >= periodEnd) {
                  const nextMilestone = activeMilestones.find(m => m.idx > newStart);
                  setPeriodEnd(nextMilestone ? nextMilestone.idx : activeLabels.length);
                }
              }}
            >
              {activeMilestones
                .filter(m => m.idx < periodEnd)
                .map((m) => (
                  <option key={m.idx} value={m.idx}>{m.label}</option>
                ))}
            </Select>
            <Text fontSize="sm" color="gray.500">〜</Text>
            <Select
              size="sm"
              w="260px"
              value={periodEnd}
              onChange={(e) => {
                const newEnd = Number(e.target.value);
                setPeriodEnd(newEnd);
                if (newEnd <= periodStart) {
                  const prevMilestone = [...activeMilestones].reverse().find(m => m.idx < newEnd);
                  setPeriodStart(prevMilestone ? prevMilestone.idx : 0);
                }
              }}
            >
              {activeMilestones
                .filter(m => m.idx > periodStart)
                .map((m) => (
                  <option key={m.idx} value={m.idx}>{m.label}</option>
                ))}
            </Select>
            <Button
              size="xs"
              variant="outline"
              colorScheme="gray"
              onClick={() => { setPeriodStart(0); setPeriodEnd(activeLabels.length); }}
            >
              {t("common.button.allPeriods")}
            </Button>
          </HStack>
          <HStack spacing={2} align="center">
            <Text fontSize="xs" color="gray.500" flexShrink={0}>{t("common.label.displayCurrency")}</Text>
            <HelpIcon size="14" placement="top">
              <Text>{t("features.InvestmentScreening.Review.common.displayCurrencyRateHelp")}</Text>
            </HelpIcon>
            <Select
              size="sm"
              w="120px"
              value={currencyName}
              onChange={(e) => setCurrencyName(e.target.value)}
            >
              <option value={localCurrencyName}>{localCurrencyName}</option>
              {localCurrencyName !== CurrencyNameJPY && (
                <option value={CurrencyNameJPY}>{CurrencyNameJPY}</option>
              )}
            </Select>
          </HStack>
        </HStack>
      </Box>

      {/* ① 営業利益 曲線チャート */}
      {(() => {
        if (isLoading) return null;
        // 通貨選択に応じてJPY版またはローカル通貨版を使用
        const emptyData = { originalPlan: [] as number[], previous: [] as number[], current: [] as number[] };
        const data = currencyName === CurrencyNameJPY
          ? (operatingProfitDataJPY ?? operatingProfitData ?? emptyData)
          : (operatingProfitData ?? emptyData);
        const labels = activeLabels;
        const compare = compareMode === "previous" ? data.previous : data.originalPlan;
        const compareLabel = compareMode === "previous"
          ? (prevReviewDateState ? t("common.label.previousValue") : t("common.label.originalPlanValue"))
          : t("common.label.originalPlanValue");
        const sliceLabels = labels.slice(periodStart, periodEnd);
        const sliceCompare = compare.slice(periodStart, periodEnd);
        const sliceActual = data.current.slice(periodStart, periodEnd);
        const localBoundary = activeBoundary - periodStart;

        // データ有無チェック: 振り返りデータが登録されているか
        const hasCurrentData = operatingProfitData
          ? hasReviewData
          : data.current.some(v => v !== 0);

        const s1 = calcActualVsPlan(data.originalPlan, data.current, activeBoundary);
        const s2 = calcForecastAccuracy(data.previous, data.current, activePrevBoundary, activeBoundary);
        const s3 = calcFinalOutlook(data.originalPlan, data.current);

        const allValues = [
          ...data.originalPlan.slice(periodStart, periodEnd),
          ...data.previous.slice(periodStart, periodEnd),
          ...data.current.slice(periodStart, periodEnd),
        ];
        const unifiedMax = Math.ceil(Math.max(...allValues, 0) * 1.1);
        const unifiedMin = Math.min(...allValues, 0);
        const suggestedMin = unifiedMin < 0 ? Math.floor(unifiedMin * 1.1) : 0;

        const profitChartData = {
          labels: sliceLabels.map(formatYearMonthByLocale),
          datasets: [
            {
              label: compareLabel,
              data: sliceCompare,
              borderColor: compareMode === "previous" ? "rgba(66, 153, 225, 1)" : "rgba(72, 187, 120, 1)",
              borderWidth: 2.5, pointRadius: 0, pointHoverRadius: 4, tension: 0.4, fill: false, order: 1,
            },
            {
              label: t("common.label.actuals"),
              data: sliceActual.map((v, i) => (i < localBoundary ? v : null)),
              borderColor: "rgba(237, 137, 54, 1)", borderWidth: 3, pointRadius: 1.5, pointHoverRadius: 5,
              tension: 0.4, fill: false, spanGaps: false, order: 0,
            },
            {
              label: currentOutlookLabel,
              data: sliceActual.map((v, i) => (i >= localBoundary - 1 ? v : null)),
              borderColor: "rgba(237, 137, 54, 0.7)", borderWidth: 2.5, borderDash: [8, 4],
              pointRadius: 0, pointHoverRadius: 4, tension: 0.4, fill: false, spanGaps: true, order: 0,
            },
          ],
        };

        const boundaryAnnotation = localBoundary >= 0 && localBoundary < sliceLabels.length ? {
          reviewBoundary: {
            type: "line" as const, xMin: localBoundary - 1, xMax: localBoundary - 1,
            borderColor: "rgba(229, 62, 62, 0.8)", borderWidth: 2, borderDash: [6, 4],
            label: { display: true, content: reviewBoundaryLabel, position: "start" as const, backgroundColor: "rgba(229, 62, 62, 0.8)", color: "white", font: { size: 10 } },
          },
        } : {};

        const profitFillPlugin = {
          id: "profitFill",
          beforeDraw(chart: any) {
            // 期間切替時にcanvasの残像をクリア
            const { ctx, chartArea } = chart;
            if (chartArea) {
              ctx.clearRect(chartArea.left, chartArea.top, chartArea.right - chartArea.left, chartArea.bottom - chartArea.top);
            }
          },
          beforeDatasetsDraw(chart: any) {
            const { ctx, chartArea } = chart;
            if (!chartArea) return;
            const compareMeta = chart.getDatasetMeta(0)?.data;
            if (!compareMeta || compareMeta.length < 2) return;
            const m1 = chart.getDatasetMeta(1)?.data;
            const m2 = chart.getDatasetMeta(2)?.data;
            const getActualPt = (idx: number) => (idx < localBoundary ? m1?.[idx] ?? m2?.[idx] : m2?.[idx] ?? m1?.[idx]);
            // ベジェ制御点ヘルパー（未定義時はその点自身にフォールバック）
            const ox = (pt: any) => pt.cp2x ?? pt.x; const oy = (pt: any) => pt.cp2y ?? pt.y;
            const ix = (pt: any) => pt.cp1x ?? pt.x; const iy = (pt: any) => pt.cp1y ?? pt.y;
            ctx.save();
            ctx.rect(chartArea.left, chartArea.top, chartArea.right - chartArea.left, chartArea.bottom - chartArea.top);
            ctx.clip();
            for (let i = 0; i < sliceCompare.length - 1; i++) {
              const c0 = compareMeta[i], c1 = compareMeta[i + 1];
              const a0 = getActualPt(i), a1 = getActualPt(i + 1);
              if (!c0 || !c1 || !a0 || !a1) continue;
              if (sliceActual[i] == null || sliceActual[i + 1] == null) continue;
              const diff1 = sliceActual[i] - sliceCompare[i], diff2 = sliceActual[i + 1] - sliceCompare[i + 1];
              const ai = i + periodStart;
              if (compareMode === "previous") {
                const rng = [ai - 1, ai, ai + 1, ai + 2].filter(k => k >= 0 && k < data.current.length);
                if (rng.every(k => data.previous[k] === data.current[k])) continue;
              }
              const eff1 = diff1 === 0 ? diff2 : diff1;
              ctx.save();
              if (eff1 * diff2 < 0) {
                // 符号反転あり: 交差点を線形近似して三角形で塗る
                const t = eff1 / (eff1 - diff2);
                const xC = c0.x + (c1.x - c0.x) * t;
                const yC = (c0.y + (c1.y - c0.y) * t + a0.y + (a1.y - a0.y) * t) / 2;
                ctx.beginPath(); ctx.moveTo(c0.x, c0.y); ctx.lineTo(xC, yC); ctx.lineTo(a0.x, a0.y); ctx.closePath();
                ctx.fillStyle = eff1 >= 0 ? "rgba(72, 187, 120, 0.15)" : "rgba(229, 62, 62, 0.15)"; ctx.fill();
                ctx.beginPath(); ctx.moveTo(xC, yC); ctx.lineTo(c1.x, c1.y); ctx.lineTo(a1.x, a1.y); ctx.closePath();
                ctx.fillStyle = diff2 >= 0 ? "rgba(72, 187, 120, 0.15)" : "rgba(229, 62, 62, 0.15)"; ctx.fill();
              } else {
                // 符号一致: ベジェ曲線に沿って塗る
                ctx.beginPath();
                ctx.moveTo(c0.x, c0.y);
                ctx.bezierCurveTo(ox(c0), oy(c0), ix(c1), iy(c1), c1.x, c1.y);
                ctx.lineTo(a1.x, a1.y);
                ctx.bezierCurveTo(ix(a1), iy(a1), ox(a0), oy(a0), a0.x, a0.y);
                ctx.closePath();
                ctx.fillStyle = eff1 >= 0 ? "rgba(72, 187, 120, 0.15)" : "rgba(229, 62, 62, 0.15)";
                ctx.fill();
              }
              ctx.restore();
            }
            ctx.restore();
          },
        };

        const fmt = (v: number) => {
          const isJPY = currencyName === CurrencyNameJPY;
          return isJPY ? Math.round(v).toLocaleString() : v.toLocaleString();
        };

        return (
          <Box mb={8} p={4} border="1px" borderColor="gray.200" borderRadius="md">
            <HStack mb={3} align="baseline">
              <Text fontSize="md" fontWeight="bold">{t("features.InvestmentScreening.Review.ReviewBusiness.section.operatingProfitComparison")}</Text>
              <Text fontSize="sm" color="gray.500">unit: {currencyName}</Text>
            </HStack>
            {hasCurrentData ? (
              <>
                <Box mb={4} p={4} bg="gray.50" borderRadius="md">
                  <SimpleGrid columns={3} spacing={3}>
                    <Box p={3} bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                      <Text fontSize="xs" color="gray.500" mb={1}>{t("features.InvestmentScreening.Review.common.summaryActualVsPlan", { from: activeLabels[0] ? formatYearMonthByLocale(activeLabels[0]) : "", to: activeLabels[activeBoundary - 1] ? formatYearMonthByLocale(activeLabels[activeBoundary - 1]) : "" })}</Text>
                      <HStack spacing={2} align="baseline"><Text fontSize="sm" color="gray.500">{t("common.label.plan")} {fmt(s1.planValue)}</Text><Text fontSize="sm" color="gray.400">{t("common.label.arrowTo")}</Text><Text fontSize="sm" fontWeight="bold">{t("common.label.actuals")} {fmt(s1.actualValue)}</Text></HStack>
                      <Text fontSize="lg" fontWeight="bold" mt={1} color={s1.rate < -10 ? "red.600" : s1.rate < 0 ? "orange.500" : "green.600"}>{s1.rate >= 0 ? "+" : ""}{s1.rate.toFixed(1)}%</Text>
                    </Box>
                    <Box p={3} bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                      <Text fontSize="xs" color="gray.500" mb={1}>{t("features.InvestmentScreening.Review.common.summaryAdjustedPlan", { from: activeLabels[0] ? formatYearMonthByLocale(activeLabels[0]) : "", to: activeLabels[activeLabels.length - 1] ? formatYearMonthByLocale(activeLabels[activeLabels.length - 1]) : "" })}</Text>
                      <HStack spacing={2} align="baseline"><Text fontSize="sm" color="gray.500">{t("common.label.plan")} {fmt(s3.planTotal)}</Text><Text fontSize="sm" color="gray.400">{t("common.label.arrowTo")}</Text><Text fontSize="sm" fontWeight="bold">{t("common.label.outlook")} {fmt(s3.outlookTotal)}</Text></HStack>
                      <Text fontSize="lg" fontWeight="bold" mt={1} color={s3.rate < -10 ? "red.600" : s3.rate < 0 ? "orange.500" : "green.600"}>{s3.rate >= 0 ? "+" : ""}{s3.rate.toFixed(1)}%</Text>
                    </Box>
                    <Box p={3} bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                      <Text fontSize="xs" color="gray.500" mb={1}>{t("features.InvestmentScreening.Review.common.summaryForecastAccuracy", { from: activeLabels[activePrevBoundary] ? formatYearMonthByLocale(activeLabels[activePrevBoundary]) : "", to: activeLabels[activeBoundary - 1] ? formatYearMonthByLocale(activeLabels[activeBoundary - 1]) : "" })}</Text>
                      <HStack spacing={2} align="baseline"><Text fontSize="sm" color="gray.500">{t("common.label.previousEstimate")} {fmt(s2.prevEstimate)}</Text><Text fontSize="sm" color="gray.400">{t("common.label.arrowTo")}</Text><Text fontSize="sm" fontWeight="bold">{t("common.label.actuals")} {fmt(s2.currActual)}</Text></HStack>
                      <Text fontSize="lg" fontWeight="bold" mt={1} color={s2.rate < -10 ? "red.600" : s2.rate < 0 ? "orange.500" : "green.600"}>{s2.rate >= 0 ? "+" : ""}{s2.rate.toFixed(1)}%</Text>
                    </Box>
                  </SimpleGrid>
                </Box>
                <Box h="350px" mb={3}>
                  <Line key={`profit-${periodStart}-${periodEnd}-${compareMode}-${currencyName}`} data={profitChartData} options={{
                    responsive: true, maintainAspectRatio: false,
                    interaction: { mode: "index" as const, intersect: false },
                    plugins: {
                      legend: { position: "top" as const, labels: { font: { size: 12 }, usePointStyle: true } },
                      tooltip: {
                        filter: (item: any) => !(item.dataset.label === currentOutlookLabel && item.dataIndex < localBoundary),
                        callbacks: {
                          label: (ctx: any) => {
                            const val = ctx.parsed.y;
                            const isJPY = currencyName === CurrencyNameJPY;
                            const formattedVal = val != null
                              ? (isJPY ? Math.round(val).toLocaleString() : val.toLocaleString())
                              : "-";
                            return `${ctx.dataset.label}: ${formattedVal} ${currencyName}`;
                          }
                        },
                      },
                      annotation: { annotations: boundaryAnnotation }, datalabels: false as any,
                    },
                    scales: {
                      x: { ticks: { maxRotation: 45, autoSkip: true, maxTicksLimit: 12, font: { size: 10 } }, grid: { color: "rgba(0,0,0,0.05)" } },
                      y: { beginAtZero: suggestedMin >= 0, suggestedMin, suggestedMax: unifiedMax, title: { display: true, text: `${t("features.InvestmentScreening.Review.ReviewBusiness.operatingProfit")}（${currencyName}）`, font: { size: 12 } }, ticks: { callback: (v: any) => Number(v).toLocaleString(), maxTicksLimit: 8 }, grid: { color: "rgba(0,0,0,0.08)" } },
                    },
                  }} plugins={[profitFillPlugin]} />
                </Box>
              </>
            ) : (
              <Box p={6} bg="gray.50" borderRadius="md" textAlign="center">
                <Text fontSize="sm" color="gray.500">{t("common.message.reviewDataNotRegistered")}</Text>
                <Text fontSize="xs" color="gray.400" mt={1}>{t("features.InvestmentScreening.Review.common.enterActualValueInProfitLoss")}</Text>
              </Box>
            )}
            <Textarea size="sm" placeholder={t("common.placeholder.diffComment")} rows={2} maxLength={500} mt={3}
              value={comments["operatingProfit"] || ""} onChange={(e) => setComments({ ...comments, operatingProfit: e.target.value })} />
          </Box>
        );
      })()}

      {/* チャート付き数値比較セクション（② 製造原価） */}
      {!isLoading && chartSections.map((section) => {
        // 通貨選択に応じてJPY版またはローカル通貨版を使用（製造原価のみ実データあり）
        const sectionData = section.id === "manufacturingCost"
          ? (currencyName === CurrencyNameJPY
            ? (manufacturingCostDataJPY ?? manufacturingCostData ?? section.data)
            : (manufacturingCostData ?? section.data))
          : section.data;
        const isTotal = section.summaryType === "total";
        const compareWith = compareMode === "previous" ? sectionData.previous : sectionData.originalPlan;
        const compareLabel = compareMode === "previous"
          ? (prevReviewDateState ? t("common.label.previousValue") : t("common.label.originalPlanValue"))
          : t("common.label.originalPlanValue");

        // 3カードサマリ（累計/平均で関数を使い分け）
        const s1 = isTotal
          ? calcActualVsPlan(sectionData.originalPlan, sectionData.current, activeBoundary)
          : calcActualVsPlanAvg(sectionData.originalPlan, sectionData.current, activeBoundary);
        const s2 = isTotal
          ? calcForecastAccuracy(sectionData.previous, sectionData.current, activePrevBoundary, activeBoundary)
          : calcForecastAccuracyAvg(sectionData.previous, sectionData.current, activePrevBoundary, activeBoundary);
        const s3 = isTotal
          ? calcFinalOutlook(sectionData.originalPlan, sectionData.current)
          : calcFinalOutlookAvg(sectionData.originalPlan, sectionData.current);
        const fmt = (v: number) => {
          if (currencyName === CurrencyNameJPY) {
            return Math.round(v).toLocaleString();
          }
          return isTotal ? v.toLocaleString() : v.toFixed(1);
        };
        const hasCurrentData = section.id === "manufacturingCost" && manufacturingCostData
          ? hasReviewData
          : sectionData.current.some(v => v !== 0);

        return (
          <Box key={section.id} mb={8} p={4} border="1px" borderColor="gray.200" borderRadius="md">
            <HStack mb={3} align="baseline">
              <Text fontSize="md" fontWeight="bold">{section.title}</Text>
              {section.unit !== "%" && <Text fontSize="sm" color="gray.500">unit: {currencyName}</Text>}
            </HStack>

            {hasCurrentData ? (
              <><Box mb={4} p={4} bg="gray.50" borderRadius="md">
                <SimpleGrid columns={3} spacing={3}>
                  <Box p={3} bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                    <Text fontSize="xs" color="gray.500" mb={1}>
                      {t("features.InvestmentScreening.Review.common.summaryActualVsPlan", { from: activeLabels[0] ? formatYearMonthByLocale(activeLabels[0]) : "", to: activeLabels[activeBoundary - 1] ? formatYearMonthByLocale(activeLabels[activeBoundary - 1]) : "" })}
                    </Text>
                    <HStack spacing={2} align="baseline">
                      <Text fontSize="sm" color="gray.500">{t("common.label.plan")} {fmt(s1.planValue)}</Text>
                      <Text fontSize="sm" color="gray.400">{t("common.label.arrowTo")}</Text>
                      <Text fontSize="sm" fontWeight="bold">{t("common.label.actuals")} {fmt(s1.actualValue)}</Text>
                    </HStack>
                    <Text fontSize="lg" fontWeight="bold" mt={1} color={rateColor(s1.rate, section.lowerIsBetter)}>
                      {s1.rate >= 0 ? "+" : ""}{s1.rate.toFixed(1)}%
                    </Text>
                  </Box>
                  <Box p={3} bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                    <Text fontSize="xs" color="gray.500" mb={1}>
                      {t("features.InvestmentScreening.Review.common.summaryAdjustedPlan", { from: activeLabels[0] ? formatYearMonthByLocale(activeLabels[0]) : "", to: activeLabels[activeLabels.length - 1] ? formatYearMonthByLocale(activeLabels[activeLabels.length - 1]) : "" })}
                    </Text>
                    <HStack spacing={2} align="baseline">
                      <Text fontSize="sm" color="gray.500">{t("common.label.plan")} {fmt(s3.planTotal)}</Text>
                      <Text fontSize="sm" color="gray.400">{t("common.label.arrowTo")}</Text>
                      <Text fontSize="sm" fontWeight="bold">{t("common.label.outlook")} {fmt(s3.outlookTotal)}</Text>
                    </HStack>
                    <Text fontSize="lg" fontWeight="bold" mt={1} color={rateColor(s3.rate, section.lowerIsBetter)}>
                      {s3.rate >= 0 ? "+" : ""}{s3.rate.toFixed(1)}%
                    </Text>
                  </Box>
                  <Box p={3} bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                    <Text fontSize="xs" color="gray.500" mb={1}>
                      {t("features.InvestmentScreening.Review.common.summaryForecastAccuracy", { from: activeLabels[activePrevBoundary] ? formatYearMonthByLocale(activeLabels[activePrevBoundary]) : "", to: activeLabels[activeBoundary - 1] ? formatYearMonthByLocale(activeLabels[activeBoundary - 1]) : "" })}
                    </Text>
                    <HStack spacing={2} align="baseline">
                      <Text fontSize="sm" color="gray.500">{t("common.label.previousEstimate")} {fmt(s2.prevEstimate)}</Text>
                      <Text fontSize="sm" color="gray.400">{t("common.label.arrowTo")}</Text>
                      <Text fontSize="sm" fontWeight="bold">{t("common.label.actuals")} {fmt(s2.currActual)}</Text>
                    </HStack>
                    <Text fontSize="lg" fontWeight="bold" mt={1} color={rateColor(s2.rate, section.lowerIsBetter)}>
                      {s2.rate >= 0 ? "+" : ""}{s2.rate.toFixed(1)}%
                    </Text>
                  </Box>
                </SimpleGrid>
              </Box>

                <Box h="280px" mb={3}>
                  <Bar
                    key={`${section.id}-${periodStart}-${periodEnd}-${compareMode}-${currencyName}`}
                    data={createChartData(compareWith, sectionData.current, compareLabel, compareMode, currentValueLabel, periodStart, periodEnd, activeLabels, activeBoundary)}
                    options={createChartOptions(section.allowNegative, periodStart, periodEnd, calcUnifiedMax(sectionData, periodStart, periodEnd), activeBoundary, reviewBoundaryLabel, currencyName)}
                  />
                </Box>
              </>
            ) : (
              <Box p={6} bg="gray.50" borderRadius="md" textAlign="center">
                <Text fontSize="sm" color="gray.500">{t("common.message.reviewDataNotRegistered")}</Text>
                <Text fontSize="xs" color="gray.400" mt={1}>{t("features.InvestmentScreening.Review.common.enterActualValueInTabs")}</Text>
              </Box>
            )}

            <Textarea
              size="sm"
              placeholder={t("common.placeholder.diffComment")}
              rows={2}
              maxLength={500}
              mt={3}
              value={comments[section.id] || ""}
              onChange={(e) => setComments({ ...comments, [section.id]: e.target.value })}
            />
          </Box>
        );
      })}


      {/* 生産数量 */}
      {(() => {
        if (isLoading) return null;
        const data = productionCapacityData ?? { originalPlan: [] as number[], previous: [] as number[], current: [] as number[] };
        const baseLabels = activeLabels;
        const totalLabelLen = baseLabels.length;
        // ============================================================
        // 生産数量グラフのデータ決定ロジック
        //
        // 【業務定義】
        //   申請時計画 = BOMキャパ（稼働日分+休日分）= 設備が生産できる理論値
        //   前回値     = 前回振り返りで入力した実績 → なければ前回BOMキャパ
        //   今回値     = 今回振り返りで入力した実績 → なければ今回BOMキャパ
        //
        // 【重要】quantityByPartNo（P&L数値）は「販売数量」であり
        //         生産数量とは異なるためここでは使用しない
        // ============================================================
        const hasPNCapacity = Object.keys(planCapacityByPartNo).length > 0;
        const zeros = new Array(totalLabelLen).fill(0);

        // ---- PartNo別実績の全体合算ヘルパー ----
        const sumSavedProduction = (
          savedMap: Record<string, number[]>,
          partNos: string[],
          fallbackMap: Record<string, number[]>
        ): number[] => {
          const result = new Array(totalLabelLen).fill(0);
          for (const pn of partNos) {
            const arr = savedMap[pn] ?? fallbackMap[pn];
            if (arr) {
              for (let i = 0; i < totalLabelLen; i++) {
                result[i] += arr[i] ?? 0;
              }
            }
          }
          return result;
        };

        // ---- 比較値（前回値 or 申請時計画）----
        let compare: number[];
        let compareLabel: string;
        if (compareMode === "previous") {
          if (selectedPartNo !== "all") {
            // 個別PartNo: 前回実績 → 前回BOMキャパ → 申請時BOMキャパ
            compare = prevSavedProductionByPartNo[selectedPartNo]
              ?? prevCapacityByPartNo[selectedPartNo]
              ?? planCapacityByPartNo[selectedPartNo]
              ?? zeros;
          } else {
            // 全体集計: 前回実績合算 → BOMキャパ
            const hasPrevSaved = activePartNos.some(pn => prevSavedProductionByPartNo[pn]);
            compare = hasPrevSaved
              ? sumSavedProduction(prevSavedProductionByPartNo, activePartNos, prevCapacityByPartNo)
              : data.previous;
          }
          compareLabel = prevReviewDateState ? t("common.label.previousValue") : t("common.label.originalPlanValue");
        } else {
          if (selectedPartNo !== "all") {
            // 個別PartNo: 申請時BOMキャパ（販売数量ではなく生産キャパ）
            compare = planCapacityByPartNo[selectedPartNo] ?? zeros;
          } else {
            // 全体集計: BOMキャパ合計
            compare = data.originalPlan;
          }
          compareLabel = t("common.label.originalPlanValue");
        }

        const sliceLabels = baseLabels.slice(periodStart, periodEnd);
        const sliceCompare = compare.slice(periodStart, periodEnd);
        const localBoundary = activeBoundary - periodStart;

        // ---- 今回値 ----
        // productionActuals state（テーブルと同じデータソース）を使用。
        // 未入力の月は BOMキャパ にフォールバック。
        let currentForChart: number[];
        if (selectedPartNo === "all") {
          if (hasPNCapacity) {
            const result = new Array(totalLabelLen).fill(0);
            for (const pn of activePartNos) {
              const bomArr = currentCapacityByPartNo[pn] ?? planCapacityByPartNo[pn] ?? [];
              for (let i = 0; i < totalLabelLen; i++) {
                const actVal = productionActuals[`${pn}-${i}`];
                result[i] += actVal !== undefined ? actVal : (bomArr[i] ?? 0);
              }
            }
            currentForChart = result;
          } else {
            currentForChart = data.current;
          }
        } else {
          const bomArr = currentCapacityByPartNo[selectedPartNo]
            ?? planCapacityByPartNo[selectedPartNo]
            ?? zeros;
          if (planCapacityByPartNo[selectedPartNo] || currentCapacityByPartNo[selectedPartNo]) {
            currentForChart = Array.from({ length: totalLabelLen }, (_, i) => {
              const actVal = productionActuals[`${selectedPartNo}-${i}`];
              return actVal !== undefined ? actVal : (bomArr[i] ?? 0);
            });
          } else {
            currentForChart = currentCapacityByPartNo[selectedPartNo]
              ?? planCapacityByPartNo[selectedPartNo]
              ?? zeros;
          }
        }
        const sliceActual = currentForChart.slice(periodStart, periodEnd);
        const hasCurrentData = hasPNCapacity || data.current.some(v => v !== 0);
        // 保存済み実績が存在するかどうか（グラフ・サマリカードの表示制御）
        const hasSavedProductionData = activePartNos.some(pn => savedProductionByPartNo[pn]);

        // ---- サマリカード用（compare・currentForChartと一致させる）----
        const summaryPlan = selectedPartNo !== "all"
          ? (planCapacityByPartNo[selectedPartNo] ?? data.originalPlan)
          : data.originalPlan;
        // summaryPrev は compareMode によらず「前回BOMキャパ or 前回実績」
        const summaryPrev = compare;
        const s1 = calcActualVsPlan(summaryPlan, currentForChart, activeBoundary);
        const s2 = calcForecastAccuracy(summaryPrev, currentForChart, activePrevBoundary, activeBoundary);
        const s3 = calcFinalOutlook(summaryPlan, currentForChart);

        // 累積データ
        const cumCompare = calcCumulative(sliceCompare);
        const cumActual = calcCumulative(sliceActual);

        // Y軸統一（compare と currentForChart の最大値で統一）
        const allValues = [
          ...compare.slice(periodStart, periodEnd),
          ...currentForChart.slice(periodStart, periodEnd),
        ];
        const unifiedMax = Math.ceil(Math.max(...allValues, 1) * 1.1);

        // 累積のY軸統一
        const cumAllValues = [...cumCompare, ...cumActual];
        const cumUnifiedMax = Math.ceil(Math.max(...cumAllValues) * 1.1);

        // ライフサイクル曲線チャートデータ
        // 累積チャートデータ
        const cumulativeChartData = {
          labels: sliceLabels.map(formatYearMonthByLocale),
          datasets: [
            {
              label: t("features.InvestmentScreening.Review.common.cumulativeWithLabel", { label: compareLabel }),
              data: cumCompare,
              borderColor: compareMode === "previous" ? "rgba(66, 153, 225, 1)" : "rgba(72, 187, 120, 1)",
              borderWidth: 2.5,
              pointRadius: 0,
              tension: 0.3,
              fill: false,
            },
            {
              label: actualsCumulativeLabel,
              data: cumActual.map((v, i) => (i < localBoundary ? v : null)),
              borderColor: "rgba(237, 137, 54, 1)",
              borderWidth: 3,
              pointRadius: 1,
              tension: 0.3,
              fill: false,
              spanGaps: false,
            },
            {
              label: outlookCumulativeLabel,
              data: cumActual.map((v, i) => (i >= localBoundary - 1 ? v : null)),
              borderColor: "rgba(237, 137, 54, 0.7)",
              borderWidth: 2.5,
              borderDash: [8, 4],
              pointRadius: 0,
              tension: 0.3,
              fill: false,
              spanGaps: true,
            },
          ],
        };

        // 共通のアノテーション
        const boundaryAnnotation = localBoundary >= 0 && localBoundary < sliceLabels.length ? {
          reviewBoundary: {
            type: "line" as const,
            xMin: localBoundary - 1,
            xMax: localBoundary - 1,
            borderColor: "rgba(229, 62, 62, 0.8)",
            borderWidth: 2,
            borderDash: [6, 4],
            label: {
              display: true,
              content: reviewBoundaryLabel,
              position: "start" as const,
              backgroundColor: "rgba(229, 62, 62, 0.8)",
              color: "white",
              font: { size: 10 },
            },
          },
        } : {};

        const cumulativeYTitle = `${t("features.InvestmentScreening.Review.ReviewBusiness.section.cumulativeProduction")}（${t("features.InvestmentScreening.Review.ReviewBusiness.unit.pieces")}）`;
        const lineChartOptions = (yTitle: string, suggestedMax: number) => ({
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: "index" as const, intersect: false },
          plugins: {
            legend: { position: "top" as const, labels: { font: { size: 12 }, usePointStyle: true } },
            tooltip: {
              filter: (item: any) => !((item.dataset.label === currentOutlookLabel || item.dataset.label === outlookCumulativeLabel) && item.dataIndex < localBoundary),
              callbacks: {
                label: (ctx: any) => `${ctx.dataset.label}: ${ctx.parsed.y?.toLocaleString() ?? "-"} ${yTitle === cumulativeYTitle ? t("features.InvestmentScreening.Review.ReviewBusiness.unit.pieces") : t("features.InvestmentScreening.Review.ReviewBusiness.unit.perMonth")}`,
              },
            },
            annotation: { annotations: boundaryAnnotation },
            datalabels: false as any,
          },
          scales: {
            x: {
              ticks: { maxRotation: 45, autoSkip: true, maxTicksLimit: 12, font: { size: 10 } },
              grid: { color: "rgba(0,0,0,0.05)" },
            },
            y: {
              beginAtZero: true,
              suggestedMax,
              title: { display: true, text: yTitle, font: { size: 12 } },
              ticks: { callback: (v: any) => Number(v).toLocaleString(), maxTicksLimit: 8 },
              grid: { color: "rgba(0,0,0,0.08)" },
            },
          },
        });

        // 差分塗りつぶしプラグイン
        const fillPlugin = {
          id: "lifecycleFill",
          beforeDatasetsDraw(chart: any) {
            const { ctx, chartArea } = chart;
            if (!chartArea) return;
            const compareMeta = chart.getDatasetMeta(0)?.data;
            if (!compareMeta || compareMeta.length < 2) return;
            const m1 = chart.getDatasetMeta(1)?.data;
            const m2 = chart.getDatasetMeta(2)?.data;
            const getActualPt = (idx: number) => (idx < localBoundary ? m1?.[idx] ?? m2?.[idx] : m2?.[idx] ?? m1?.[idx]);
            const ox = (pt: any) => pt.cp2x ?? pt.x; const oy = (pt: any) => pt.cp2y ?? pt.y;
            const ix = (pt: any) => pt.cp1x ?? pt.x; const iy = (pt: any) => pt.cp1y ?? pt.y;

            ctx.save();
            ctx.rect(chartArea.left, chartArea.top, chartArea.right - chartArea.left, chartArea.bottom - chartArea.top);
            ctx.clip();

            for (let i = 0; i < sliceCompare.length - 1; i++) {
              const c0 = compareMeta[i], c1 = compareMeta[i + 1];
              const a0 = getActualPt(i), a1 = getActualPt(i + 1);
              if (!c0 || !c1 || !a0 || !a1) continue;
              if (sliceActual[i] == null || sliceActual[i + 1] == null) continue;

              const avgA = (sliceCompare[i] + sliceCompare[i + 1]) / 2;
              const avgB = (sliceActual[i] + sliceActual[i + 1]) / 2;

              ctx.save();
              ctx.beginPath();
              ctx.moveTo(c0.x, c0.y);
              ctx.bezierCurveTo(ox(c0), oy(c0), ix(c1), iy(c1), c1.x, c1.y);
              ctx.lineTo(a1.x, a1.y);
              ctx.bezierCurveTo(ix(a1), iy(a1), ox(a0), oy(a0), a0.x, a0.y);
              ctx.closePath();
              ctx.fillStyle = avgA > avgB ? "rgba(229, 62, 62, 0.15)" : "rgba(56, 161, 105, 0.15)";
              ctx.fill();
              ctx.restore();
            }

            ctx.restore();
          },
        };

        return (
          <Box mb={8} p={4} border="1px" borderColor="gray.200" borderRadius="md">
            <HStack mb={3} justify="space-between" align="center">
              <HStack align="baseline" spacing={2}>
                <Text fontSize="md" fontWeight="bold">{t("features.InvestmentScreening.Review.ReviewBusiness.section.productionVolume")}</Text>
                <Text fontSize="sm" color="gray.500">unit: {t("features.InvestmentScreening.Review.ReviewBusiness.unit.pieces")}</Text>
              </HStack>
              <HStack spacing={2}>
                <Text fontSize="xs" color="gray.500">{t("common.label.partNo")}</Text>
                <Select size="sm" w="200px" value={selectedPartNo} onChange={(e) => setSelectedPartNo(e.target.value)}>
                  <option value="all">{t("features.InvestmentScreening.Review.ReviewBusiness.option.allPartNos")}</option>
                  {activePartNos.map(pn => (
                    <option key={pn} value={pn}>{displayPartNoMap[pn] || pn}</option>
                  ))}
                </Select>
              </HStack>
            </HStack>

            {/* 生産実績入力テーブル（最上部に配置・常に表示） */}
            <Box mb={4} p={3} border="1px" borderColor="gray.200" borderRadius="md" bg="gray.50">
              {/* ヘッダー：タイトル + 入力モード切り替えボタン */}
              <HStack mb={3} justify="space-between" align="center">
                <Text fontSize="sm" fontWeight="bold">{t("common.label.actualValueInput")}</Text>
                <HStack spacing={2}>
                  <ButtonGroup>
                    <Button
                      colorScheme={productionInputMode === "manual" ? "green" : "gray"}
                      onClick={handleManualClick}
                      isLoading={isSpreadSheetLoading}
                    >
                      {t("common.label.screenInput")}
                    </Button>
                    <Button
                      colorScheme={productionInputMode === "google" ? "green" : "gray"}
                      onClick={handleGoogleSpreadClick}
                      isLoading={isSpreadSheetLoading}
                    >
                      {t("common.label.googleSpread")}
                    </Button>
                  </ButtonGroup>
                  <Button
                    colorScheme="blue"
                    onClick={handleSyncData}
                    isDisabled={productionInputMode === "manual"}
                    isLoading={isSpreadSheetLoading}
                  >
                    {t("common.label.dataSync")}
                  </Button>
                </HStack>
              </HStack>

              {/* MMSとの連携予告 */}
              <Text fontSize="xs" color="red.500" fontWeight="bold" mb={2}>{t("common.label.mmsIntegrationPlanned")}</Text>

              {/* 期間区分の凡例 */}
              <HStack spacing={4} mb={2} fontSize="xs">
                <HStack spacing={1}><Box w="12px" h="12px" bg="gray.200" borderRadius="2px" /><Text color="gray.500">{t("common.label.previousReviewCompleted")}</Text></HStack>
                <HStack spacing={1}><Box w="12px" h="12px" bg="orange.100" borderRadius="2px" /><Text color="gray.500">{t("common.label.currentReviewTarget")}</Text></HStack>
                <HStack spacing={1}><Box w="12px" h="12px" bg="blue.100" borderRadius="2px" /><Text color="gray.500">{t("common.label.futureEstimate")}</Text></HStack>
              </HStack>

              {/* テーブル：Googleモード時は入力不可 */}
              <Box
                overflowX="auto"
                pointerEvents={productionInputMode === "google" ? "none" : "auto"}
                opacity={productionInputMode === "google" ? 0.5 : 1}
                transition="opacity 0.2s"
              >
                <Table size="sm" variant="simple" minW="max-content">
                  <Thead>
                    <Tr>
                      <Th position="sticky" left={0} bg="gray.50" zIndex={1} fontSize="xs" color="gray.600" w="130px" borderBottom="1px solid" borderColor="gray.200" fontWeight="bold" textTransform="none">Part No.</Th>
                      {baseLabels.slice(periodStart, periodEnd).map((label, i) => {
                        const idx = periodStart + i;
                        const headerBg = idx < activePrevBoundary ? "gray.100"
                          : idx < activeBoundary ? "orange.50"
                            : "blue.50";
                        return (
                          <Th key={i} textAlign="center" fontSize="xs" color="gray.600" px={2} bg={headerBg} borderBottom="1px solid" borderColor="gray.200" fontWeight="bold">{formatYearMonthByLocale(label)}</Th>
                        );
                      })}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {activePartNos.map((pn, pnIdx) => {
                      return (
                        <Tr key={pn} borderTop={pnIdx > 0 ? "1px solid" : undefined} borderColor="gray.200">
                          <Td position="sticky" left={0} bg="white" zIndex={1} fontSize="xs" fontWeight="bold" color="gray.700" borderBottom="1px solid" borderColor="gray.100" py={2}>{displayPartNoMap[pn] || pn}</Td>
                          {baseLabels.slice(periodStart, periodEnd).map((_, i) => {
                            const idx = periodStart + i;
                            const key = `${pn}-${idx}`;
                            const cellBg = idx < activePrevBoundary ? "gray.50" : "white";
                            return (
                              <Td key={i} px={2} py={1} bg={cellBg} borderBottom="1px solid" borderColor="gray.100" textAlign="right">
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  style={{
                                    width: "90px", fontSize: "13px", textAlign: "right",
                                    border: "1px solid #E2E8F0",
                                    borderRadius: "6px",
                                    padding: "4px 8px",
                                    backgroundColor: idx < activePrevBoundary ? "#F7FAFC" : "white",
                                    color: idx < activePrevBoundary ? "#A0AEC0" : "#333",
                                  }}
                                  value={
                                    focusedInputKey === key
                                      ? (productionActuals[key] !== undefined ? String(productionActuals[key]) : "")
                                      : (productionActuals[key] !== undefined ? productionActuals[key].toLocaleString() : "")
                                  }
                                  onFocus={() => setFocusedInputKey(key)}
                                  onBlur={() => setFocusedInputKey(null)}
                                  onChange={(e) => {
                                    const raw = e.target.value.replace(/,/g, '');
                                    if (raw === '') {
                                      setProductionActuals(prev => ({ ...prev, [key]: 0 }));
                                    } else {
                                      const val = Number(raw);
                                      if (!isNaN(val)) {
                                        setProductionActuals(prev => ({ ...prev, [key]: val }));
                                      }
                                    }
                                  }}
                                />
                              </Td>
                            );
                          })}
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>

              {/* 一括保存ボタン */}
              <HStack mt={3} justify="flex-end">
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={handleSaveProductionActuals}
                >
                  {t("common.button.save")}
                </Button>
              </HStack>
            </Box>

            {hasSavedProductionData ? (
              <>
                {/* サマリ3カード */}
                {hasCurrentData && (
                  <Box mb={4} p={4} bg="gray.50" borderRadius="md">
                    <SimpleGrid columns={3} spacing={3}>
                      <Box p={3} bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                        <Text fontSize="xs" color="gray.500" mb={1}>
                          {t("features.InvestmentScreening.Review.common.summaryActualVsPlan", { from: baseLabels[0] ? formatYearMonthByLocale(baseLabels[0]) : "", to: baseLabels[activeBoundary - 1] ? formatYearMonthByLocale(baseLabels[activeBoundary - 1]) : "" })}
                        </Text>
                        <HStack spacing={2} align="baseline">
                          <Text fontSize="sm" color="gray.500">{t("common.label.plan")} {s1.planValue.toLocaleString()}</Text>
                          <Text fontSize="sm" color="gray.400">{t("common.label.arrowTo")}</Text>
                          <Text fontSize="sm" fontWeight="bold">{t("common.label.actuals")} {s1.actualValue.toLocaleString()}</Text>
                        </HStack>
                        <Text fontSize="lg" fontWeight="bold" mt={1} color={s1.rate < -10 ? "red.600" : s1.rate < 0 ? "orange.500" : "green.600"}>
                          {s1.rate >= 0 ? "+" : ""}{s1.rate.toFixed(1)}%
                        </Text>
                      </Box>
                      <Box p={3} bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                        <Text fontSize="xs" color="gray.500" mb={1}>
                          {t("features.InvestmentScreening.Review.common.summaryAdjustedPlan", { from: baseLabels[0] ? formatYearMonthByLocale(baseLabels[0]) : "", to: baseLabels[baseLabels.length - 1] ? formatYearMonthByLocale(baseLabels[baseLabels.length - 1]) : "" })}
                        </Text>
                        <HStack spacing={2} align="baseline">
                          <Text fontSize="sm" color="gray.500">{t("common.label.plan")} {s3.planTotal.toLocaleString()}</Text>
                          <Text fontSize="sm" color="gray.400">{t("common.label.arrowTo")}</Text>
                          <Text fontSize="sm" fontWeight="bold">{t("common.label.outlook")} {s3.outlookTotal.toLocaleString()}</Text>
                        </HStack>
                        <Text fontSize="lg" fontWeight="bold" mt={1} color={s3.rate < -10 ? "red.600" : s3.rate < 0 ? "orange.500" : "green.600"}>
                          {s3.rate >= 0 ? "+" : ""}{s3.rate.toFixed(1)}%
                        </Text>
                      </Box>
                      <Box p={3} bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                        <Text fontSize="xs" color="gray.500" mb={1}>
                          {t("features.InvestmentScreening.Review.common.summaryForecastAccuracy", { from: baseLabels[activePrevBoundary] ? formatYearMonthByLocale(baseLabels[activePrevBoundary]) : "", to: baseLabels[activeBoundary - 1] ? formatYearMonthByLocale(baseLabels[activeBoundary - 1]) : "" })}
                        </Text>
                        <HStack spacing={2} align="baseline">
                          <Text fontSize="sm" color="gray.500">{t("common.label.previousEstimate")} {s2.prevEstimate.toLocaleString()}</Text>
                          <Text fontSize="sm" color="gray.400">{t("common.label.arrowTo")}</Text>
                          <Text fontSize="sm" fontWeight="bold">{t("common.label.actuals")} {s2.currActual.toLocaleString()}</Text>
                        </HStack>
                        <Text fontSize="lg" fontWeight="bold" mt={1} color={s2.rate < -10 ? "red.600" : s2.rate < 0 ? "orange.500" : "green.600"}>
                          {s2.rate >= 0 ? "+" : ""}{s2.rate.toFixed(1)}%
                        </Text>
                      </Box>
                    </SimpleGrid>
                  </Box>
                )}

                {/* グラフ: 常に前回値は表示。今回値は保存済みの場合のみ表示 */}
                <Box h="350px" mb={3}>
                  <Line
                    key={`lifecycle-${periodStart}-${periodEnd}-${compareMode}-${hasCurrentData}-${selectedPartNo}`}
                    data={{
                      labels: sliceLabels.map(formatYearMonthByLocale),
                      datasets: [
                        // 前回値/計画値（常に表示）
                        {
                          label: compareLabel,
                          data: sliceCompare,
                          borderColor: compareMode === "previous" ? "rgba(66, 153, 225, 1)" : "rgba(72, 187, 120, 1)",
                          borderWidth: 2.5,
                          pointRadius: 0,
                          pointHoverRadius: 4,
                          tension: 0.4,
                          fill: false,
                          order: 1,
                        },
                        // 今回値（保存済みの場合のみ）
                        ...(hasCurrentData ? [
                          {
                            label: t("common.label.actuals"),
                            data: sliceActual.map((v: number, i: number) => (i < localBoundary ? v : null)),
                            borderColor: "rgba(237, 137, 54, 1)",
                            borderWidth: 3,
                            pointRadius: 1.5,
                            pointHoverRadius: 5,
                            tension: 0.4,
                            fill: false,
                            spanGaps: false,
                            order: 0,
                          },
                          {
                            label: currentOutlookLabel,
                            data: sliceActual.map((v: number, i: number) => (i >= localBoundary - 1 ? v : null)),
                            borderColor: "rgba(237, 137, 54, 0.7)",
                            borderWidth: 2.5,
                            borderDash: [8, 4],
                            pointRadius: 0,
                            pointHoverRadius: 4,
                            tension: 0.4,
                            fill: false,
                            spanGaps: true,
                            order: 0,
                          },
                        ] : []),
                      ],
                    } as any}
                    options={lineChartOptions(`${t("features.InvestmentScreening.Review.ReviewBusiness.section.productionVolume")}（${t("features.InvestmentScreening.Review.ReviewBusiness.unit.perMonth")}）`, unifiedMax) as any}
                    plugins={hasCurrentData ? [fillPlugin] : []}
                  />
                </Box>

                <Textarea
                  size="sm"
                  placeholder={t("features.InvestmentScreening.Review.ReviewBusiness.placeholder.productionVolumeComment")}
                  rows={2}
                  maxLength={500}
                  value={comments["productionVolume"] || ""}
                  onChange={(e) => setComments({ ...comments, productionVolume: e.target.value })}
                />

                {/* 累積生産数量 */}
                <Box mt={6} pt={4} borderTop="1px" borderColor="gray.200">
                  <HStack mb={3} align="baseline" spacing={2}>
                    <Text fontSize="md" fontWeight="bold">{t("features.InvestmentScreening.Review.ReviewBusiness.section.cumulativeProduction")}</Text>
                    <Text fontSize="sm" color="gray.500">unit: {t("features.InvestmentScreening.Review.ReviewBusiness.unit.pieces")}</Text>
                  </HStack>
                  <Box h="300px" mb={3}>
                    <Line
                      key={`cumulative-${periodStart}-${periodEnd}-${compareMode}-${selectedPartNo}-${hasCurrentData}`}
                      data={{
                        labels: sliceLabels.map(formatYearMonthByLocale),
                        datasets: [
                          {
                            label: t("features.InvestmentScreening.Review.common.cumulativeWithLabel", { label: compareLabel }),
                            data: cumCompare,
                            borderColor: compareMode === "previous" ? "rgba(66, 153, 225, 1)" : "rgba(72, 187, 120, 1)",
                            borderWidth: 2.5,
                            pointRadius: 0,
                            tension: 0.3,
                            fill: false,
                          },
                          ...(hasCurrentData ? [
                            {
                              label: actualsCumulativeLabel,
                              data: cumActual.map((v: number, i: number) => (i < localBoundary ? v : null)),
                              borderColor: "rgba(237, 137, 54, 1)",
                              borderWidth: 3,
                              pointRadius: 1,
                              tension: 0.3,
                              fill: false,
                              spanGaps: false,
                            },
                            {
                              label: outlookCumulativeLabel,
                              data: cumActual.map((v: number, i: number) => (i >= localBoundary - 1 ? v : null)),
                              borderColor: "rgba(237, 137, 54, 0.7)",
                              borderWidth: 2.5,
                              borderDash: [8, 4],
                              pointRadius: 0,
                              tension: 0.3,
                              fill: false,
                              spanGaps: true,
                            },
                          ] : []),
                        ],
                      } as any}
                      options={lineChartOptions(`${t("features.InvestmentScreening.Review.ReviewBusiness.section.cumulativeProduction")}（${t("features.InvestmentScreening.Review.ReviewBusiness.unit.pieces")}）`, cumUnifiedMax) as any}
                      plugins={hasCurrentData ? [{
                        id: "cumulativeFill",
                        beforeDatasetsDraw(chart: any) {
                          const { ctx, scales } = chart;
                          const xScale = scales.x; const yScale = scales.y;
                          if (!xScale || !yScale) return;
                          ctx.save();
                          for (let i = 0; i < cumCompare.length - 1; i++) {
                            const c1 = cumCompare[i], c2 = cumCompare[i + 1];
                            const a1 = cumActual[i], a2 = cumActual[i + 1];
                            const diff1 = a1 - c1, diff2 = a2 - c2;
                            const effectiveDiff1 = diff1 === 0 ? diff2 : diff1;
                            if (Math.abs(diff1) < 1 && Math.abs(diff2) < 1) continue;
                            const x1 = xScale.getPixelForValue(i);
                            const x2 = xScale.getPixelForValue(i + 1);
                            ctx.save();
                            if (effectiveDiff1 * diff2 < 0) {
                              const t = effectiveDiff1 / (effectiveDiff1 - diff2);
                              const xCross = x1 + (x2 - x1) * t;
                              const crossVal = c1 + (c2 - c1) * t;
                              const yCross = yScale.getPixelForValue(crossVal);
                              ctx.beginPath(); ctx.moveTo(x1, yScale.getPixelForValue(c1)); ctx.lineTo(xCross, yCross); ctx.lineTo(x1, yScale.getPixelForValue(a1)); ctx.closePath();
                              ctx.fillStyle = effectiveDiff1 >= 0 ? "rgba(72, 187, 120, 0.15)" : "rgba(229, 62, 62, 0.15)"; ctx.fill();
                              ctx.beginPath(); ctx.moveTo(xCross, yCross); ctx.lineTo(x2, yScale.getPixelForValue(c2)); ctx.lineTo(x2, yScale.getPixelForValue(a2)); ctx.closePath();
                              ctx.fillStyle = diff2 >= 0 ? "rgba(72, 187, 120, 0.15)" : "rgba(229, 62, 62, 0.15)"; ctx.fill();
                            } else {
                              ctx.beginPath(); ctx.moveTo(x1, yScale.getPixelForValue(c1)); ctx.lineTo(x2, yScale.getPixelForValue(c2));
                              ctx.lineTo(x2, yScale.getPixelForValue(a2)); ctx.lineTo(x1, yScale.getPixelForValue(a1)); ctx.closePath();
                              ctx.fillStyle = effectiveDiff1 >= 0 ? "rgba(72, 187, 120, 0.15)" : "rgba(229, 62, 62, 0.15)"; ctx.fill();
                            }
                            ctx.restore();
                          }
                          ctx.restore();
                        },
                      }] : []}
                    />
                  </Box>
                </Box>
              </>
            ) : (
              <Box p={6} bg="gray.50" borderRadius="md" textAlign="center">
                <Text fontSize="sm" color="gray.500">{t("common.message.reviewDataNotRegistered")}</Text>
                <Text fontSize="xs" color="gray.400" mt={1}>{t("features.InvestmentScreening.Review.common.graphDisplaysAfterSavingActuals")}</Text>
              </Box>
            )}
          </Box>
        );
      })()}

      {/* ⑤ 設備稼働率 */}
      {(() => {
        if (isLoading) return null;
        const section = operatingRateSection;
        // 実データがあれば差し替え
        const sectionData = operatingRateRealData ?? section.data;
        const compareWith = compareMode === "previous" ? sectionData.previous : sectionData.originalPlan;
        const compareLabel = compareMode === "previous"
          ? (prevReviewDateState ? t("common.label.previousValue") : t("common.label.originalPlanValue"))
          : t("common.label.originalPlanValue");

        // 設備稼働率は常に平均値で比較
        const hasCurrentData = operatingRateRealData ? hasReviewData : sectionData.current.some(v => v !== 0);
        const s1 = calcActualVsPlanAvg(sectionData.originalPlan, sectionData.current, activeBoundary);
        const s2 = calcForecastAccuracyAvg(sectionData.previous, sectionData.current, activePrevBoundary, activeBoundary);
        const s3 = calcFinalOutlookAvg(sectionData.originalPlan, sectionData.current);
        const fmt = (v: number) => v.toFixed(1);

        return (
          <Box key={section.id} mb={8} p={4} border="1px" borderColor="gray.200" borderRadius="md">
            <HStack mb={3} align="baseline" spacing={2}>
              <Text fontSize="md" fontWeight="bold">{section.title}</Text>
              <Text fontSize="sm" color="gray.500">unit: {section.unit}</Text>
            </HStack>

            {hasCurrentData ? (
              <>
                <Box mb={4} p={4} bg="gray.50" borderRadius="md">
                  <SimpleGrid columns={3} spacing={3}>
                    <Box p={3} bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                      <Text fontSize="xs" color="gray.500" mb={1}>
                        {t("features.InvestmentScreening.Review.common.summaryActualVsPlan", { from: activeLabels[0] ? formatYearMonthByLocale(activeLabels[0]) : "", to: activeLabels[activeBoundary - 1] ? formatYearMonthByLocale(activeLabels[activeBoundary - 1]) : "" })}
                      </Text>
                      <HStack spacing={2} align="baseline">
                        <Text fontSize="sm" color="gray.500">{t("common.label.plan")} {fmt(s1.planValue)}</Text>
                        <Text fontSize="sm" color="gray.400">{t("common.label.arrowTo")}</Text>
                        <Text fontSize="sm" fontWeight="bold">{t("common.label.actuals")} {fmt(s1.actualValue)}</Text>
                      </HStack>
                      <Text fontSize="lg" fontWeight="bold" mt={1} color={s1.rate < -10 ? "red.600" : s1.rate < 0 ? "orange.500" : "green.600"}>
                        {s1.rate >= 0 ? "+" : ""}{s1.rate.toFixed(1)}%
                      </Text>
                    </Box>
                    <Box p={3} bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                      <Text fontSize="xs" color="gray.500" mb={1}>
                        {t("features.InvestmentScreening.Review.common.summaryAdjustedPlan", { from: activeLabels[0] ? formatYearMonthByLocale(activeLabels[0]) : "", to: activeLabels[activeLabels.length - 1] ? formatYearMonthByLocale(activeLabels[activeLabels.length - 1]) : "" })}
                      </Text>
                      <HStack spacing={2} align="baseline">
                        <Text fontSize="sm" color="gray.500">{t("common.label.plan")} {fmt(s3.planTotal)}</Text>
                        <Text fontSize="sm" color="gray.400">{t("common.label.arrowTo")}</Text>
                        <Text fontSize="sm" fontWeight="bold">{t("common.label.outlook")} {fmt(s3.outlookTotal)}</Text>
                      </HStack>
                      <Text fontSize="lg" fontWeight="bold" mt={1} color={s3.rate < -10 ? "red.600" : s3.rate < 0 ? "orange.500" : "green.600"}>
                        {s3.rate >= 0 ? "+" : ""}{s3.rate.toFixed(1)}%
                      </Text>
                    </Box>
                    <Box p={3} bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                      <Text fontSize="xs" color="gray.500" mb={1}>
                        {t("features.InvestmentScreening.Review.common.summaryForecastAccuracy", { from: activeLabels[activePrevBoundary] ? formatYearMonthByLocale(activeLabels[activePrevBoundary]) : "", to: activeLabels[activeBoundary - 1] ? formatYearMonthByLocale(activeLabels[activeBoundary - 1]) : "" })}
                      </Text>
                      <HStack spacing={2} align="baseline">
                        <Text fontSize="sm" color="gray.500">{t("common.label.previousEstimate")} {fmt(s2.prevEstimate)}</Text>
                        <Text fontSize="sm" color="gray.400">{t("common.label.arrowTo")}</Text>
                        <Text fontSize="sm" fontWeight="bold">{t("common.label.actuals")} {fmt(s2.currActual)}</Text>
                      </HStack>
                      <Text fontSize="lg" fontWeight="bold" mt={1} color={s2.rate < -10 ? "red.600" : s2.rate < 0 ? "orange.500" : "green.600"}>
                        {s2.rate >= 0 ? "+" : ""}{s2.rate.toFixed(1)}%
                      </Text>
                    </Box>
                  </SimpleGrid>
                </Box>

                <Box h="280px" mb={3}>
                  <Bar
                    data={createChartData(compareWith, sectionData.current, compareLabel, compareMode, currentValueLabel, periodStart, periodEnd, activeLabels, activeBoundary)}
                    options={createChartOptions(section.allowNegative, periodStart, periodEnd, calcUnifiedMax(sectionData, periodStart, periodEnd), activeBoundary, reviewBoundaryLabel, currencyName)}
                  />
                </Box>
              </>
            ) : (
              <Box p={6} bg="gray.50" borderRadius="md" textAlign="center">
                <Text fontSize="sm" color="gray.500">{t("common.message.reviewDataNotRegistered")}</Text>
                <Text fontSize="xs" color="gray.400" mt={1}>{t("features.InvestmentScreening.Review.common.enterActualValueInTabs")}</Text>
              </Box>
            )}

            <Textarea
              size="sm"
              placeholder={t("common.placeholder.diffComment")}
              rows={2}
              maxLength={500}
              mt={3}
              value={comments[section.id] || ""}
              onChange={(e) => setComments({ ...comments, [section.id]: e.target.value })}
            />
          </Box>
        );
      })()}

      {/* 単一値比較項目（回収期間・人員数） */}
      <Box mb={6}>
        <Text fontSize="md" fontWeight="bold" mb={3}>{t("features.InvestmentScreening.Review.ReviewBusiness.section.singleValueItems")}</Text>
        <Table variant="simple" size="sm" border="1px" borderColor="gray.200">
          <Thead>
            <Tr bg={compareMode === "previous" ? "blue.600" : "green.600"}>
              <Th w="30%" color="white">{t("common.label.item")}</Th>
              <Th w="15%" textAlign="center" color="white">
                {compareMode === "previous"
                  ? (prevReviewDateState ? t("common.label.previousValue") : t("common.label.applicationInitial"))
                  : t("common.label.applicationTime")}
              </Th>
              <Th w="15%" textAlign="center" color="white">{t("common.label.currentValue")}</Th>
              <Th w="10%" textAlign="center" color="white">{t("common.label.difference")}</Th>
              <Th w="30%" color="white">{t("common.placeholder.comment")}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {/* ③ 回収期間 — 大きいほど悪化（diff>0 = 赤） */}
            {(() => {
              const prev = compareMode === "previous" ? (paybackPrev ?? paybackPlan) : paybackPlan;
              const curr = paybackReview;
              const diff = prev != null && curr != null && typeof prev === "number" && typeof curr === "number"
                ? Math.round((curr - prev) * 10) / 10 : null;
              const fmtPayback = (v: number | "回収不能" | null) =>
                v === "回収不能" ? t("common.label.irrecoverable") : v != null ? t("features.InvestmentScreening.Review.ReviewBusiness.valueWithMonths", { value: v }) : "—";
              return (
                <Tr _hover={{ bg: "gray.50" }}>
                  <Td fontWeight="semibold" fontSize="sm">{t("features.InvestmentScreening.Review.ReviewBusiness.item.paybackPeriod")}</Td>
                  <Td textAlign="center" fontSize="sm">{fmtPayback(prev)}</Td>
                  <Td textAlign="center" fontSize="sm">{fmtPayback(curr)}</Td>
                  <Td textAlign="center" fontSize="sm" color={diff != null && diff > 0 ? "red.600" : diff != null && diff < 0 ? "green.600" : "gray.400"}>
                    {diff != null ? t("features.InvestmentScreening.Review.ReviewBusiness.diffWithMonths", { sign: diff >= 0 ? "+" : "", value: diff }) : "—"}
                  </Td>
                  <Td>
                    <Textarea size="sm" placeholder={t("common.placeholder.comment")} rows={1} maxLength={500}
                      value={comments["payback_period"] || ""}
                      onChange={(e) => setComments({ ...comments, payback_period: e.target.value })} />
                  </Td>
                </Tr>
              );
            })()}
            {/* ⑦ 人員数 — 小さいほど効率良化（diff<0 = 緑） */}
            {(() => {
              const prev = compareMode === "previous" ? (headcountPrev ?? headcountPlan) : headcountPlan;
              const curr = headcountReview;
              const diff = prev != null && curr != null ? curr - prev : null;
              return (
                <Tr _hover={{ bg: "gray.50" }}>
                  <Td fontWeight="semibold" fontSize="sm">{t("features.InvestmentScreening.Review.ReviewBusiness.item.headcount")}</Td>
                  <Td textAlign="center" fontSize="sm">{prev != null ? t("features.InvestmentScreening.Review.ReviewBusiness.valueWithPersons", { value: prev }) : "—"}</Td>
                  <Td textAlign="center" fontSize="sm">{curr != null ? t("features.InvestmentScreening.Review.ReviewBusiness.valueWithPersons", { value: curr }) : "—"}</Td>
                  <Td textAlign="center" fontSize="sm" color={diff != null && diff < 0 ? "green.600" : diff != null && diff > 0 ? "red.600" : "gray.400"}>
                    {diff != null ? t("features.InvestmentScreening.Review.ReviewBusiness.diffWithPersons", { sign: diff >= 0 ? "+" : "", value: diff }) : "—"}
                  </Td>
                  <Td>
                    <Textarea size="sm" placeholder={t("common.placeholder.comment")} rows={1} maxLength={500}
                      value={comments["headcount"] || ""}
                      onChange={(e) => setComments({ ...comments, headcount: e.target.value })} />
                  </Td>
                </Tr>
              );
            })()}
          </Tbody>
        </Table>
      </Box>

      {/* ⑥ 歩留・サイクルタイム（期間平均） */}
      <Box mb={6}>
        <HStack mb={3} justify="space-between">
          <Text fontSize="md" fontWeight="bold">{t("features.InvestmentScreening.Review.ReviewBusiness.yieldCycleTimeFiltered")}</Text>
          {Object.keys(yieldAtBoundaryByPartNo).length > 1 && (
            <HStack spacing={2}>
              <Text fontSize="xs" color="gray.500">{t("common.label.partNo")}</Text>
              <Select size="sm" w="200px" value={selectedPartNoYieldCt} onChange={(e) => setSelectedPartNoYieldCt(e.target.value)}>
                <option value="all">{t("features.InvestmentScreening.Review.ReviewBusiness.option.overall")}</option>
                {Object.keys(yieldAtBoundaryByPartNo).map(pn => (
                  <option key={pn} value={pn}>{displayPartNoMap[pn] || pn}</option>
                ))}
              </Select>
            </HStack>
          )}
        </HStack>
        <Table variant="simple" size="sm" border="1px" borderColor="gray.200">
          <Thead>
            <Tr bg={compareMode === "previous" ? "blue.600" : "green.600"}>
              <Th w="30%" color="white">{t("common.label.item")}</Th>
              <Th w="15%" textAlign="center" color="white">
                <Text>{compareMode === "previous" ? (prevReviewDateState ? t("common.label.previousValue") : t("common.label.applicationInitial")) : t("common.label.applicationTime")}</Text>
                {yieldCtPrevPeriodText && <Text fontSize="2xs" fontWeight="normal" opacity={0.85}>{yieldCtPrevPeriodText}</Text>}
              </Th>
              <Th w="15%" textAlign="center" color="white">
                <Text>{t("common.label.currentValue")}</Text>
                {yieldCtCurrPeriodText && <Text fontSize="2xs" fontWeight="normal" opacity={0.85}>{yieldCtCurrPeriodText}</Text>}
              </Th>
              <Th w="10%" textAlign="center" color="white">{t("common.label.difference")}</Th>
              <Th w="30%" color="white">{t("common.placeholder.comment")}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {/* ⑥ 歩留まり — 大きいほど良化（diff>0 = 緑） */}
            {selectedPartNoYieldCt !== "all" && yieldAtBoundaryByPartNo[selectedPartNoYieldCt] ? (
              // PartNo個別: 選択PartNoの境界値を単一行表示
              (() => {
                const d = yieldAtBoundaryByPartNo[selectedPartNoYieldCt];
                const prev = compareMode === "previous" ? d.prev : d.plan;
                const curr = d.current;
                const diff = Math.round((curr - prev) * 10) / 10;
                return (
                  <Tr _hover={{ bg: "gray.50" }}>
                    <Td fontWeight="semibold" fontSize="sm">
                      {t("features.InvestmentScreening.Review.ReviewBusiness.item.yield")}
                      <Text as="span" fontSize="xs" color="gray.500" ml={1}>({displayPartNoMap[selectedPartNoYieldCt] || selectedPartNoYieldCt})</Text>
                    </Td>
                    <Td textAlign="center" fontSize="sm">{prev} %</Td>
                    <Td textAlign="center" fontSize="sm">{curr} %</Td>
                    <Td textAlign="center" fontSize="sm" color={diff > 0 ? "green.600" : diff < 0 ? "red.600" : "gray.400"}>
                      {diff >= 0 ? "+" : ""}{diff} %
                    </Td>
                    <Td>
                      <Textarea size="sm" placeholder={t("common.placeholder.comment")} rows={1} maxLength={500}
                        value={comments["yield_rate"] || ""}
                        onChange={(e) => setComments({ ...comments, yield_rate: e.target.value })} />
                    </Td>
                  </Tr>
                );
              })()
            ) : yieldStatsPlan && yieldStatsReview ? (
              // 多数モデル: BOM由来のmax/min/avgを3行表示
              <>
                {(['max', 'min', 'avg'] as const).map((statKey, i) => {
                  const statLabel = { max: t("common.label.max"), min: t("common.label.min"), avg: t("common.label.avg") }[statKey];
                  const prevYieldStats = compareMode === "previous" ? (yieldStatsPrev ?? yieldStatsPlan) : yieldStatsPlan;
                  const prev = Math.round(prevYieldStats[statKey] * 10) / 10;
                  const curr = Math.round(yieldStatsReview[statKey] * 10) / 10;
                  const diff = Math.round((curr - prev) * 10) / 10;
                  return (
                    <Tr key={`yield-${statKey}`} _hover={{ bg: "gray.50" }}>
                      <Td fontWeight="semibold" fontSize="sm">
                        {i === 0 ? t("features.InvestmentScreening.Review.ReviewBusiness.item.yield") : "　"}
                        <Text as="span" fontSize="xs" color="gray.500" ml={1}>({statLabel})</Text>
                      </Td>
                      <Td textAlign="center" fontSize="sm">{prev} %</Td>
                      <Td textAlign="center" fontSize="sm">{curr} %</Td>
                      <Td textAlign="center" fontSize="sm" color={diff > 0 ? "green.600" : diff < 0 ? "red.600" : "gray.400"}>
                        {diff >= 0 ? "+" : ""}{diff} %
                      </Td>
                      {i === 0 && (
                        <Td rowSpan={3} verticalAlign="top">
                          <Textarea size="sm" placeholder={t("common.placeholder.comment")} rows={3} maxLength={500}
                            value={comments["yield_rate"] || ""}
                            onChange={(e) => setComments({ ...comments, yield_rate: e.target.value })} />
                        </Td>
                      )}
                    </Tr>
                  );
                })}
              </>
            ) : (() => {
              // 単一モデル: 既存の単一値表示
              const prev = compareMode === "previous" ? (yieldPrev ?? yieldPlan) : yieldPlan;
              const curr = yieldReview;
              const diff = prev != null && curr != null ? Math.round((curr - prev) * 10) / 10 : null;
              return (
                <Tr _hover={{ bg: "gray.50" }}>
                  <Td fontWeight="semibold" fontSize="sm">{t("features.InvestmentScreening.Review.ReviewBusiness.item.yield")}</Td>
                  <Td textAlign="center" fontSize="sm">{prev != null ? `${prev} %` : "—"}</Td>
                  <Td textAlign="center" fontSize="sm">{curr != null ? `${curr} %` : "—"}</Td>
                  <Td textAlign="center" fontSize="sm" color={diff != null && diff > 0 ? "green.600" : diff != null && diff < 0 ? "red.600" : "gray.400"}>
                    {diff != null ? `${diff >= 0 ? "+" : ""}${diff} %` : "—"}
                  </Td>
                  <Td>
                    <Textarea size="sm" placeholder={t("common.placeholder.comment")} rows={1} maxLength={500}
                      value={comments["yield_rate"] || ""}
                      onChange={(e) => setComments({ ...comments, yield_rate: e.target.value })} />
                  </Td>
                </Tr>
              );
            })()}
            {/* ⑥ サイクルタイム — 小さいほど良化（diff<0 = 緑） */}
            {selectedPartNoYieldCt !== "all" && ctAtBoundaryByPartNo[selectedPartNoYieldCt] ? (
              // PartNo個別: 選択PartNoの境界値を単一行表示
              (() => {
                const d = ctAtBoundaryByPartNo[selectedPartNoYieldCt];
                const prev = compareMode === "previous" ? d.prev : d.plan;
                const curr = d.current;
                const diff = Math.round((curr - prev) * 10) / 10;
                return (
                  <Tr _hover={{ bg: "gray.50" }}>
                    <Td fontWeight="semibold" fontSize="sm">
                      {t("features.InvestmentScreening.Review.ReviewBusiness.item.cycleTime")}
                      <Text as="span" fontSize="xs" color="gray.500" ml={1}>({displayPartNoMap[selectedPartNoYieldCt] || selectedPartNoYieldCt})</Text>
                    </Td>
                    <Td textAlign="center" fontSize="sm">{t("features.InvestmentScreening.Review.ReviewBusiness.valueWithSeconds", { value: prev })}</Td>
                    <Td textAlign="center" fontSize="sm">{t("features.InvestmentScreening.Review.ReviewBusiness.valueWithSeconds", { value: curr })}</Td>
                    <Td textAlign="center" fontSize="sm" color={diff < 0 ? "green.600" : diff > 0 ? "red.600" : "gray.400"}>
                      {t("features.InvestmentScreening.Review.ReviewBusiness.diffWithSeconds", { sign: diff >= 0 ? "+" : "", value: diff })}
                    </Td>
                    <Td>
                      <Textarea size="sm" placeholder={t("common.placeholder.comment")} rows={1} maxLength={500}
                        value={comments["cycle_time"] || ""}
                        onChange={(e) => setComments({ ...comments, cycle_time: e.target.value })} />
                    </Td>
                  </Tr>
                );
              })()
            ) : ctStatsPlan && ctStatsReview ? (
              // 多数モデル: BOM由来のmax/min/avgを3行表示
              <>
                {(['max', 'min', 'avg'] as const).map((statKey, i) => {
                  const statLabel = { max: t("common.label.max"), min: t("common.label.min"), avg: t("common.label.avg") }[statKey];
                  const prevCtStats = compareMode === "previous" ? (ctStatsPrev ?? ctStatsPlan) : ctStatsPlan;
                  const prev = Math.round(prevCtStats[statKey] * 10) / 10;
                  const curr = Math.round(ctStatsReview[statKey] * 10) / 10;
                  const diff = Math.round((curr - prev) * 10) / 10;
                  return (
                    <Tr key={`ct-${statKey}`} _hover={{ bg: "gray.50" }}>
                      <Td fontWeight="semibold" fontSize="sm">
                        {i === 0 ? t("features.InvestmentScreening.Review.ReviewBusiness.item.cycleTime") : "　"}
                        <Text as="span" fontSize="xs" color="gray.500" ml={1}>({statLabel})</Text>
                      </Td>
                      <Td textAlign="center" fontSize="sm">{t("features.InvestmentScreening.Review.ReviewBusiness.valueWithSeconds", { value: prev })}</Td>
                      <Td textAlign="center" fontSize="sm">{t("features.InvestmentScreening.Review.ReviewBusiness.valueWithSeconds", { value: curr })}</Td>
                      <Td textAlign="center" fontSize="sm" color={diff < 0 ? "green.600" : diff > 0 ? "red.600" : "gray.400"}>
                        {t("features.InvestmentScreening.Review.ReviewBusiness.diffWithSeconds", { sign: diff >= 0 ? "+" : "", value: diff })}
                      </Td>
                      {i === 0 && (
                        <Td rowSpan={3} verticalAlign="top">
                          <Textarea size="sm" placeholder={t("common.placeholder.comment")} rows={3} maxLength={500}
                            value={comments["cycle_time"] || ""}
                            onChange={(e) => setComments({ ...comments, cycle_time: e.target.value })} />
                        </Td>
                      )}
                    </Tr>
                  );
                })}
              </>
            ) : (() => {
              // 単一モデル: 既存の単一値表示
              const prev = compareMode === "previous" ? (cycleTimePrev ?? cycleTimePlan) : cycleTimePlan;
              const curr = cycleTimeReview;
              const diff = prev != null && curr != null ? Math.round((curr - prev) * 10) / 10 : null;
              return (
                <Tr _hover={{ bg: "gray.50" }}>
                  <Td fontWeight="semibold" fontSize="sm">{t("features.InvestmentScreening.Review.ReviewBusiness.item.cycleTime")}</Td>
                  <Td textAlign="center" fontSize="sm">{prev != null ? t("features.InvestmentScreening.Review.ReviewBusiness.valueWithSeconds", { value: prev }) : "—"}</Td>
                  <Td textAlign="center" fontSize="sm">{curr != null ? t("features.InvestmentScreening.Review.ReviewBusiness.valueWithSeconds", { value: curr }) : "—"}</Td>
                  <Td textAlign="center" fontSize="sm" color={diff != null && diff < 0 ? "green.600" : diff != null && diff > 0 ? "red.600" : "gray.400"}>
                    {diff != null ? t("features.InvestmentScreening.Review.ReviewBusiness.diffWithSeconds", { sign: diff >= 0 ? "+" : "", value: diff }) : "—"}
                  </Td>
                  <Td>
                    <Textarea size="sm" placeholder={t("common.placeholder.comment")} rows={1} maxLength={500}
                      value={comments["cycle_time"] || ""}
                      onChange={(e) => setComments({ ...comments, cycle_time: e.target.value })} />
                  </Td>
                </Tr>
              );
            })()}
          </Tbody>
        </Table>
      </Box>

      {/* テキスト入力項目（⑧⑨⑩） */}
      <Box mb={6}>
        <Text fontSize="md" fontWeight="bold" mb={3}>{t("features.InvestmentScreening.Review.ReviewBusiness.section.textInputItems")}</Text>
        <Table variant="simple" size="sm" border="1px" borderColor="gray.200">
          <Thead>
            <Tr bg={compareMode === "previous" ? "blue.600" : "green.600"}>
              <Th w="30%" color="white">{t("common.label.item")}</Th>
              <Th w="70%" color="white">{t("common.label.content")}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {TEXT_ITEM_IDS.map((id) => {
              const camelId = id === "maintenance_cost" ? "maintenanceCost" : id === "skill_transfer" ? "skillTransfer" : "energyEfficiency";
              return (
                <Tr key={id} _hover={{ bg: "gray.50" }}>
                  <Td fontWeight="semibold" fontSize="sm">
                    {t(`features.InvestmentScreening.Review.ReviewBusiness.item.${camelId}`)}
                  </Td>
                  <Td>
                    <Textarea
                      size="sm"
                      placeholder={t(`features.InvestmentScreening.Review.ReviewBusiness.placeholder.${camelId}`)}
                      rows={2}
                      maxLength={500}
                      value={textValues[id] || ""}
                      onChange={(e) => setTextValues({ ...textValues, [id]: e.target.value })}
                    />
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>

      {/* Excel出力・確認ボタン */}
      <Flex justify="space-between" mt="20px" mb={3}>
        <Box>
          <Button
            colorScheme="green"
            onClick={handleExcelExport}
            isDisabled={!hasReviewData}
          >
            {t('common.button.excelExport')}
          </Button>
        </Box>
        <Flex>
          <Box>
            <Button
              colorScheme="blue"
              name="companyDivision"
              onClick={handleModalOpen}
              isDisabled={!pagePermissions?.eabledCompanyDivision}
              ref={refs["companyDivision"]}
            >
              {t("common.constants.divisionConfirm.company")}
            </Button>
            <ConfirmorList confirmorInfo={names["companyDivision"]} width={buttonWidths["companyDivision"]} />
          </Box>
          <Box>
            <Button
              ml="10px"
              colorScheme="blue"
              name="companyHeadDivision"
              onClick={handleModalOpen}
              isDisabled={!pagePermissions?.eabledCompanyHeadDivision}
              ref={refs["companyHeadDivision"]}
            >
              {t("common.constants.divisionConfirm.companyHead")}
            </Button>
            <ConfirmorList confirmorInfo={names["companyHeadDivision"]} width={buttonWidths["companyHeadDivision"]} />
          </Box>
          <Box>
            <Button
              ml="10px"
              colorScheme="blue"
              name="procurementDivision"
              onClick={handleModalOpen}
              isDisabled={!pagePermissions?.eabledProcurementDivision}
              ref={refs["procurementDivision"]}
            >
              {t("common.constants.divisionConfirm.procurement")}
            </Button>
            <ConfirmorList confirmorInfo={names["procurementDivision"]} width={buttonWidths["procurementDivision"]} />
          </Box>
        </Flex>
      </Flex>

      {/* 確認モーダル */}
      <Modal isOpen={isOpenConfirmModal} onClose={onCloseConfirmModal} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("common.button.save")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>{t("common.message.confirmSave")}</Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} isDisabled={isSaving} isLoading={isSaving} onClick={handleConfirm}>
              {t("common.button.save")}
            </Button>
            <Button variant="ghost" onClick={onCloseConfirmModal}>{t("common.button.cancel")}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ReviewBusiness;
